(() => {
// MobilePage.ts — the entry point for mobile.pqforce.com.
//
// ⚠ This bundle deliberately imports NEITHER jQuery NOR UIkit, unlike every
// other bundle here. Those exist for the desktop portal's pages; this app is
// served to a phone from a different host with its own stylesheet, and the
// reasoning that keeps `default.scss` off it applies to ~120 KB of framework
// just as much. See the header of `src/js/ui/mobile/dom.ts` for what is kept
// from the convention (no window globals, no inline handlers, wiring inside
// initUI*) and what is not.
// ─────────────────────────────────────────────────────────────────────────────
// PQFORCE Mobile — the app shell. Owns the store, wires every action, renders.
//
// ⚠ NOTHING SENSITIVE IS PERSISTED IN THE BROWSER. The access token lives on the
// MobileClient instance, in memory, and dies with the tab; the long-lived device
// credential is an httpOnly cookie this code cannot read. The only thing written
// to localStorage is the address the user typed, so the next visit can skip
// step 1 — a convenience, and no use to anyone who steals it.
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// PQFORCE Mobile — the app layer's view of the portal's mobile REST surface.
//
// UI → App → Data: nothing in `src/js/ui/mobile` calls `MobileClient` directly.
//
// ⚠ MobileClient is imported BY PATH, not through `home/data`. That barrel
// re-exports ApiCache and CacheManager, and a phone has no reason to download
// the desktop cache layer — the same rule `ui_tiles.ts` documents for ObjectTable.
//
// ⚠ Every route below is app-shaped (`todos`, `me`), never a PQFORCE path. The
// portal proxy is a NARROW ALLOW-LIST that builds each upstream URL itself from
// the device record — the tenant, and the caller's own `resourceId`, are never
// request parameters. A route here that took a raw endpoint would defeat that;
// see §3 of the implementation plan and `pqfClfGlobalStoreEndpoint()`, which
// makes the same argument for the same reason.
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// PQFORCE Mobile — TODO logic. Pure: no DOM, no jQuery, no network.
//
// Everything here is a function of its arguments so it can be tested without a
// browser, per the project's 3-layer rule (application layer must not touch the
// DOM) and the way `mylibs/` keeps its decidable parts pure on the PHP side.
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// PQFORCE Mobile — the shapes the app layer works in.
//
// These deliberately mirror what the PQFORCE API actually returns rather than
// what the design mock held in `state`. Two differences carry the whole feature:
//
//   1. ⚠ `Todo` IS A CORE OBJECT TYPE, but its STATES are tenant configuration.
//      This comment used to say there was no Todo type in PQFORCE at all. That was
//      wrong, and it was corrected by measurement: `Todo` is one of the eight
//      hardcoded core object types in `lifecycleObjectTypesList()`, and
//      `NOTIFY_DEFAULT_OBJECT_TYPES` already treats it as a constant. So the item
//      type needs no configuration and none is offered.
//
//      What IS tenant configuration is everything inside it — including the
//      lifecycle's STATES, which the app shows exactly as the tenant configured
//      them: its names, its colours, its order. There is no fixed vocabulary of
//      states on this side and no mapping onto one in the configurator; every
//      state object PQFORCE returns carries its `category` and `sortIndex`, and
//      that is all the app reasons on. (A five-bucket model resolved from
//      configurator bindings did this until 2026-09-15.)
//
//   2. ⚠ TRANSITIONS COME FROM THE SERVER, NEVER FROM A TABLE HERE. The design
//      mock carries a `byRelation` lookup; that is a mock of the tenant's
//      lifecycle configuration, and the design's own footnote says so
//      ("Transitions come from your tenant's lifecycle configuration and your
//      relation to this TODO"). The real source is
//      `GET /LCY/Object/{type}/{id}/Transitions` — documented as "transitions
//      that are allowed for the current user" — or the
//      `possibleLifecycleTransitions` already carried on `ItemSummaries`.
//      Deriving them locally would show buttons the API then refuses, and hide
//      ones it would have allowed.
// ─────────────────────────────────────────────────────────────────────────────
/**
 * PQFORCE's own `lcy.LifecycleCategory`, as carried on every state.
 *
 * ⚠ A HINT, NOT A MEANING. It paints a chip whose state has no colour of its own and decides what
 * counts as still open (anything not CLOSED). It must not be read as more: measured on
 * develop.pqforce.com/dani, a state called "Blocked" carries PLANNING. `null` is a real value — a
 * state the tenant never categorised.
 */ const $6ad175d5fef0aab4$export$c7e9902bc85a21ff = [
    "NEW",
    "PLANNING",
    "ACTIVE",
    "CLOSED"
];
const $6ad175d5fef0aab4$export$a40d177a2c848217 = [
    "due",
    "status",
    "priority",
    "project"
];
const $6ad175d5fef0aab4$export$5cb4e1555e39a8c8 = [
    "Overdue",
    "Today",
    "This week",
    "Later",
    "None"
];


// ─────────────────────────────────────────────────────────────────────────────
// Due dates: the label, the group and the overdue flag, all derived from ONE
// stored value and the instant you ask.
//
// ⚠ NEVER STORE A BUCKET OR AN OVERDUE FLAG. They are true only for the moment
// they were computed, and this app stays open in a pocket: a todo due today
// becomes overdue at midnight without a single byte changing. The server used to
// compute all three and send them, which was wrong twice over — stale after
// midnight, and computed against the SERVER's clock.
//
// ⚠ THE DEVICE'S CLOCK IS THE RIGHT ONE, and that is the point rather than a
// convenience. A server in UTC and a phone in Zürich disagree about what "Today"
// means for two hours out of every day. The person reading the screen is the one
// the word is about.
//
// Pure, and `now` is a required argument — never `Date.now()` inside. That keeps
// it testable and, just as importantly, makes a whole list derive against ONE
// instant instead of drifting across a render.
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// PQFORCE Mobile — the app's words, in German or English.
//
// The page embeds every `mobile…` translation in BOTH languages (it cannot know
// whose device is asking — see mylibs/pqforce-mobile-language.php) and this
// module picks: the PQFORCE ACCOUNT's language once the profile says it, the
// phone's before that. DOM-free; `ui_mobile_language.ts` reads the page.
//
// ⚠ `t()` IS CALLED AT RENDER TIME, NEVER AT MODULE LEVEL. A module-level
// `const LABEL = t(…)` is evaluated once, at import, before any dictionary is
// loaded — it would freeze the English fallback for the life of the page, and
// switching language would not reach it. Keep a function, as the portal's own
// staff pages do (their `T` tables).
//
// ⚠ Every call is `t("mobile…", "English")` with LITERAL arguments — an id and a fallback string.
// MobileTranslationsTest scans for that shape to prove every id is seeded in
// both languages; an id built by concatenation, or passed through a variable,
// is invisible to it and ships unseeded.
// ─────────────────────────────────────────────────────────────────────────────
/** The two languages the app speaks, like the rest of the portal's own words. */ let $498477530f916680$var$dictionaries = {
    de: {},
    en: {}
};
let $498477530f916680$var$current = "en";
function $498477530f916680$export$7248d74115066(value) {
    return String(value ?? "").trim().toLowerCase().startsWith("de") ? "de" : "en";
}
function $498477530f916680$export$8a1db0e5d8aa5f24(raw) {
    const pick = (value)=>{
        if (!value || typeof value !== "object") return {};
        return Object.fromEntries(Object.entries(value).filter(([, text])=>typeof text === "string"));
    };
    const source = raw && typeof raw === "object" ? raw : {};
    $498477530f916680$var$dictionaries = {
        de: pick(source.de),
        en: pick(source.en)
    };
}
function $498477530f916680$export$4b6bf64406ec64af(language) {
    $498477530f916680$var$current = language;
}
function $498477530f916680$export$324a75438e87a537() {
    return $498477530f916680$var$current;
}
function $498477530f916680$export$f2894e1cf9785d29(stored, pageLanguage) {
    return stored === "de" || stored === "en" ? stored : $498477530f916680$export$7248d74115066(pageLanguage);
}
const $498477530f916680$export$b29a60a5d482bc74 = [
    "auto",
    "de",
    "en"
];
function $498477530f916680$export$e80e2ac6cfd81322(value) {
    return value === "de" || value === "en" ? value : "auto";
}
function $498477530f916680$export$1d03e05656460a20(choice, account, phone) {
    if (choice !== "auto") return choice;
    return account ?? phone;
}
function $498477530f916680$export$69b3add48d105f59(choice) {
    if (choice === "de") return "Deutsch";
    if (choice === "en") return "English";
    return $498477530f916680$export$625550452a3fa3ec("mobileSheetLanguageAuto", "From PQFORCE");
}
function $498477530f916680$export$a6ce7dcbdf9c4fcc() {
    return $498477530f916680$var$current === "de" ? "de-CH" : "en-GB";
}
function $498477530f916680$export$625550452a3fa3ec(id, fallback, values) {
    const text = $498477530f916680$var$dictionaries[$498477530f916680$var$current][id] || fallback;
    if (!values) return text;
    return text.replace(/\{(\w+)\}/g, (match, name)=>name in values ? String(values[name]) : match);
}


/**
 * ⚠ In the APP's language, never the phone's locale: a date rendered in the device's own locale
 * would be the one foreign string on a screen written in the account's language. See
 * `app/mobile/i18n.ts`.
 *
 * English keeps its fixed table, so its label stays byte-identical to the one the server used to
 * send; German goes through Intl with `dateLocale()` ("29. Aug.").
 */ const $439e2ca410399204$var$MONTHS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec"
];
function $439e2ca410399204$export$46fe736982a14ec1(date) {
    if ((0, $498477530f916680$export$324a75438e87a537)() === "en") return `${date.getDate()} ${$439e2ca410399204$var$MONTHS[date.getMonth()]}`;
    return new Intl.DateTimeFormat((0, $498477530f916680$export$a6ce7dcbdf9c4fcc)(), {
        day: "numeric",
        month: "short"
    }).format(date);
}
function $439e2ca410399204$export$6814d6bdd416543f(dueISO) {
    const text = String(dueISO ?? "").trim();
    if (!text) return "";
    const date = $439e2ca410399204$export$b3bf801538b9acf7(text);
    if (!date) return text;
    if ((0, $498477530f916680$export$324a75438e87a537)() === "en") return `${$439e2ca410399204$export$46fe736982a14ec1(date)} ${date.getFullYear()}`;
    return new Intl.DateTimeFormat((0, $498477530f916680$export$a6ce7dcbdf9c4fcc)(), {
        day: "numeric",
        month: "short",
        year: "numeric"
    }).format(date);
}
function $439e2ca410399204$export$c78b734ae6fbc937(bucket) {
    switch(bucket){
        case "Overdue":
            return (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextDueOverdue", "Overdue");
        case "Today":
            return (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextDueToday", "Today");
        case "This week":
            return (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextDueThisWeek", "This week");
        case "None":
            return (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextDueNone", "No due date");
        default:
            return (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextDueLater", "Later");
    }
}
function $439e2ca410399204$export$b3bf801538b9acf7(value) {
    const text = String(value ?? "").trim();
    if (!text) return null;
    const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
    if (dateOnly) {
        const [, y, m, d] = dateOnly;
        const date = new Date(Number(y), Number(m) - 1, Number(d));
        // Rejects 2026-02-31, which the constructor would roll into March.
        return date.getMonth() === Number(m) - 1 ? date : null;
    }
    const stamp = Date.parse(text);
    if (Number.isNaN(stamp)) return null;
    const moment = new Date(stamp);
    return new Date(moment.getFullYear(), moment.getMonth(), moment.getDate());
}
function $439e2ca410399204$export$b5e1c5ebfaeadbc8(date, now) {
    const today = new Date(now);
    const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    // ⚠ Divide the DIFFERENCE OF TWO LOCAL MIDNIGHTS and round, never add 86.4e6
    // repeatedly: a DST change makes one of those days 23 or 25 hours long, and
    // truncation would put the day either side of a boundary out by one.
    return Math.round((date.getTime() - start.getTime()) / 86400000);
}
function $439e2ca410399204$export$566d600030c4efae(dueISO, now) {
    const text = String(dueISO ?? "").trim();
    // No due date is its OWN group, shown last — not "Later", which it used to share with dates far
    // ahead, making that group the biggest and least useful one on most lists.
    if (!text) return {
        label: "",
        bucket: "None",
        overdue: false
    };
    const date = $439e2ca410399204$export$b3bf801538b9acf7(text);
    if (!date) return {
        label: text,
        bucket: "Later",
        overdue: false
    };
    const days = $439e2ca410399204$export$b5e1c5ebfaeadbc8(date, now);
    const dayMonth = $439e2ca410399204$export$46fe736982a14ec1(date);
    if (days < 0) return {
        label: dayMonth,
        bucket: "Overdue",
        overdue: true
    };
    if (days === 0) return {
        label: (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextDueToday", "Today"),
        bucket: "Today",
        overdue: false
    };
    const label = days === 1 ? (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextDueTomorrow", "Tomorrow") : dayMonth;
    return {
        label: label,
        bucket: days <= 7 ? "This week" : "Later",
        overdue: false
    };
}



// ─────────────────────────────────────────────────────────────────────────────
// PQFORCE Mobile — a TODO's priority. Pure: no DOM, no network.
//
// ⚠ PRIORITY IS NOT A PROPERTY. It is the item's own `marker`, and its values are
// a list the PQFORCE client HARDCODES — the three `prioMarker` entries below,
// copied from it verbatim. The server stores any string in `marker`; one not in
// this list is one the PQFORCE client cannot draw either, so it is NO priority.
//
// The server maps a stored marker to the `id` below (`PQF_MOBILE_PRIORITY_MARKERS`
// in mylibs/pqforce-mobile-todos.php) and back again on create, so the app never
// handles the marker key itself. It is kept here all the same, so the two lists
// can be compared: `MobilePriorityMirrorTest` fails if their pairs differ.
// ─────────────────────────────────────────────────────────────────────────────

const $2336e268632bd98b$export$6a1b098444ac7587 = [
    {
        id: "prio_low",
        marker: "CDE68BA071A2445F8B58D770B212E008",
        name: {
            en: "Low",
            de: "Tief",
            fr: "Faible",
            it: "Bassa",
            "zh-hans": "\u4F4E",
            "zh-hant": "\u4F4E"
        },
        color: "#67d1ff"
    },
    {
        id: "prio_medium",
        marker: "DF8586B06ADA4DA587BE1936BEE2022D",
        name: {
            en: "Medium",
            de: "Mittel",
            fr: "Moyenne",
            it: "Media",
            "zh-hans": "\u4E2D",
            "zh-hant": "\u4E2D"
        },
        color: "#fcc01a"
    },
    {
        id: "prio_high",
        marker: "1F08A59E18FB489D9CC4649CD5F4BF83",
        name: {
            en: "High",
            de: "Hoch",
            fr: "\xc9lev\xe9e",
            it: "Alta",
            "zh-hans": "\u9AD8",
            "zh-hant": "\u9AD8"
        },
        color: "#cb4647"
    }
];
function $2336e268632bd98b$export$29a85ef5d4ea90eb(value) {
    return $2336e268632bd98b$export$6a1b098444ac7587.find((priority)=>priority.id === value) ?? null;
}
function $2336e268632bd98b$export$bf7f685c4fbf1078(value, language = (0, $498477530f916680$export$324a75438e87a537)()) {
    const priority = $2336e268632bd98b$export$29a85ef5d4ea90eb(value);
    if (!priority) return "";
    return priority.name[language] ?? priority.name.en ?? priority.id;
}
function $2336e268632bd98b$export$34aad111a23000c4(color) {
    const hex = /^#([0-9a-f]{6})$/i.exec(color.trim());
    if (!hex) return "#07074e";
    const channel = (offset)=>{
        const c = parseInt(hex[1].slice(offset, offset + 2), 16) / 255;
        return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    };
    const luminance = 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
    return luminance > 0.35 ? "#07074e" : "#ffffff";
}


// ─────────────────────────────────────────────────────────────────────────────
// PQFORCE Mobile — the tenant's own words for "assignee" and "owner". Pure: no DOM, no storage.
//
// A TODO's people are linked through two relation types the tenant configures and NAMES itself
// (bound in the configurator's Mobile section). On the measured tenant they read `Zugewiesen an` /
// `Besitzer` in German and `Assigned to` / `Owner` in English — so the app labels people with those,
// as the PQFORCE web client does, rather than with words of its own that the tenant never chose.
//
// The names arrive with the composer's options (`todos/options` → `relationNames`), in the app's
// language. Until they do, or where the tenant has none, every label keeps the app's own word: each
// call site passes its translated fallback.
//
// Module state, like the current language in i18n.ts, because the labels are drawn deep inside
// renderers that have no reason to carry the profile around — and, like `t()`, read at render time.
// ─────────────────────────────────────────────────────────────────────────────
let $0a4952cb2d3e704b$var$names = {
    assignee: "",
    owner: ""
};
/**
 * The same two relation types named from the PERSON's side (`nameBackward`) — the names of the two
 * LISTS a person has: on the measured tenant `To dos` / `Eigene Todos`, `To-Do List` /
 * `My Own To-Dos`. What the app calls "Assigned to me" and "I own".
 */ let $0a4952cb2d3e704b$var$listNames = {
    assignee: "",
    owner: ""
};
const $0a4952cb2d3e704b$var$pickNames = (raw)=>{
    const pick = (value)=>typeof value === "string" ? value.trim() : "";
    const source = raw && typeof raw === "object" ? raw : {};
    return {
        assignee: pick(source.assignee),
        owner: pick(source.owner)
    };
};
function $0a4952cb2d3e704b$export$f9c9510d76d586c3(raw, lists) {
    const source = raw && typeof raw === "object" ? raw : {};
    if ("roles" in source || "lists" in source) {
        $0a4952cb2d3e704b$var$names = $0a4952cb2d3e704b$var$pickNames(source.roles);
        $0a4952cb2d3e704b$var$listNames = $0a4952cb2d3e704b$var$pickNames(source.lists);
        return;
    }
    $0a4952cb2d3e704b$var$names = $0a4952cb2d3e704b$var$pickNames(raw);
    $0a4952cb2d3e704b$var$listNames = $0a4952cb2d3e704b$var$pickNames(lists);
}
function $0a4952cb2d3e704b$export$ae1f3e529360b802() {
    return {
        roles: {
            ...$0a4952cb2d3e704b$var$names
        },
        lists: {
            ...$0a4952cb2d3e704b$var$listNames
        }
    };
}
function $0a4952cb2d3e704b$export$38e3edeba43dc984(role, fallback) {
    return $0a4952cb2d3e704b$var$listNames[role] || fallback;
}
function $0a4952cb2d3e704b$export$8cfece6d5e778803(role, fallback) {
    return $0a4952cb2d3e704b$var$names[role] || fallback;
}


/**
 * The colour per CATEGORY, for a state the tenant gave no usable colour — as a THEME TOKEN.
 *
 * ⚠ The fallback, not the rule. A tenant's own state colour always wins (see `stateColor()`),
 * because it is the only thing that tells two states of one category apart — "In progress" and
 * "To be reviewed" are both ACTIVE.
 *
 * A token rather than a hex because these are OUR colours and must follow light and dark mode:
 * ACTIVE's navy is the dark ground's own colour. `mobile.scss` defines `--color-state-*` per theme.
 * A tenant's colour is theirs and stays as they chose it; the stylesheet lifts it in dark mode.
 */ const $0a55aae61f0d5dc0$var$CATEGORY_COLORS = {
    NEW: "var(--color-state-new)",
    PLANNING: "var(--color-state-new)",
    ACTIVE: "var(--color-state-active)",
    CLOSED: "var(--color-state-closed)"
};
/** The colour of a state with neither a colour nor a category. */ const $0a55aae61f0d5dc0$var$FALLBACK_STATE_COLOR = "var(--color-state-new)";
function $0a55aae61f0d5dc0$export$7e06879938900bda(value) {
    const category = String(value ?? "").trim().toUpperCase();
    return (0, $6ad175d5fef0aab4$export$c7e9902bc85a21ff).includes(category) ? category : null;
}
function $0a55aae61f0d5dc0$export$b408166f1b79530f(value) {
    const text = String(value ?? "").trim();
    const short = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(text);
    if (short) return `#${short[1]}${short[1]}${short[2]}${short[2]}${short[3]}${short[3]}`.toUpperCase();
    return /^#[0-9a-f]{6}$/i.test(text) ? text.toUpperCase() : null;
}
/** WCAG relative luminance of `#RRGGBB`. */ function $0a55aae61f0d5dc0$var$luminanceOf(hex) {
    const channel = (offset)=>{
        const c = parseInt(hex.slice(offset, offset + 2), 16) / 255;
        return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    };
    return 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5);
}
function $0a55aae61f0d5dc0$export$e73df0c08bfd3ec3(a, b) {
    const [hi, lo] = [
        $0a55aae61f0d5dc0$var$luminanceOf(a),
        $0a55aae61f0d5dc0$var$luminanceOf(b)
    ].sort((x, y)=>y - x);
    return (hi + 0.05) / (lo + 0.05);
}
/** `#RRGGBB` mixed with `toward` by `share` (0–1) — what CSS `color-mix(in srgb, …)` computes. */ function $0a55aae61f0d5dc0$var$mixHex(hex, toward, share) {
    const part = (h, offset)=>parseInt(h.slice(offset, offset + 2), 16);
    const out = [
        1,
        3,
        5
    ].map((offset)=>Math.round(part(hex, offset) * (1 - share) + part(toward, offset) * share).toString(16).padStart(2, "0"));
    return `#${out.join("")}`.toUpperCase();
}
function $0a55aae61f0d5dc0$export$5d3eb9c86643f42d(hex) {
    let color = hex.toUpperCase();
    for(let step = 0; step < 20; step++){
        const tint = $0a55aae61f0d5dc0$var$mixHex(color, "#FFFFFF", 0.86);
        if ($0a55aae61f0d5dc0$export$e73df0c08bfd3ec3(color, tint) >= 3) return color;
        color = $0a55aae61f0d5dc0$var$mixHex(color, "#000000", 0.08);
    }
    return color;
}
function $0a55aae61f0d5dc0$export$38e0108eebce6b1c(category, tenantColor) {
    const own = $0a55aae61f0d5dc0$export$b408166f1b79530f(tenantColor);
    if (own) return $0a55aae61f0d5dc0$export$5d3eb9c86643f42d(own);
    const known = $0a55aae61f0d5dc0$export$7e06879938900bda(category);
    return known ? $0a55aae61f0d5dc0$var$CATEGORY_COLORS[known] : $0a55aae61f0d5dc0$var$FALLBACK_STATE_COLOR;
}
function $0a55aae61f0d5dc0$export$798a7b69c8458ebd(transition) {
    // ⚠ A self-transition never reaches here — `movesOf()` filters it out, because it is not a
    // move at all: it IS the comment field. See selfTransitionOf() below.
    return transition.to;
}
function $0a55aae61f0d5dc0$export$9dfebb7ead965eaa(todo) {
    return todo.transitions.find((t)=>t.selfTransition === true) ?? null;
}
function $0a55aae61f0d5dc0$export$13e2f80d35473793(todo) {
    return todo.transitions.filter((t)=>t.selfTransition !== true);
}
function $0a55aae61f0d5dc0$export$420aa3698f8d9bbc(transition) {
    return transition?.label?.trim() || (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextWriteComment", "Write a comment");
}
function $0a55aae61f0d5dc0$export$7e2424d55072c6e6(todo) {
    const holder = $0a55aae61f0d5dc0$export$9d312342c5da567c(todo).name;
    return holder ? (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextLogWithHolder", "You cannot add to the log while this TODO is with {holder}.", {
        holder: holder
    }) : (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextLogWithSomebody", "You cannot add to the log while this TODO is with somebody else.");
}
function $0a55aae61f0d5dc0$export$e7e86996b7d67fe9() {
    return (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextTransitionNoteHint", "Optional. Anything you write is stored with the transition.");
}
function $0a55aae61f0d5dc0$export$1963261d6999187(todo) {
    if (todo.relation === "assignee" && todo.statusCategory !== "CLOSED") // The owner may be unknown when the tenant has bound no owner relation —
    // "Waiting for  ." is worse than not naming anybody.
    return todo.owner ? (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextWaitingFor", "Waiting for {owner}. You cannot move it further.", {
        owner: todo.owner
    }) : (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextWaitingForOwner", "Waiting for its owner. You cannot move it further.");
    return (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextNoFurtherTransition", "Your relation to this TODO allows no further transition.");
}
function $0a55aae61f0d5dc0$export$f6a098653d630c23(name) {
    return name.split(/\s+/).filter(Boolean).map((w)=>w[0]).join("").slice(0, 2).toUpperCase();
}
const $0a55aae61f0d5dc0$var$MONTHS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec"
];
function $0a55aae61f0d5dc0$export$a12a26c5f76005ed(ts) {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n)=>String(n).padStart(2, "0");
    const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
    // English from the fixed table, byte-identical to the design; German through Intl, in the app's
    // language rather than the phone's — see `app/mobile/i18n.ts`.
    if ((0, $498477530f916680$export$324a75438e87a537)() === "en") return `${d.getDate()} ${$0a55aae61f0d5dc0$var$MONTHS[d.getMonth()]}, ${time}`;
    return `${new Intl.DateTimeFormat((0, $498477530f916680$export$a6ce7dcbdf9c4fcc)(), {
        day: "numeric",
        month: "short"
    }).format(d)}, ${time}`;
}
function $0a55aae61f0d5dc0$export$6d9937f48401e58a(comments) {
    return [
        ...comments
    ].sort((a, b)=>b.ts - a.ts);
}
function $0a55aae61f0d5dc0$export$64474058fdddf6fa(comment) {
    return !!comment.to && comment.to !== comment.from;
}
function $0a55aae61f0d5dc0$export$7887e07d7b5d5fe1(todos, relation) {
    return todos.filter((t)=>t.relations.includes(relation));
}
function $0a55aae61f0d5dc0$export$da39d8d3da1486ab(todo) {
    return todo.statusCategory !== "CLOSED";
}
function $0a55aae61f0d5dc0$export$371eecdf1cc71c1(todos) {
    return todos.filter($0a55aae61f0d5dc0$export$da39d8d3da1486ab).length;
}
function $0a55aae61f0d5dc0$export$4db3194d3a8e1f24(todos, allTodos, scope) {
    if (scope !== "all") return $0a55aae61f0d5dc0$export$7887e07d7b5d5fe1(todos, scope);
    const own = new Map(todos.map((todo)=>[
            todo.id,
            todo
        ]));
    return (allTodos ?? []).map((todo)=>own.get(todo.id) ?? todo);
}
/**
 * The lifecycle order of a todo's state, for sorting. A state with no order sorts after every state
 * that has one — it has to appear somewhere, and inventing a position would be a guess.
 */ function $0a55aae61f0d5dc0$var$stateRank(todo) {
    return typeof todo.statusOrder === "number" ? todo.statusOrder : Number.POSITIVE_INFINITY;
}
/** Ascending lifecycle order; ties by name, so two states the tenant left unordered still sort predictably. */ function $0a55aae61f0d5dc0$var$byLifecycleOrder(a, b) {
    const ra = $0a55aae61f0d5dc0$var$stateRank(a);
    const rb = $0a55aae61f0d5dc0$var$stateRank(b);
    if (ra !== rb) return ra < rb ? -1 : 1;
    return a.status.localeCompare(b.status);
}
/** What a todo is grouped under when grouping by status: its state id, or its name from an older server. */ function $0a55aae61f0d5dc0$var$stateKey(todo) {
    return todo.statusId || `name:${todo.status}`;
}
function $0a55aae61f0d5dc0$export$e7b38ea9409824d2(grouping) {
    const at = (0, $6ad175d5fef0aab4$export$a40d177a2c848217).indexOf(grouping);
    return (0, $6ad175d5fef0aab4$export$a40d177a2c848217)[(at + 1) % (0, $6ad175d5fef0aab4$export$a40d177a2c848217).length] ?? "due";
}
function $0a55aae61f0d5dc0$export$3110bb3f95eace5(todos, grouping, now) {
    // ⚠ ONE instant for the whole list. Deriving per todo from its own `Date.now()` would let a
    // list straddle midnight and put two todos with the same date in different groups.
    /**
     * ⚠ WITHIN a group, by lifecycle order and then by title — never the order the API happened to
     * return. That order is not stable between two reads, so a list could reshuffle under
     * somebody's thumb between one refresh and the next with nothing having changed.
     *
     * Lifecycle order first, so a due group reads the way the tenant's process runs; title second,
     * which is the only tie-break a reader can predict. When grouping BY status the state is
     * constant inside a group, so this degrades to title alone.
     *
     * `localeCompare` rather than `<`: these are the tenant's own words, and umlauts sort as
     * letters rather than as codepoints.
     */ const sortItems = (items)=>items.sort((a, b)=>$0a55aae61f0d5dc0$var$byLifecycleOrder(a, b) || a.title.localeCompare(b.title));
    if (grouping === "due") return (0, $6ad175d5fef0aab4$export$5cb4e1555e39a8c8).map((label)=>{
        const items = sortItems(todos.filter((t)=>(0, $439e2ca410399204$export$566d600030c4efae)(t.dueISO, now).bucket === label));
        // ⚠ The KEY keeps the bucket value — it is what a device remembers as folded, and it
        // must not change with the language. Only the heading is translated. `due` is what the
        // heading's chip is coloured by.
        return {
            key: `due:${label}`,
            label: (0, $439e2ca410399204$export$c78b734ae6fbc937)(label),
            due: label,
            count: items.length,
            items: items
        };
    }).filter((g)=>g.items.length > 0);
    if (grouping === "project") return $0a55aae61f0d5dc0$var$projectGroups(todos, sortItems);
    if (grouping === "priority") {
        // Highest first — the reverse of the PQFORCE client's own low-to-high list, because a list
        // grouped by priority is read for what to do first. An unknown value is "none", as everywhere.
        const levels = [
            ...(0, $2336e268632bd98b$export$6a1b098444ac7587)
        ].reverse();
        const byLevel = levels.map((priority)=>{
            const items = sortItems(todos.filter((t)=>(0, $2336e268632bd98b$export$29a85ef5d4ea90eb)(t.priority)?.id === priority.id));
            return {
                key: `priority:${priority.id}`,
                label: (0, $2336e268632bd98b$export$bf7f685c4fbf1078)(priority.id),
                priority: priority.id,
                count: items.length,
                items: items
            };
        });
        const none = sortItems(todos.filter((t)=>!(0, $2336e268632bd98b$export$29a85ef5d4ea90eb)(t.priority)));
        return [
            ...byLevel,
            {
                key: "priority:none",
                label: (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewNoPriorityGroup", "No priority"),
                count: none.length,
                items: none
            }
        ].filter((g)=>g.items.length > 0);
    }
    // Grouped by the state itself (its id), headed by the tenant's name for it, in the tenant's
    // lifecycle order. Keyed on the id so two states that happen to share a name stay two groups.
    const groups = new Map();
    for (const todo of todos){
        const key = $0a55aae61f0d5dc0$var$stateKey(todo);
        groups.set(key, [
            ...groups.get(key) ?? [],
            todo
        ]);
    }
    return [
        ...groups.values()
    ].sort((a, b)=>$0a55aae61f0d5dc0$var$byLifecycleOrder(a[0], b[0])).map((items)=>{
        const first = items[0];
        return {
            key: `status:${$0a55aae61f0d5dc0$var$stateKey(first)}`,
            label: first.status,
            // Read off the group's first TODO: grouped by the state's id, they all share it.
            state: {
                status: first.status,
                statusCategory: first.statusCategory,
                statusColor: first.statusColor,
                statusIcon: first.statusIcon
            },
            count: items.length,
            items: sortItems(items)
        };
    });
}
function $0a55aae61f0d5dc0$export$40ff6f45b231e2f7(todo) {
    const ppm = todo.ppm;
    if (ppm) {
        const id = ppm.projectId ?? "";
        const name = ppm.kind === "project" ? ppm.name : ppm.kind === "task" ? ppm.project : "";
        if (id || name) return {
            id: id,
            name: name
        };
    }
    const related = (todo.related ?? []).find((link)=>link.project);
    return related ? {
        id: "",
        name: related.project
    } : null;
}
/**
 * The list grouped by project: one group per project, alphabetically, then "No project".
 *
 * ⚠ KEYED BY THE PROJECT'S ID, never its name, wherever the id is known — two projects may share a
 * name, and one heading over both would put one project's work under the other. A TODO known only
 * by a project NAME (a risk or report link) joins the group of the one project in this list with
 * that name; where no project or several carry it, it gets a group of its own under that name,
 * rather than being filed under a project it may not belong to.
 *
 * A project whose name could not be read is still a project: headed "Unnamed project", one group
 * per id, sorted after the named ones.
 */ function $0a55aae61f0d5dc0$var$projectGroups(todos, sortItems) {
    const byKey = new Map();
    const add = (key, label, named, todo)=>{
        const group = byKey.get(key) ?? {
            label: label,
            named: named,
            items: []
        };
        group.items.push(todo);
        byKey.set(key, group);
    };
    // Which ids carry each name, among the TODOs whose project is known by id.
    const idsByName = new Map();
    for (const todo of todos){
        const project = $0a55aae61f0d5dc0$export$40ff6f45b231e2f7(todo);
        if (project?.id && project.name) idsByName.set(project.name, (idsByName.get(project.name) ?? new Set()).add(project.id));
    }
    const none = [];
    for (const todo of todos){
        const project = $0a55aae61f0d5dc0$export$40ff6f45b231e2f7(todo);
        if (!project) {
            none.push(todo);
            continue;
        }
        if (project.id) {
            add(`project:id:${project.id}`, project.name || (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewUnnamedProject", "Unnamed project"), !!project.name, todo);
            continue;
        }
        const ids = idsByName.get(project.name);
        if (ids && ids.size === 1) add(`project:id:${[
            ...ids
        ][0]}`, project.name, true, todo);
        else add(`project:name:${project.name}`, project.name, true, todo);
    }
    const groups = [
        ...byKey.entries()
    ].sort(([, a], [, b])=>a.named === b.named ? a.label.localeCompare(b.label) : a.named ? -1 : 1).map(([key, group])=>({
            key: key,
            label: group.label,
            ...key.startsWith("project:id:") ? {
                projectId: key.slice(11)
            } : {},
            count: group.items.length,
            items: sortItems(group.items)
        }));
    if (none.length) groups.push({
        key: "project:none",
        label: (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewNoProjectGroup", "No project"),
        count: none.length,
        items: sortItems(none)
    });
    return groups;
}
function $0a55aae61f0d5dc0$export$8f30fba2bc1b196d(collapsed, key) {
    return collapsed.includes(key) ? collapsed.filter((k)=>k !== key) : [
        ...collapsed,
        key
    ];
}
function $0a55aae61f0d5dc0$export$7b98c8e0261a518e(collapsed, keys, collapse) {
    if (!collapse) return collapsed.filter((key)=>!keys.includes(key));
    return [
        ...collapsed,
        ...keys.filter((key)=>!collapsed.includes(key))
    ];
}
function $0a55aae61f0d5dc0$export$44f691e0cb516e9e(collapsed, keys) {
    return keys.some((key)=>!collapsed.includes(key));
}
function $0a55aae61f0d5dc0$export$7a7fcaa2edee7e0b(raw) {
    try {
        const parsed = raw ? JSON.parse(raw) : [];
        if (!Array.isArray(parsed)) return [];
        const keys = parsed.filter((k)=>typeof k === "string" && /^(due|status|priority|project):/.test(k));
        return [
            ...new Set(keys)
        ].slice(-$0a55aae61f0d5dc0$export$336db08e250a7b79);
    } catch  {
        return [];
    }
}
const $0a55aae61f0d5dc0$export$336db08e250a7b79 = 100;
function $0a55aae61f0d5dc0$export$2a61f2c4a41ccf86(todos, relation, now) {
    const list = $0a55aae61f0d5dc0$export$7887e07d7b5d5fe1(todos, relation);
    // One count per OPEN state that holds something, in lifecycle order. CLOSED is the one category
    // read for meaning here, and only to leave finished work out of a tile about what is on your plate.
    const open = new Map();
    for (const todo of [
        ...list
    ].sort($0a55aae61f0d5dc0$var$byLifecycleOrder)){
        if (todo.statusCategory === "CLOSED") continue;
        const key = $0a55aae61f0d5dc0$var$stateKey(todo);
        open.set(key, [
            ...open.get(key) ?? [],
            todo
        ]);
    }
    const states = [
        ...open.values()
    ].map((items)=>({
            label: items[0].status,
            count: items.length
        }));
    return {
        total: list.length,
        overdue: list.filter((t)=>(0, $439e2ca410399204$export$566d600030c4efae)(t.dueISO, now).overdue).length,
        states: states
    };
}
function $0a55aae61f0d5dc0$export$f0958e71b8d4c18d(summary) {
    return [
        (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextOverdueCount", "{count} overdue", {
            count: summary.overdue
        }),
        ...summary.states.map((s)=>`${s.count} ${s.label}`)
    ].join(" \xb7 ");
}
function $0a55aae61f0d5dc0$export$af79cddbf50192cb(todos, now, limit = 3) {
    const bucketOf = (t)=>(0, $439e2ca410399204$export$566d600030c4efae)(t.dueISO, now).bucket;
    const rank = (t)=>bucketOf(t) === "Overdue" ? 0 : 1;
    return todos.filter((t)=>bucketOf(t) === "Overdue" || bucketOf(t) === "Today").sort((a, b)=>rank(a) - rank(b)).slice(0, limit);
}
function $0a55aae61f0d5dc0$export$9de787bb37c116a0(count) {
    return count === 1 ? (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextUrgentOne", "1 thing needs you today") : (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextUrgentMany", "{count} things need you today", {
        count: count
    });
}
function $0a55aae61f0d5dc0$export$67fb02f47ab7311e(names) {
    const named = names.map((n)=>n.trim()).filter(Boolean);
    if (named.length <= 1) return named[0] ?? "";
    return (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextNamesAnd", "{names} and {last}", {
        names: named.slice(0, -1).join(", "),
        last: named[named.length - 1]
    });
}
function $0a55aae61f0d5dc0$export$5b9e1d8c1d3d59ac(todo) {
    return todo.assignees.map((a)=>a.name.trim()).filter(Boolean).join(", ");
}
function $0a55aae61f0d5dc0$export$c6f240c4f4db706a(people, selfResourceId) {
    const mine = selfResourceId !== "" && people.some((p)=>p.id === selfResourceId);
    const others = people.filter((p)=>!(mine && p.id === selfResourceId)).map((p)=>p.name.trim()).filter(Boolean);
    return (mine ? [
        (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextYou", "You"),
        ...others
    ] : others).join(", ");
}
function $0a55aae61f0d5dc0$export$293dc77768dec4fd(todo, selfResourceId) {
    return todo.assignees.filter((a)=>a.id !== "" && a.id !== selfResourceId);
}
function $0a55aae61f0d5dc0$export$77cf39232a7a6ce8(targets) {
    if (targets.length === 1) return targets[0].name ? (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextNudgeOne", "Nudge {name}", {
        name: targets[0].name
    }) : (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextNudgeAssignee", "Nudge the assignee");
    return (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextNudgeMany", "Nudge {count} assignees", {
        count: targets.length
    });
}
function $0a55aae61f0d5dc0$export$31255910f3c96146(todo, result) {
    const nameOf = (id)=>todo.assignees.find((a)=>a.id === id)?.name.trim() ?? "";
    const names = (ids)=>$0a55aae61f0d5dc0$export$67fb02f47ab7311e(ids.map(nameOf));
    const emailed = names(result.emailed);
    const parts = [
        emailed ? (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextNudgeEmailedTo", "Reminder emailed to {names}.", {
            names: emailed
        }) : (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextNudgeEmailed", "Reminder emailed.")
    ];
    const by = (reason)=>result.skipped.filter((s)=>s.reason === reason).map((s)=>s.resourceId);
    const optedOut = by("optedOut");
    const noAddress = [
        ...by("noAddress"),
        ...by("noAccount")
    ];
    const failed = by("failed");
    // "Somebody" stands in for a name the TODO could not resolve — whole sentences, so a translator
    // never has to fit a pronoun into somebody else's grammar.
    if (optedOut.length) {
        const who = names(optedOut);
        parts.push(!who ? (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextNudgeOptedOutSomebody", "Somebody has email notifications off.") : optedOut.length === 1 ? (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextNudgeOptedOutOne", "{names} has email notifications off.", {
            names: who
        }) : (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextNudgeOptedOutMany", "{names} have email notifications off.", {
            names: who
        }));
    }
    if (noAddress.length) {
        const who = names(noAddress);
        parts.push(who ? (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextNudgeNoAddress", "No email address is stored for {names}.", {
            names: who
        }) : (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextNudgeNoAddressSomebody", "No email address is stored for somebody."));
    }
    if (failed.length) {
        const who = names(failed);
        parts.push(who ? (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextNudgeFailed", "The mail to {names} could not be sent.", {
            names: who
        }) : (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextNudgeFailedSomebody", "The mail to somebody could not be sent."));
    }
    return parts.join(" ");
}
function $0a55aae61f0d5dc0$export$b2f7348c8e4be141(todo, people) {
    const assignedIds = new Set(todo.assignees.map((a)=>a.id));
    return {
        assigned: todo.assignees.filter((a)=>a.id !== ""),
        others: people.filter((p)=>!assignedIds.has(p.id))
    };
}
/**
 * Does `haystack` answer `query`?
 *
 * Every word typed must appear somewhere in it, in any order — "müller anna" finds "Anna Müller".
 * Case and accents are ignored, so "muller" finds her too: a phone keyboard makes an umlaut a
 * long-press, and nobody searching should need one. An empty query matches everything.
 *
 * ONE rule for both searches in the app, so the assignees sheet and the TODO list cannot come to
 * disagree about what typing two words means.
 */ function $0a55aae61f0d5dc0$var$matchesQueryWords(haystack, query) {
    const fold = (s)=>s.normalize("NFD").replace(/\p{M}/gu, "").toLocaleLowerCase();
    const folded = fold(haystack);
    return fold(query).split(/\s+/).filter(Boolean).every((word)=>folded.includes(word));
}
function $0a55aae61f0d5dc0$export$1ad433f9ad7133a0(name, query) {
    return $0a55aae61f0d5dc0$var$matchesQueryWords(name, query);
}
/** The named entities a tenant's rich text actually carries, plus whatever it numbers. */ const $0a55aae61f0d5dc0$var$HTML_ENTITIES = {
    nbsp: " ",
    amp: "&",
    lt: "<",
    gt: ">",
    quot: '"',
    apos: "'"
};
/**
 * The readable TEXT of a description, for MATCHING ONLY.
 *
 * ⚠ NOT A SANITISER, and nothing it returns is ever put on screen — `richTextHtml()` in `dom.ts`
 * does that, from a tree. This only has to produce a haystack, which is why a regex is honest here
 * and is a defect there: the worst a malformed tag can do is make a word unfindable.
 *
 * ⚠ TAGS FIRST, ENTITIES SECOND. The other order turns a written `&lt;b&gt;` into a tag and then
 * deletes it, losing the words somebody typed — the trap `pqfSanitizeSupportHtml()` records from the
 * other side. Tags become a SPACE, or `one</p><p>two` matches nothing but "onetwo".
 */ function $0a55aae61f0d5dc0$var$descriptionText(html) {
    if (!html) return "";
    return html.replace(/<[^>]*>/g, " ").replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (entity, body)=>{
        if (body[0] !== "#") return $0a55aae61f0d5dc0$var$HTML_ENTITIES[body.toLowerCase()] ?? entity;
        const code = body[1] === "x" || body[1] === "X" ? Number.parseInt(body.slice(2), 16) : Number.parseInt(body.slice(1), 10);
        return Number.isFinite(code) && code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : entity;
    });
}
function $0a55aae61f0d5dc0$export$2070e4233e275ce0(todo, query) {
    // The CODE too (`T-12`): it is what people say to each other about a TODO, and typing it is the
    // quickest way to find one somebody mentioned.
    return $0a55aae61f0d5dc0$var$matchesQueryWords(`${todo.code ?? ""} ${todo.title} ${$0a55aae61f0d5dc0$var$descriptionText(todo.description)}`, query);
}
const $0a55aae61f0d5dc0$export$d9f56df35f0ee9ae = new Set([
    "assignee",
    "owner"
]);
function $0a55aae61f0d5dc0$export$c54a0983716bc386(todo) {
    const slots = [
        "ppm"
    ];
    if ((todo.related ?? []).length > 0) slots.push("related");
    slots.push("assignee", "owner");
    const mayLink = todo.permissions?.link === true && !todo.partial;
    return slots.map((slot)=>({
            slot: slot,
            editable: mayLink && $0a55aae61f0d5dc0$export$d9f56df35f0ee9ae.has(slot)
        }));
}
function $0a55aae61f0d5dc0$export$7ba8ca721a3ec0a6(todos, known) {
    const byId = new Map(known.filter((t)=>t.permissions).map((t)=>[
            t.id,
            t.permissions
        ]));
    if (byId.size === 0) return todos;
    let changed = false;
    const out = todos.map((todo)=>{
        const permissions = byId.get(todo.id);
        if (todo.permissions || !permissions) return todo;
        changed = true;
        return {
            ...todo,
            permissions: permissions
        };
    });
    return changed ? out : todos;
}
function $0a55aae61f0d5dc0$export$96d1499a68ee10b5(_state) {
    return "todos";
}
function $0a55aae61f0d5dc0$export$4f5121a84b794f1e(todos, query) {
    if (!query.trim()) return [];
    return todos.filter((todo)=>$0a55aae61f0d5dc0$export$2070e4233e275ce0(todo, query)).sort((a, b)=>{
        if (a.dueISO !== b.dueISO) {
            if (!a.dueISO) return 1;
            if (!b.dueISO) return -1;
            return a.dueISO < b.dueISO ? -1 : 1;
        }
        return a.title.localeCompare(b.title);
    });
}
function $0a55aae61f0d5dc0$export$1dcbd4d406e534bd(todos, known = []) {
    const icons = new Map();
    const learn = (id, icon)=>{
        if (id && icon?.d && !icons.has(id)) icons.set(id, icon);
    };
    for (const list of [
        todos,
        known
    ])for (const todo of list){
        learn(todo.statusId, todo.statusIcon);
        for (const transition of todo.transitions)learn(transition.toId, transition.toIcon);
    }
    let changed = false;
    const filled = todos.map((todo)=>{
        // The icon to ADD, or undefined — one that arrived is never replaced.
        const statusIcon = todo.statusIcon?.d ? undefined : icons.get(todo.statusId);
        let transitionsChanged = false;
        const transitions = todo.transitions.map((transition)=>{
            const toIcon = transition.toIcon?.d || !transition.toId ? undefined : icons.get(transition.toId);
            if (!toIcon) return transition;
            transitionsChanged = true;
            return {
                ...transition,
                toIcon: toIcon
            };
        });
        if (!statusIcon && !transitionsChanged) return todo;
        changed = true;
        return {
            ...todo,
            ...statusIcon ? {
                statusIcon: statusIcon
            } : {},
            ...transitionsChanged ? {
                transitions: transitions
            } : {}
        };
    });
    return changed ? filled : todos;
}
function $0a55aae61f0d5dc0$export$9d312342c5da567c(todo) {
    return todo.relation === "assignee" ? {
        // ⚠ Plural when there are several, like the assignee side below it — a TODO may have
        // several owners, and "Owner: Anna, Marc" says one of them is called both names.
        // The tenant's own name for the role wins (relationNames.ts); the app's word otherwise.
        label: (0, $0a4952cb2d3e704b$export$8cfece6d5e778803)("owner", todo.owners.length > 1 ? (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextOwners", "Owners") : (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextOwner", "Owner")),
        name: todo.owner
    } : {
        label: (0, $0a4952cb2d3e704b$export$8cfece6d5e778803)("assignee", todo.assignees.length > 1 ? (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextAssignees", "Assignees") : (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextAssignee", "Assignee")),
        name: $0a55aae61f0d5dc0$export$5b9e1d8c1d3d59ac(todo)
    };
}
function $0a55aae61f0d5dc0$export$ec59b59d53da5a56(todo) {
    return todo.relation === "assignee" ? (0, $0a4952cb2d3e704b$export$38e3edeba43dc984)("assignee", (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextRoleYours", "Yours")) : (0, $0a4952cb2d3e704b$export$38e3edeba43dc984)("owner", (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextRoleYouOwn", "You own"));
}
function $0a55aae61f0d5dc0$export$c43d9bf90e88579a(todos, todoId, to, note, who, now, nextTransitions) {
    return todos.map((t)=>t.id === todoId ? {
            ...t,
            status: to,
            transitions: nextTransitions,
            comments: [
                ...t.comments,
                {
                    who: who,
                    ts: now,
                    from: t.status,
                    to: to,
                    text: note.trim()
                }
            ]
        } : t);
}
function $0a55aae61f0d5dc0$export$f34d4a5bbdd59dbc(todos, todoId, text, who, now) {
    const trimmed = text.trim();
    if (!trimmed) return [
        ...todos
    ];
    return todos.map((t)=>t.id === todoId ? {
            ...t,
            comments: [
                ...t.comments,
                {
                    who: who,
                    ts: now,
                    text: trimmed
                }
            ]
        } : t);
}
function $0a55aae61f0d5dc0$export$13ed3280aeacbdb1(kind) {
    switch(kind){
        case "task":
            return (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextKindTask", "Task");
        case "project":
            return (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextKindProject", "Project");
        default:
            return (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextKindPortfolio", "Portfolio");
    }
}
function $0a55aae61f0d5dc0$export$f742eb0bd8e19d1c(ppm) {
    if (!ppm) return "";
    const above = [
        ppm.project,
        ppm.portfolio
    ].filter((name)=>!!name);
    return above.length ? (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextInContext", "in {context}", {
        context: above.join(" \xb7 ")
    }) : "";
}
function $0a55aae61f0d5dc0$export$6de3918520797b13(ppm) {
    if (!ppm) return (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextNoProjectLink", "No project link");
    const head = ppm.name || $0a55aae61f0d5dc0$export$13ed3280aeacbdb1(ppm.kind);
    const context = ppm.project || ppm.portfolio;
    return context ? `${head} \xb7 ${context}` : head;
}
function $0a55aae61f0d5dc0$export$53ca5286fdb64f67(kind) {
    switch(kind){
        case "risk":
            return (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextKindRisk", "Risk");
        case "projectReport":
            return (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextKindProjectReport", "Project report");
        default:
            return (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextKindPortfolioReport", "Portfolio report");
    }
}
function $0a55aae61f0d5dc0$export$fc10d3c2823c1c52(link) {
    const above = [
        link.project,
        link.portfolio
    ].filter((name)=>!!name);
    return above.length ? (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextInContext", "in {context}", {
        context: above.join(" \xb7 ")
    }) : "";
}
function $0a55aae61f0d5dc0$export$63902713e18bfd31(todo) {
    if (todo.ppm) return $0a55aae61f0d5dc0$export$6de3918520797b13(todo.ppm);
    const first = (todo.related ?? [])[0];
    if (!first) return $0a55aae61f0d5dc0$export$6de3918520797b13(null);
    const head = first.name || $0a55aae61f0d5dc0$export$53ca5286fdb64f67(first.kind);
    const context = first.project || first.portfolio;
    return context ? `${head} \xb7 ${context}` : head;
}



function $d2bd7d9b8ec176dc$export$cafef7527b6deb0c(payload) {
    return {
        id: payload.id,
        itemType: payload.itemType,
        relation: payload.relation,
        // A payload from before the server carried the list still has one relation, and it is
        // the one it named.
        relations: payload.relations ?? [
            payload.relation
        ],
        status: payload.status,
        // ⚠ An older server sends none of these three. Grouping then falls back to the name, the
        // chip to the neutral paint, and the order to alphabetical — all visible, none a guess.
        statusId: payload.statusId ?? "",
        statusCategory: (0, $0a55aae61f0d5dc0$export$7e06879938900bda)(payload.statusCategory),
        statusColor: payload.statusColor ?? null,
        statusOrder: typeof payload.statusOrder === "number" && Number.isFinite(payload.statusOrder) ? payload.statusOrder : null,
        title: payload.title,
        description: payload.description ?? "",
        descriptionText: payload.descriptionText ?? "",
        // ⚠ Strictly `=== true`: absent means an older server, which is not a promise that the
        // value is plain. See the field's own note in types.ts.
        descriptionEditable: payload.descriptionEditable === true,
        // ⚠ ABSENT AND NULL MEAN THE SAME THING HERE: no link. `undefined` is a server that
        // predates the field, `null` is one that looked and found nothing, and a reader cannot act
        // on the difference — so both become null rather than the field being made optional, which
        // would push the same choice out to every render site.
        ppm: payload.ppm ?? null,
        related: payload.related ?? [],
        project: payload.project ?? "",
        task: payload.task ?? "\u2014",
        // An older server sends one `assignee` name and no ids. Shown as it came, with no id — so it
        // reads correctly and cannot be addressed by an add, a remove or a nudge.
        assignees: Array.isArray(payload.assignees) ? payload.assignees.filter((a)=>a && typeof a.id === "string" && a.id !== "").map((a)=>({
                id: a.id,
                name: String(a.name ?? "")
            })) : payload.assignee ? [
            {
                id: "",
                name: payload.assignee
            }
        ] : [],
        // ⚠ THREE SHAPES, because a phone outlives a deploy: `{id,name}` from today's server, bare
        // names from yesterday's, and `owner` alone from the one before that. An entry with no id
        // matches nobody, which is the right answer — it simply shows a name instead of "You".
        owners: (payload.owners ?? (payload.owner ? [
            payload.owner
        ] : [])).map((o)=>typeof o === "string" ? {
                id: "",
                name: o
            } : {
                id: String(o.id ?? ""),
                name: String(o.name ?? "")
            }),
        owner: payload.owner ?? "",
        // ⚠ ONE stored value. The label, the group and the overdue flag are derived on the DEVICE,
        // against the device's own clock — see `dueFacts()`. The server still sends the three
        // computed fields for one release, for a phone running an older bundle; this one ignores
        // them, because they are the server's opinion of what "today" means.
        dueISO: payload.dueISO ?? "",
        // ⚠ NULL, not a neutral priority, when the item has no marker — or one this app does not
        // know (an older server sent `High`/`Normal`/`Low` from a property binding).
        priority: (0, $2336e268632bd98b$export$29a85ef5d4ea90eb)(payload.priority)?.id ?? null,
        comments: (payload.comments ?? []).map((comment)=>({
                ...comment,
                fromCategory: (0, $0a55aae61f0d5dc0$export$7e06879938900bda)(comment.fromCategory),
                toCategory: (0, $0a55aae61f0d5dc0$export$7e06879938900bda)(comment.toCategory)
            })),
        transitions: (payload.transitions ?? []).map((transition)=>({
                ...transition,
                toCategory: (0, $0a55aae61f0d5dc0$export$7e06879938900bda)(transition.toCategory)
            })),
        ...payload.partial === true ? {
            partial: true
        } : {},
        code: typeof payload.code === "string" ? payload.code : "",
        // ⚠ Strictly `=== true` per action: anything else the server sent is not a grant.
        ...payload.permissions && typeof payload.permissions === "object" ? {
            permissions: {
                update: payload.permissions.update === true,
                link: payload.permissions.link === true,
                delete: payload.permissions.delete === true
            }
        } : {}
    };
}
class $d2bd7d9b8ec176dc$export$b63f8c437a9f7168 {
    constructor(client){
        this.client = client;
    }
    // ── onboarding ───────────────────────────────────────────────────────────
    /**
     * Ask the portal which sign-in methods an installation offers.
     *
     * The address the user typed is sent WHOLE (alias included): the host decides
     * discovery, but `ACM/ApiToken/Create?tenantalias=` needs the alias, and
     * asking for it twice is how the two come to disagree.
     */ discover(tenantUrl) {
        return this.client.post("auth/discover", {
            tenantUrl: tenantUrl
        });
    }
    /**
     * Begin pairing: the portal mints an unauthorised token on the tenant and
     * hands back the URL the user must approve it at.
     *
     * ⚠ The raw `apiToken` is NOT in this response and must never be — it stays
     * server-side against the pending enrollment.
     */ startPairing(tenantUrl, methodId) {
        return this.client.post("auth/start", {
            tenantUrl: tenantUrl,
            methodId: methodId
        });
    }
    /**
     * Poll for approval. Resolves once the tenant reports the token authorised
     * and the portal has read the profile behind it.
     */ completePairing(enrollmentId) {
        return this.client.post("auth/complete", {
            enrollmentId: enrollmentId
        });
    }
    /**
     * Sign out of the ACTIVE installation only.
     *
     * Revokes the token on that tenant and deletes its device record; any other installation this
     * device is paired with is untouched. `remaining` says how many are left, so the app can put
     * the person on one of them rather than on an address field they have already filled in.
     */ signOut() {
        return this.client.post("auth/logout");
    }
    /**
     * Switch to an installation this device is ALREADY paired with.
     *
     * ⚠ No tenant call and no re-approval — the pairing lives in the device cookie and this only
     * decides which of them is active. A 404 means the pairing is gone (or was never made on this
     * device), and the caller answers that by pairing rather than by showing a failure.
     */ switchTenant(tenantUrl) {
        return this.client.post("auth/switch", {
            tenantUrl: tenantUrl
        });
    }
    // ── session ──────────────────────────────────────────────────────────────
    me() {
        return this.client.get("me");
    }
    // ── todos ────────────────────────────────────────────────────────────────
    /**
     * Both relations in one call.
     *
     * Deliberately not one request per relation: the home screen needs both
     * counts before it can draw, and two round trips on a phone's first paint is
     * two chances to be slow.
     */ async todos() {
        const payload = await this.client.get("todos");
        return payload.map($d2bd7d9b8ec176dc$export$cafef7527b6deb0c);
    }
    /** Every TODO this person may see — the list's "All TODOs". Rows are `partial`. */ async allTodos() {
        const payload = await this.client.get("todos/all");
        return payload.map($d2bd7d9b8ec176dc$export$cafef7527b6deb0c);
    }
    /**
     * Make one of a TODO's link slots hold exactly these targets — `owner` with one person is a
     * hand-over. A person's type is the server's business; an object slot (later) sends it.
     */ async setLinks(id, slot, targets) {
        return $d2bd7d9b8ec176dc$export$cafef7527b6deb0c(await this.client.put(`todos/${encodeURIComponent(id)}/links/${slot}`, {
            targets: targets
        }));
    }
    /** Delete a TODO — the server refuses unless PQFORCE grants this person DELETE on it. */ async deleteTodo(id) {
        await this.client.request("DELETE", `todos/${encodeURIComponent(id)}`);
    }
    /** One TODO, whole — with what this person may do to it (`permissions`), read when it is opened. */ async todo(id) {
        return $d2bd7d9b8ec176dc$export$cafef7527b6deb0c(await this.client.get(`todos/${encodeURIComponent(id)}`));
    }
    /**
     * Fire a lifecycle transition.
     *
     * Returns the todo as the tenant now holds it — including its NEW transition
     * set, which only the server can know. Recomputing that locally is exactly
     * the mistake `types.ts` warns about.
     */ async fireTransition(todoId, transitionId, note) {
        const payload = await this.client.post(`todos/${encodeURIComponent(todoId)}/transition`, {
            transitionId: transitionId,
            note: note
        });
        return $d2bd7d9b8ec176dc$export$cafef7527b6deb0c(payload);
    }
    async comment(todoId, text) {
        const payload = await this.client.post(`todos/${encodeURIComponent(todoId)}/comment`, {
            text: text
        });
        return $d2bd7d9b8ec176dc$export$cafef7527b6deb0c(payload);
    }
    /**
     * Add ONE assignee. The others are left as they are — a TODO may have several.
     * Adding somebody already assigned changes nothing and still answers with the TODO.
     */ async assign(todoId, personId) {
        const payload = await this.client.post(`todos/${encodeURIComponent(todoId)}/assign`, {
            personId: personId
        });
        return $d2bd7d9b8ec176dc$export$cafef7527b6deb0c(payload);
    }
    /** Remove ONE assignee. Removing somebody who is not assigned changes nothing. */ async unassign(todoId, personId) {
        const payload = await this.client.post(`todos/${encodeURIComponent(todoId)}/unassign`, {
            personId: personId
        });
        return $d2bd7d9b8ec176dc$export$cafef7527b6deb0c(payload);
    }
    /**
     * Change or clear a TODO's due date.
     *
     * ⚠ Owner only, and the server says so — this is a date that makes something overdue for
     * somebody else. `""` clears it; the route reads an empty string as a deliberate absence.
     */ async setDue(todoId, dueISO) {
        const payload = await this.client.post(`todos/${encodeURIComponent(todoId)}/due`, {
            dueISO: dueISO
        });
        return $d2bd7d9b8ec176dc$export$cafef7527b6deb0c(payload);
    }
    /**
     * Change or clear a TODO's priority — its `marker`. Owner only, and the server says so.
     *
     * `""` clears it: most TODOs carry none, so "none" is a value an owner must be able to set. The
     * server answers with the TODO as the tenant now holds it, and refuses when the change did not
     * take — so what comes back is always the truth, not an echo of the request.
     */ async setPriority(todoId, priority) {
        const payload = await this.client.post(`todos/${encodeURIComponent(todoId)}/priority`, {
            priority: priority
        });
        return $d2bd7d9b8ec176dc$export$cafef7527b6deb0c(payload);
    }
    /**
     * Rename a TODO and rewrite its description. Owner only, and the server says so.
     *
     * ⚠ `description` is sent only when it is being changed — a key the route does not receive is a
     * field it does not touch, so renaming cannot wipe a description. The server refuses outright
     * when the stored value carries formatting a textarea would flatten.
     */ async setDetails(todoId, title, description) {
        const payload = await this.client.post(`todos/${encodeURIComponent(todoId)}/details`, description === undefined ? {
            title: title
        } : {
            title: title,
            description: description
        });
        return $d2bd7d9b8ec176dc$export$cafef7527b6deb0c(payload);
    }
    /**
     * Remind every assignee but the caller, by EMAIL. Writes nothing to the lifecycle.
     *
     * Answers with the resource ids actually emailed and, per person skipped, why — so the toast names
     * both rather than claiming everybody was reminded. An older server answers only `addressees`.
     */ async nudge(todoId) {
        const output = await this.client.post(`todos/${encodeURIComponent(todoId)}/nudge`);
        const ids = output?.addressees;
        const skipped = Array.isArray(output?.skipped) ? output.skipped : [];
        const REASONS = [
            "optedOut",
            "noAddress",
            "noAccount",
            "failed"
        ];
        return {
            emailed: Array.isArray(ids) ? ids.filter((id)=>typeof id === "string") : [],
            skipped: skipped.flatMap((entry)=>{
                const e = entry;
                return typeof e?.resourceId === "string" && REASONS.includes(e.reason) ? [
                    {
                        resourceId: e.resourceId,
                        reason: e.reason
                    }
                ] : [];
            })
        };
    }
    async create(input) {
        const payload = await this.client.post("todos", input);
        return $d2bd7d9b8ec176dc$export$cafef7527b6deb0c(payload);
    }
    /** What the New TODO sheet may offer. Read once per session, then cached by the store. */ createOptions() {
        return this.client.get("todos/options");
    }
}




// ─────────────────────────────────────────────────────────────────────────────
// PQFORCE Mobile — onboarding logic. Pure: no DOM, no network.
//
// Four steps, one per screen, "because each can fail differently": tenant URL →
// discovery → sign-in → token. The whole flow runs in the browser tab, and the
// install prompt is deliberately withheld until there is a valid session, so
// nobody installs an app they cannot get into.
// ─────────────────────────────────────────────────────────────────────────────

function $ea7cc6cc52c8cad1$export$da45a345dadb2e43(input) {
    return input.trim().replace(/^https?:\/\//i, "").replace(/\/.*$/, "").toLowerCase();
}
function $ea7cc6cc52c8cad1$export$3ebeddf6921c9442(host) {
    return /^[a-z0-9.-]+\.[a-z]{2,}$/i.test(host);
}
function $ea7cc6cc52c8cad1$export$bf294736920fd217() {
    return (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInHostInvalid", "That does not look like a PQFORCE address. It should be a full host, like demo.pqforce.com.");
}
function $ea7cc6cc52c8cad1$export$246988ca1430edd(host) {
    const first = host.split(".")[0] ?? "";
    return first ? first.charAt(0).toUpperCase() + first.slice(1) : host;
}
/**
 * The four-segment progress rail.
 *
 * Four segments, seven states: the transient states sit with the step they
 * belong to, so the rail never flickers backwards while a request is in flight.
 * `failed` holds the rail where discovery left it — the user has not advanced.
 */ const $ea7cc6cc52c8cad1$var$RAIL_POSITION = {
    url: 0,
    discovering: 0,
    failed: 0,
    options: 1,
    login: 2,
    sso: 2,
    validating: 3,
    done: 3
};
function $ea7cc6cc52c8cad1$export$86f9d0ab9b6b686c(step) {
    const position = $ea7cc6cc52c8cad1$var$RAIL_POSITION[step] ?? 0;
    return [
        0,
        1,
        2,
        3
    ].map((i)=>i <= position);
}
function $ea7cc6cc52c8cad1$export$b88b3f903c831442(methods) {
    if (methods.length === 0) return "failed";
    if (methods.length === 1) return $ea7cc6cc52c8cad1$export$61ff9a91c9b962ca(methods[0]);
    return "options";
}
function $ea7cc6cc52c8cad1$export$61ff9a91c9b962ca(method) {
    return method.acceptsCredentials === true ? "login" : "sso";
}
function $ea7cc6cc52c8cad1$export$ec3ad3f87e3e5eca(count) {
    if (count === 1) return (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInMethodCountOne", "One sign-in method available.");
    // English spells small counts out; German gets the figure, since a spelled-out English word
    // would land in the German sentence.
    const words = [
        "No",
        "One",
        "Two",
        "Three",
        "Four",
        "Five",
        "Six"
    ];
    const word = (0, $498477530f916680$export$324a75438e87a537)() === "en" ? words[count] ?? String(count) : String(count);
    return (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInMethodCountMany", "{count} sign-in methods available.", {
        count: word
    });
}
function $ea7cc6cc52c8cad1$export$3da35ff9cbe22ecd(step) {
    return step === "login" || step === "sso" ? "options" : "url";
}
function $ea7cc6cc52c8cad1$export$b836dcfe272671b(host) {
    return [
        (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInStepRequestToken", "Requesting a bearer token"),
        (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInStepValidate", "Validating it against {host}", {
            host: host
        }),
        (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInStepProfile", "Loading your profile and projects")
    ];
}
function $ea7cc6cc52c8cad1$export$f65cf35775105606(current, failedAt = null) {
    return [
        0,
        1,
        2
    ].map((i)=>{
        if (failedAt !== null && i === failedAt) return "failed";
        if (failedAt !== null && i > failedAt) return "pending";
        if (i < current) return "done";
        if (i === current) return "active";
        return "pending";
    });
}
function $ea7cc6cc52c8cad1$export$74bc47fd0c9e3311(status) {
    switch(status){
        case 403:
            return (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInFailNotEnabled", "Not switched on for this tenant");
        case 404:
            return (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInFailUnknownAddress", "We do not know that address");
        case 503:
            return (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInFailNotReady", "The mobile service is not ready yet");
        default:
            return (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInFailNoAnswer", "That tenant did not answer");
    }
}
function $ea7cc6cc52c8cad1$export$5331fe171634f115(host) {
    return (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInDiscoveryFailed", "We reached {host} but it did not answer with login options. It may be an older version, or the mobile endpoint may be switched off. Your administrator can enable it.", {
        host: host
    });
}


// ─────────────────────────────────────────────────────────────────────────────
// PQFORCE Mobile — the state container. Pure: no DOM, no network.
//
// A tiny observable store rather than a framework. The app has one screen stack
// and about twenty fields; the portal ships no state library, and adding one for
// this would be a dependency a phone downloads to hold a discriminated union.
//
// The UI subscribes, re-renders from a snapshot, and never mutates: every writer
// goes through `update()`, so there is one place a change can be traced to.
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// PQFORCE Mobile — light or dark.
//
// A reading preference for this device, chosen in the account sheet and kept in
// localStorage beside the folded list groups. DOM-free: `ui_mobile_appearance.ts`
// applies the answer to the page.
// ─────────────────────────────────────────────────────────────────────────────

const $ca2b09cd067c2e8d$export$9aa713c49cf52433 = [
    "light",
    "dark",
    "system"
];
const $ca2b09cd067c2e8d$export$d6830277b06edd6 = "light";
function $ca2b09cd067c2e8d$export$2273adc37b58ffd0(appearance) {
    switch(appearance){
        case "dark":
            return (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetAppearanceDark", "Dark");
        case "system":
            return (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetAppearanceSystem", "Match device");
        default:
            return (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetAppearanceLight", "Light");
    }
}
function $ca2b09cd067c2e8d$export$f50c4d358998cba5(raw) {
    return $ca2b09cd067c2e8d$export$9aa713c49cf52433.includes(raw) ? raw : $ca2b09cd067c2e8d$export$d6830277b06edd6;
}
function $ca2b09cd067c2e8d$export$12cea13604c831a8(appearance, deviceIsDark) {
    if (appearance === "system") return deviceIsDark ? "dark" : "light";
    return appearance;
}



const $34dfe091e21ab98b$export$cdbc538da098d018 = {
    title: "",
    projectId: "",
    assigneeId: "",
    /** `YYYY-MM-DD`, or `""` for no due date. Absence is a legitimate value, not a missing answer. */ dueISO: "",
    priority: ""
};
function $34dfe091e21ab98b$export$d4c72bab9d6cc13a() {
    return {
        ready: false,
        signedIn: false,
        profile: null,
        onboarding: "url",
        tenantInput: "",
        tenantError: "",
        discovery: null,
        enrollmentId: null,
        authorizationUrl: null,
        authorizationDeadline: null,
        tokenStep: 0,
        tokenFailedAt: null,
        onboardingError: "",
        approvalOpened: null,
        onboardingErrorStatus: null,
        knownTenants: [],
        sessionExpired: false,
        addingTenant: false,
        lastMethodId: null,
        tab: "home",
        todoView: "list",
        relation: "assignee",
        allTodos: null,
        allLoading: false,
        allError: "",
        grouping: "due",
        collapsedGroups: [],
        appearance: (0, $ca2b09cd067c2e8d$export$d6830277b06edd6),
        languageChoice: "auto",
        selectedId: null,
        todos: [],
        loading: false,
        loadError: "",
        sheet: null,
        newTodoProjectId: null,
        searchOpen: false,
        pairingFromApp: false,
        busyLabel: null,
        todoLoading: null,
        transitionDraft: null,
        commentDraft: "",
        commentBusy: false,
        assigneeBusy: null,
        priorityBusy: false,
        priorityPick: null,
        ownerPick: null,
        newTodo: {
            ...$34dfe091e21ab98b$export$cdbc538da098d018
        },
        createOptions: null,
        toast: null
    };
}
class $34dfe091e21ab98b$export$3ba7ded393c43e1e {
    getState() {
        return this.state;
    }
    /**
     * Shallow-merge a patch and notify. The only writer.
     *
     * Every write of `todos` passes through `withKnownStateIcons()` — here rather than at the half
     * dozen call sites, so the list load, the re-read after a change and a created todo cannot differ
     * about which icon a state wears. The rows being replaced are a source too.
     */ update(patch) {
        // …and through `withKnownPermissions()`: a list never carries what this person may do, and a
        // reload must not take back what an opened TODO already learned.
        const known = [
            ...this.state.todos,
            ...this.state.allTodos ?? []
        ];
        if (patch.todos) patch = {
            ...patch,
            todos: (0, $0a55aae61f0d5dc0$export$7ba8ca721a3ec0a6)((0, $0a55aae61f0d5dc0$export$1dcbd4d406e534bd)(patch.todos, this.state.todos), known)
        };
        if (patch.allTodos) patch = {
            ...patch,
            allTodos: (0, $0a55aae61f0d5dc0$export$7ba8ca721a3ec0a6)((0, $0a55aae61f0d5dc0$export$1dcbd4d406e534bd)(patch.allTodos, known), known)
        };
        this.state = {
            ...this.state,
            ...patch
        };
        for (const listener of this.listeners)listener(this.state);
        return this.state;
    }
    subscribe(listener) {
        this.listeners.push(listener);
        return ()=>{
            this.listeners = this.listeners.filter((l)=>l !== listener);
        };
    }
    /** The currently selected todo, or null — this person's own copy first, the "All" one else. */ selected() {
        const { todos: todos, allTodos: allTodos, selectedId: selectedId } = this.state;
        return todos.find((t)=>t.id === selectedId) ?? allTodos?.find((t)=>t.id === selectedId) ?? null;
    }
    /** Replace one todo in place, keeping list order. */ /**
     * Replace one todo in place, keeping list order — in BOTH lists, since a TODO met in "All" may
     * also be one this person holds.
     */ replaceTodo(todo) {
        const swap = (list)=>list.map((t)=>t.id === todo.id ? todo : t);
        return this.update({
            todos: swap(this.state.todos),
            ...this.state.allTodos ? {
                allTodos: swap(this.state.allTodos)
            } : {}
        });
    }
    /** Take one todo out of BOTH lists — after it was deleted. */ removeTodo(id) {
        const drop = (list)=>list.filter((t)=>t.id !== id);
        return this.update({
            todos: drop(this.state.todos),
            ...this.state.allTodos ? {
                allTodos: drop(this.state.allTodos)
            } : {}
        });
    }
    constructor(){
        this.state = $34dfe091e21ab98b$export$d4c72bab9d6cc13a();
        this.listeners = [];
    }
}
const $34dfe091e21ab98b$export$e71ef44e153d57d5 = 2600;
const $34dfe091e21ab98b$export$2cf9854ae498407c = 2000;
const $34dfe091e21ab98b$export$bdbb6556783384c0 = 300000;


// ─────────────────────────────────────────────────────────────────────────────
// PQFORCE Mobile — the installations this device knows about. Pure.
//
// ⚠ MULTI-TENANT IS SUPPORTED, and this is why the remembered address is a LIST
// rather than a single string. Real people work across installations — the
// design's own example has one person on "Helvetia Group · SSO" and "ANDRITZ
// Soutec · email login" — and a single remembered address would silently
// overwrite one with the other on every switch.
//
// ⚠ NOTHING HERE IS A CREDENTIAL. A host, a display name and which sign-in
// method was chosen last time. The bearer token never leaves the portal, and the
// device credential is an httpOnly cookie this code cannot read. That is what
// makes it safe to keep this list in localStorage at all.
// ─────────────────────────────────────────────────────────────────────────────
function $9a394e9e7ce3770e$export$144a76485d039242(list, tenant) {
    const rest = list.filter((t)=>t.host !== tenant.host);
    const previous = list.find((t)=>t.host === tenant.host);
    // Merge, so a re-pair that learns nothing new does not erase what we knew.
    const merged = {
        ...previous,
        ...tenant,
        displayName: tenant.displayName ?? previous?.displayName,
        methodId: tenant.methodId ?? previous?.methodId,
        methodTitle: tenant.methodTitle ?? previous?.methodTitle
    };
    return $9a394e9e7ce3770e$export$403da18ad08ec726([
        ...rest,
        merged
    ]);
}
function $9a394e9e7ce3770e$export$403da18ad08ec726(list) {
    return [
        ...list
    ].sort((a, b)=>b.lastUsed - a.lastUsed);
}
function $9a394e9e7ce3770e$export$3cd89ddcdf90b64f(list) {
    return $9a394e9e7ce3770e$export$403da18ad08ec726(list)[0] ?? null;
}
function $9a394e9e7ce3770e$export$a2ecad0d59d96472(list, host) {
    return list.filter((t)=>t.host !== host);
}
function $9a394e9e7ce3770e$export$bdd7edb13ed615d(raw) {
    if (!raw) return [];
    // The pre-list format: a bare address string.
    //
    // ⚠ It must LOOK like a host before it is read as one. Anything else stored
    // under this key — a JSON object from a future format, or plain corruption —
    // would otherwise become an "installation" whose host is that text, and the
    // returning screen would offer it as somewhere to sign in.
    if (!raw.startsWith("[")) {
        const url = raw.trim();
        const host = url.replace(/^https?:\/\//i, "").replace(/\/.*$/, "").toLowerCase();
        if (!/^[a-z0-9.-]+\.[a-z]{2,}$/i.test(host)) return [];
        return [
            {
                url: url,
                host: host,
                lastUsed: 0
            }
        ];
    }
    try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return $9a394e9e7ce3770e$export$403da18ad08ec726(parsed.filter((t)=>!!t && typeof t === "object" && typeof t.url === "string" && typeof t.host === "string").map((t)=>({
                ...t,
                lastUsed: typeof t.lastUsed === "number" ? t.lastUsed : 0
            })));
    } catch  {
        return [];
    }
}
function $9a394e9e7ce3770e$export$7be77b29b66ac800(tenant) {
    const parts = [
        tenant.displayName,
        tenant.methodTitle
    ].filter(Boolean);
    return parts.length ? parts.join(" \xb7 ") : tenant.host;
}




// ─────────────────────────────────────────────────────────────────────────────
// PQFORCE Mobile — which screen to come back to after a reload. Pure: no DOM, no storage.
//
// A reload — pulling down, the phone discarding a background tab, an update of the app — used to
// start on Home whatever was on screen. The shell remembers the VIEW here (tab, list or TODO, the
// list's segment and grouping, the scroll offset) in `sessionStorage` and hands it back on the next
// start of the same tab. Sheets and the search card are not views: they are half-finished actions,
// and reopening one unasked would be its own surprise.
//
// ⚠ Keyed by the INSTALLATION. A TODO id means nothing on another installation, and a phone paired
// with two must not open the other one's list on a TODO that does not exist there.
// ─────────────────────────────────────────────────────────────────────────────
const $ef9823cca3288d87$var$GROUPINGS = [
    "due",
    "status",
    "priority",
    "project"
];
function $ef9823cca3288d87$export$3c0dd642b9a3c1cb(profile) {
    return profile?.tenantUrl || profile?.tenantHost || "";
}
function $ef9823cca3288d87$export$bc820e57bf78fba(state, scrollY) {
    const tenant = $ef9823cca3288d87$export$3c0dd642b9a3c1cb(state.profile);
    if (!state.signedIn || !tenant) return null;
    const detail = state.tab === "todos" && state.todoView === "detail" && Boolean(state.selectedId);
    return {
        tenant: tenant,
        tab: state.tab,
        todoView: detail ? "detail" : "list",
        selectedId: detail ? state.selectedId : null,
        relation: state.relation,
        grouping: state.grouping,
        scrollY: Math.max(0, Math.round(scrollY))
    };
}
function $ef9823cca3288d87$export$9268357d5d664eb6(raw, tenant) {
    if (!raw || typeof raw !== "object" || !tenant) return null;
    const saved = raw;
    if (saved.tenant !== tenant) return null;
    const tab = saved.tab === "todos" ? "todos" : "home";
    const selectedId = typeof saved.selectedId === "string" && saved.selectedId ? saved.selectedId : null;
    const detail = tab === "todos" && saved.todoView === "detail" && selectedId !== null;
    const patch = {
        tab: tab,
        todoView: detail ? "detail" : "list"
    };
    if (detail) patch.selectedId = selectedId;
    if (saved.relation === "assignee" || saved.relation === "owner" || saved.relation === "all") patch.relation = saved.relation;
    if ($ef9823cca3288d87$var$GROUPINGS.includes(saved.grouping)) patch.grouping = saved.grouping;
    const scrollY = typeof saved.scrollY === "number" && Number.isFinite(saved.scrollY) ? Math.max(0, saved.scrollY) : 0;
    return {
        patch: patch,
        scrollY: scrollY
    };
}


// ─────────────────────────────────────────────────────────────────────────────
// MobileClient — the mobile app's HTTP client. Data layer: a class, no DOM.
//
// The mobile app holds TWO credentials, and the split is the security design:
//
//   • a long-lived DEVICE credential in an httpOnly, Secure, SameSite=Strict
//     cookie scoped by `Path` to the refresh route alone. JavaScript cannot read
//     it, so XSS cannot steal it; and because every data route below is
//     bearer-authenticated rather than cookie-authenticated, those routes have
//     no CSRF surface at all.
//
//   • a short-lived ACCESS TOKEN held ONLY in memory on this instance. Never
//     `localStorage` — see the header of `ui_mobile_shell.ts`.
//
// ⚠ NEITHER IS THE PQFORCE BEARER TOKEN. That one never leaves the portal: it
// is sealed at rest in the device record and unsealed per request, server-side.
// If a future change puts it in a response body, the whole model is gone.
// ─────────────────────────────────────────────────────────────────────────────
class $373193550cbe2f46$export$f717aa8b913cec23 extends Error {
    constructor(message, /** The HTTP status, or 0 when the request never got an answer. */ statusCode, /** The parsed body, when there was one. */ body = null){
        super(message), this.statusCode = statusCode, this.body = body;
        this.name = "MobileApiError";
    }
}
class $373193550cbe2f46$export$d68e436226202e2 {
    constructor(options){
        this.options = options;
        this.accessToken = null;
        this.refreshing = null;
    }
    setAccessToken(token) {
        this.accessToken = token;
    }
    hasSession() {
        return this.accessToken !== null;
    }
    /**
     * Exchange the device cookie for a fresh access token.
     *
     * Sends no Authorization header on purpose — this is the one route the
     * cookie authenticates, and presenting a stale bearer alongside it would let
     * a server-side ordering bug answer from the wrong credential.
     */ async refreshSession() {
        if (this.refreshing) return this.refreshing;
        this.refreshing = (async ()=>{
            try {
                const response = await fetch(this.url("auth/session"), {
                    method: "POST",
                    credentials: "same-origin",
                    headers: this.baseHeaders()
                });
                if (!response.ok) return false;
                const body = await response.json();
                const token = body?.output?.accessToken;
                if (!token) return false;
                this.accessToken = token;
                return true;
            } catch  {
                // A network failure is not proof the session is gone — the caller
                // distinguishes the two, because dropping a valid session every
                // time a train enters a tunnel would be its own bug.
                return false;
            } finally{
                this.refreshing = null;
            }
        })();
        return this.refreshing;
    }
    /**
     * One request, with a single refresh-and-retry on 401.
     *
     * ⚠ Exactly one retry. A refresh that yields a token the route still refuses
     * means the session is genuinely finished; looping would hammer the endpoint
     * and never terminate.
     */ async request(method, path, body) {
        let response = await this.send(method, path, body);
        if (response.status === 401 && await this.refreshSession()) response = await this.send(method, path, body);
        if (response.status === 401) {
            this.accessToken = null;
            this.options.onSessionLost?.();
            throw new $373193550cbe2f46$export$f717aa8b913cec23("Session expired", 401);
        }
        const parsed = await this.parse(response);
        if (!response.ok) {
            // `result` is the message the USER SEES — the portal's own convention
            // — so it is preferred over anything invented here.
            const message = parsed?.result || `Request failed (${response.status})`;
            throw new $373193550cbe2f46$export$f717aa8b913cec23(message, response.status, parsed);
        }
        return parsed.output;
    }
    get(path) {
        return this.request("GET", path);
    }
    post(path, body) {
        return this.request("POST", path, body);
    }
    put(path, body) {
        return this.request("PUT", path, body);
    }
    baseHeaders() {
        const headers = {
            Accept: "application/json",
            "X-Pqf-Request": "1"
        };
        const language = this.options.language?.() ?? null;
        if (language) headers["X-Pqf-Language"] = language;
        return headers;
    }
    async send(method, path, body) {
        // X-Pqf-Request: the mobile router refuses a cookie-carrying POST without it. Every
        // *.pqforce.com host is the same site, so SameSite=Strict alone does not stop another one
        // from sending such a POST; a custom header does (request guard).
        const headers = this.baseHeaders();
        if (this.accessToken) headers.Authorization = `Bearer ${this.accessToken}`;
        if (body !== undefined) headers["Content-Type"] = "application/json";
        try {
            return await fetch(this.url(path), {
                method: method,
                credentials: "same-origin",
                headers: headers,
                body: body === undefined ? undefined : JSON.stringify(body)
            });
        } catch (error) {
            throw new $373193550cbe2f46$export$f717aa8b913cec23(error instanceof Error ? error.message : "Network error", 0);
        }
    }
    async parse(response) {
        const text = await response.text();
        if (!text) return null;
        try {
            return JSON.parse(text);
        } catch  {
            // A proxy or a WAF answering HTML is a real case — see the Barracuda
            // in front of the tenants. Keep the text so it reaches the log.
            return {
                result: text.slice(0, 200),
                output: null
            };
        }
    }
    url(path) {
        const base = this.options.baseUrl.endsWith("/") ? this.options.baseUrl : `${this.options.baseUrl}/`;
        return base + path.replace(/^\//, "");
    }
}


// ─────────────────────────────────────────────────────────────────────────────
// Minimal DOM helpers for the mobile app.
//
// ⚠ DELIBERATE DEVIATION FROM THE PORTAL'S UI CONVENTION, stated here so it is
// a decision rather than an oversight.
//
// CLAUDE.md's 3-layer rules say event wiring uses jQuery `.on()` closures. That
// convention exists for the portal's pages, which already load jQuery and UIkit
// for everything else they do. This bundle is served to a phone from a different
// host, with its own stylesheet, and shipping ~120 KB of desktop framework to
// render two screens would contradict the same reasoning that keeps
// `default.scss` off this app.
//
// What IS kept, because it is what the rule protects:
//   • no `window` globals for dispatch,
//   • no `onclick`/`onchange` attributes in markup,
//   • every listener registered inside a module's `initUI*` function, in a
//     closure over its own state.
//
// ⚠ TEXT IS SET WITH textContent, NEVER innerHTML. Todo titles, comments,
// project names and people's names come out of a CUSTOMER'S OWN TENANT — they
// are user-authored strings from a system this app does not control. `html()`
// below exists only for our own icon constants and says so.
// ─────────────────────────────────────────────────────────────────────────────
function $afadd878e68b56c8$export$c3da0dad1b44eea9(tag, attributes = {}, children = []) {
    const node = document.createElement(tag);
    for (const [key, value] of Object.entries(attributes)){
        if (value === null || value === undefined || value === false) continue;
        if (key === "class") node.className = String(value);
        else if (key === "text") node.textContent = String(value);
        else if (key === "style") node.setAttribute("style", String(value));
        else if (value === true) node.setAttribute(key, "");
        else node.setAttribute(key, String(value));
    }
    const list = typeof children === "string" ? [
        children
    ] : children;
    for (const child of list)node.appendChild(typeof child === "string" ? document.createTextNode(child) : child);
    return node;
}
function $afadd878e68b56c8$export$98e05ab619d63cba(markup, className) {
    const span = document.createElement("span");
    if (className) span.className = className;
    span.innerHTML = markup;
    span.style.display = "inline-flex";
    return span;
}
/**
 * Tags a lifecycle remark may carry, and the attributes each may keep.
 *
 * ⚠ THIS IS THE SECOND OF TWO PASSES. `pqfMobileRemarkHtml()` on the server is the boundary and
 * this list is its mirror; the scrub is repeated here because a guarantee that lives only at the
 * other end of a wire is not one this module can state, and this module is the one calling
 * `innerHTML`. Keep the two lists in step — a tag the server allows and this drops is formatting
 * that silently disappears; the reverse is a hole.
 */ const $afadd878e68b56c8$var$RICH_TEXT_TAGS = {
    P: [
        "style"
    ],
    DIV: [
        "style"
    ],
    BR: [],
    B: [
        "style"
    ],
    STRONG: [
        "style"
    ],
    I: [
        "style"
    ],
    EM: [
        "style"
    ],
    U: [
        "style"
    ],
    S: [
        "style"
    ],
    STRIKE: [
        "style"
    ],
    SPAN: [
        "style"
    ],
    UL: [
        "style"
    ],
    OL: [
        "style"
    ],
    LI: [
        "style"
    ],
    BLOCKQUOTE: [
        "style"
    ],
    CODE: [
        "style"
    ],
    PRE: [
        "style"
    ],
    A: [
        "href",
        "target",
        "rel",
        "style"
    ]
};
/**
 * The `style` properties a comment may carry — an ALLOW-LIST, never a deny-list.
 *
 * Interpreting the HTML means interpreting its styling; `span` exists in the tag list for no other
 * reason. What makes that safe is this list rather than the tags: `style` is a full CSS surface,
 * and `position: fixed` with a `z-index` turns a comment into an overlay over the whole app.
 * Naming the handful of properties that describe TEXT means nothing else can arrive, including a
 * property invented after this was written. Mirrors `pqfMobileInlineStyle()`.
 */ const $afadd878e68b56c8$var$RICH_TEXT_STYLE_PROPERTIES = [
    "color",
    "background-color",
    "font-size",
    "font-weight",
    "font-style",
    "text-decoration",
    "text-decoration-line",
    "text-align"
];
/** `url(` reaches the network, and the rest are ways a value has smuggled something past before. */ const $afadd878e68b56c8$var$RICH_TEXT_STYLE_UNSAFE = /url\s*\(|expression\s*\(|@import|[\\<>{}]|\/\*|!important/i;
/** Keep the declarations that describe text, drop the rest. Empty means: remove the attribute. */ function $afadd878e68b56c8$var$scrubInlineStyle(style) {
    const kept = [];
    for (const declaration of style.split(";")){
        const at = declaration.indexOf(":");
        if (at < 0) continue;
        const property = declaration.slice(0, at).trim().toLowerCase();
        const value = declaration.slice(at + 1).trim();
        if (!$afadd878e68b56c8$var$RICH_TEXT_STYLE_PROPERTIES.includes(property)) continue;
        if (!value || value.length > 64) continue;
        if ($afadd878e68b56c8$var$RICH_TEXT_STYLE_UNSAFE.test(value)) continue;
        kept.push(`${property}: ${value}`);
    }
    return kept.join("; ");
}
/** Removed WHOLE, never unwrapped: unwrapping a script puts its source on screen as prose. */ const $afadd878e68b56c8$var$RICH_TEXT_DROP = new Set([
    "SCRIPT",
    "STYLE",
    "IFRAME",
    "OBJECT",
    "EMBED",
    "TEMPLATE",
    "LINK",
    "META"
]);
function $afadd878e68b56c8$export$b563501506ada1a0(html, className) {
    const span = document.createElement("span");
    if (className) span.className = className;
    if (!html) return span;
    const parsed = new DOMParser().parseFromString(`<body>${html}</body>`, "text/html");
    $afadd878e68b56c8$var$scrubRichText(parsed.body);
    while(parsed.body.firstChild)span.appendChild(parsed.body.firstChild);
    return span;
}
/** Depth-first: drop what carries nothing, unwrap what is not allowed, strip stray attributes. */ function $afadd878e68b56c8$var$scrubRichText(node) {
    // A snapshot, because removing a child mutates a live child list underneath the loop — which
    // is how a scrubber comes to skip every second node, and the skipped ones are its whole point.
    for (const child of Array.from(node.children)){
        if ($afadd878e68b56c8$var$RICH_TEXT_DROP.has(child.tagName)) {
            child.remove();
            continue;
        }
        $afadd878e68b56c8$var$scrubRichText(child);
        const keep = $afadd878e68b56c8$var$RICH_TEXT_TAGS[child.tagName];
        if (!keep) {
            // Unwrap, never delete: the text inside an unknown wrapper is somebody's sentence.
            child.replaceWith(...Array.from(child.childNodes));
            continue;
        }
        for (const name of Array.from(child.getAttributeNames()))if (!keep.includes(name.toLowerCase())) child.removeAttribute(name);
        const style = child.getAttribute("style");
        if (style !== null) {
            const kept = $afadd878e68b56c8$var$scrubInlineStyle(style);
            if (kept) child.setAttribute("style", kept);
            else child.removeAttribute("style");
        }
        // A SPAN is in the tag list ONLY to carry styling; one left with none is unwrapped, so
        // the tree never grows an element that expresses nothing.
        if (child.tagName === "SPAN" && child.attributes.length === 0) {
            child.replaceWith(...Array.from(child.childNodes));
            continue;
        }
        if (child.tagName === "A") {
            const href = (child.getAttribute("href") ?? "").trim();
            // An explicit http/https/mailto scheme and nothing else — which is what refuses
            // `javascript:`, `data:` and the protocol-relative `//evil.com` alike.
            if (!/^(https?|mailto):/i.test(href)) child.removeAttribute("href");
            else {
                child.setAttribute("target", "_blank");
                child.setAttribute("rel", "noopener noreferrer");
            }
        }
    }
}
function $afadd878e68b56c8$export$42ffd38884aecdac(node) {
    while(node.firstChild)node.removeChild(node.firstChild);
}
function $afadd878e68b56c8$export$9563e054e6f787fb(node, children) {
    $afadd878e68b56c8$export$42ffd38884aecdac(node);
    for (const child of children)node.appendChild(typeof child === "string" ? document.createTextNode(child) : child);
}
function $afadd878e68b56c8$export$af631764ddc44097(root, event, selector, handler) {
    const listener = (raw)=>{
        const target = raw.target;
        const match = target?.closest(selector);
        if (match && root.contains(match)) handler(match, raw);
    };
    root.addEventListener(event, listener);
    return ()=>root.removeEventListener(event, listener);
}
function $afadd878e68b56c8$export$2385a24977818dd0(node, event, handler) {
    node.addEventListener(event, handler);
    return ()=>node.removeEventListener(event, handler);
}


// ─────────────────────────────────────────────────────────────────────────────
// Phosphor icons, inline.
//
// Every path below is transcribed from `PQFORCE Mobile.dc.html`. Inline SVG on
// `currentColor` (or an explicit ramp colour) — no icon font, no sprite sheet,
// no request. The handoff's asset list is "None. All icons are inline Phosphor
// paths."
//
// These are the ONLY trusted markup constants in the app; see `trustedSvg()`.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A colour for an SVG attribute, and the one for the wrapping span.
 *
 * ⚠ A THEME TOKEN (`var(--color-ink-5)`) CANNOT GO INTO `fill=`/`stroke=`: presentation attributes
 * do not reliably resolve custom properties. So a token becomes `currentColor` in the markup and
 * the span's `color` — which is how the icons follow light and dark mode. A literal (the white
 * tick, the logo) goes straight into the attribute as before.
 */ function $62a79e86ee9eca2d$var$paintOf(color) {
    return color.startsWith("var(") ? {
        attr: "currentColor",
        span: color
    } : {
        attr: color,
        span: null
    };
}
/** `trustedSvg`, with the span coloured when the paint was a token. */ function $62a79e86ee9eca2d$var$painted(markup, span) {
    const node = (0, $afadd878e68b56c8$export$98e05ab619d63cba)(markup);
    if (span) node.style.color = span;
    return node;
}
const $62a79e86ee9eca2d$var$PHOSPHOR = (size, path, fill = "currentColor")=>`<svg width="${size}" height="${size}" viewBox="0 0 256 256" fill="${fill}" aria-hidden="true">${path}</svg>`;
const $62a79e86ee9eca2d$var$PATHS = {
    house: '<path d="M219.31 108.68l-80-80a16 16 0 0 0-22.62 0l-80 80A15.87 15.87 0 0 0 32 120v96a8 8 0 0 0 8 8h64a8 8 0 0 0 8-8v-56h32v56a8 8 0 0 0 8 8h64a8 8 0 0 0 8-8v-96a15.87 15.87 0 0 0-4.69-11.32Z"></path>',
    checkSquare: '<path d="M216 40H40a16 16 0 0 0-16 16v144a16 16 0 0 0 16 16h176a16 16 0 0 0 16-16V56a16 16 0 0 0-16-16Zm-40.4 62.1-56 56a8 8 0 0 1-11.3 0l-24-24a8 8 0 0 1 11.3-11.3L114 141.7l50.3-50.3a8 8 0 0 1 11.3 11.3Z"></path>',
    magnifier: '<path d="M229.66 218.34l-50.07-50.06a88.11 88.11 0 1 0-11.31 11.31l50.06 50.07a8 8 0 0 0 11.32-11.32ZM40 112a72 72 0 1 1 72 72 72.08 72.08 0 0 1-72-72Z"></path>',
    x: '<path d="M205.66 194.34a8 8 0 0 1-11.32 11.32L128 139.31l-66.34 66.35a8 8 0 0 1-11.32-11.32L116.69 128 50.34 61.66a8 8 0 0 1 11.32-11.32L128 116.69l66.34-66.35a8 8 0 0 1 11.32 11.32L139.31 128Z"></path>',
    // Phosphor `PencilSimple` (fill): change a fact — the title, a link.
    pencil: '<path d="M227.31 73.37L182.63 28.68a16 16 0 0 0-22.63 0L36.69 152A15.86 15.86 0 0 0 32 163.31V208a16 16 0 0 0 16 16h44.69a15.86 15.86 0 0 0 11.31-4.69L227.31 96a16 16 0 0 0 0-22.63ZM192 108.68L147.31 64l24-24L216 84.68Z"></path>',
    // Phosphor `Bell` (fill): remind the assignees.
    bell: '<path d="M221.8 175.94C216.25 166.38 208 139.33 208 104a80 80 0 1 0-160 0c0 35.34-8.26 62.38-13.81 71.94A16 16 0 0 0 48 200h40.81a40 40 0 0 0 78.38 0H208a16 16 0 0 0 13.8-24.06ZM128 216a24 24 0 0 1-22.62-16h45.24A24 24 0 0 1 128 216Z"></path>',
    // The list's three segments, drawn as plain filled shapes on the 256 grid: a person (assigned to
    // me), a crown (I own), a list (all TODOs). Each button also carries its name — see renderList().
    person: '<path d="M128 28a52 52 0 1 1 0 104a52 52 0 1 1 0-104Zm0 120c-58 0-104 34-104 76v12h208v-12c0-42-46-76-104-76Z"></path>',
    crown: '<path d="M24 76l52 52 52-84 52 84 52-52-26 124H50Zm26 136h156v20H50Z"></path>',
    listAll: '<path d="M32 52h32v32H32Zm56 4h136v24H88ZM32 112h32v32H32Zm56 4h136v24H88ZM32 172h32v32H32Zm56 4h136v24H88Z"></path>',
    // Collapse / expand every group: two chevrons pointing in, or out.
    collapseAll: '<path d="M128 120L64 56l20-20 44 44 44-44 20 20Zm0 16l64 64-20 20-44-44-44 44-20-20Z"></path>',
    expandAll: '<path d="M128 28l64 64-20 20-44-44-44 44-20-20Zm0 200l-64-64 20-20 44 44 44-44 20 20Z"></path>',
    xCircle: '<path d="M128 24a104 104 0 1 0 104 104A104.11 104.11 0 0 0 128 24Zm37.66 130.34a8 8 0 0 1-11.32 11.32L128 139.31l-26.34 26.35a8 8 0 0 1-11.32-11.32L116.69 128l-26.35-26.34a8 8 0 0 1 11.32-11.32L128 116.69l26.34-26.35a8 8 0 0 1 11.32 11.32L139.31 128Z"></path>',
    calendarBlank: '<path d="M208 32h-24v-8a8 8 0 0 0-16 0v8H88v-8a8 8 0 0 0-16 0v8H48a16 16 0 0 0-16 16v160a16 16 0 0 0 16 16h160a16 16 0 0 0 16-16V48a16 16 0 0 0-16-16ZM72 48v8a8 8 0 0 0 16 0v-8h80v8a8 8 0 0 0 16 0v-8h24v32H48V48Zm136 160H48V96h160Z"></path>',
    flag: '<path d="M34.76 42A8 8 0 0 0 32 48v168a8 8 0 0 0 16 0v-44.23c26.79-21.16 49.87-9.75 76.45 3.41 16.4 8.11 34.06 16.85 53 16.85 13.93 0 28.54-4.75 43.82-18a8 8 0 0 0 2.76-6V48a8 8 0 0 0-13.27-6c-28 24.23-51.72 12.49-79.21-1.12C111.07 26.76 75.64 9.22 34.76 42ZM208 164.25c-26.79 21.16-49.87 9.74-76.45-3.41-25-12.38-52.33-25.9-83.55-8.4V51.79c26.79-21.16 49.87-9.75 76.45 3.4 25 12.38 52.33 25.91 83.55 8.4Z"></path>',
    trash: '<path d="M216 48h-40v-8a24 24 0 0 0-24-24h-48a24 24 0 0 0-24 24v8H40a8 8 0 0 0 0 16h8v144a16 16 0 0 0 16 16h128a16 16 0 0 0 16-16V64h8a8 8 0 0 0 0-16ZM96 40a8 8 0 0 1 8-8h48a8 8 0 0 1 8 8v8H96Z"></path>',
    plus: '<path d="M224 128a8 8 0 0 1-8 8h-80v80a8 8 0 0 1-16 0v-80H40a8 8 0 0 1 0-16h80V40a8 8 0 0 1 16 0v80h80a8 8 0 0 1 8 8Z"></path>',
    chat: '<path d="M216 48H40a16 16 0 0 0-16 16v160l40-32h152a16 16 0 0 0 16-16V64a16 16 0 0 0-16-16Z"></path>',
    warningCircle: '<path d="M128 24a104 104 0 1 0 104 104A104.11 104.11 0 0 0 128 24Zm-8 56a8 8 0 0 1 16 0v56a8 8 0 0 1-16 0Zm8 104a12 12 0 1 1 12-12 12 12 0 0 1-12 12Z"></path>',
    check: '<path d="M229.66 77.66l-128 128a8 8 0 0 1-11.32 0l-56-56a8 8 0 0 1 11.32-11.32L96 188.69 218.34 66.34a8 8 0 0 1 11.32 11.32Z"></path>',
    lock: '<path d="M208 80h-24V56a56 56 0 0 0-112 0v24H48a16 16 0 0 0-16 16v112a16 16 0 0 0 16 16h160a16 16 0 0 0 16-16V96a16 16 0 0 0-16-16ZM88 56a40 40 0 0 1 80 0v24H88Z"></path>',
    lockKey: '<path d="M208 80h-24V56a56 56 0 0 0-112 0v24H48a16 16 0 0 0-16 16v112a16 16 0 0 0 16 16h160a16 16 0 0 0 16-16V96a16 16 0 0 0-16-16ZM88 56a40 40 0 0 1 80 0v24H88Zm52 104.6V184a12 12 0 0 1-24 0v-23.4a20 20 0 1 1 24 0Z"></path>',
    envelope: '<path d="M230.14 58.87A16 16 0 0 0 216 48H40a16 16 0 0 0-16 16v128a16 16 0 0 0 16 16h176a16 16 0 0 0 16-16V64a16 16 0 0 0-1.86-5.13ZM128 129.09 47.16 64h161.68Z"></path>'
};
function $62a79e86ee9eca2d$export$1ca1ec8b29a4ce27(name, size = 22, fill = "currentColor") {
    const paint = $62a79e86ee9eca2d$var$paintOf(fill);
    return $62a79e86ee9eca2d$var$painted($62a79e86ee9eca2d$var$PHOSPHOR(size, $62a79e86ee9eca2d$var$PATHS[name], paint.attr), paint.span);
}
function $62a79e86ee9eca2d$export$6038895ac52bd044(color = "currentColor") {
    const paint = $62a79e86ee9eca2d$var$paintOf(color);
    return $62a79e86ee9eca2d$var$painted(`<svg width="8" height="14" viewBox="0 0 8 14" aria-hidden="true">` + `<path d="M7 1L1 7l6 6" stroke="${paint.attr}" stroke-width="2" fill="none" stroke-linecap="round"></path></svg>`, paint.span);
}
function $62a79e86ee9eca2d$export$dc84183312f0c201(color = "currentColor") {
    const paint = $62a79e86ee9eca2d$var$paintOf(color);
    return $62a79e86ee9eca2d$var$painted(`<svg width="7" height="12" viewBox="0 0 8 14" aria-hidden="true">` + `<path d="M1 1l6 6-6 6" stroke="${paint.attr}" stroke-width="2" fill="none" stroke-linecap="round"></path></svg>`, paint.span);
}
function $62a79e86ee9eca2d$export$fe1bdb38100283b2() {
    return $62a79e86ee9eca2d$var$painted(`<svg width="13" height="10" viewBox="0 0 20 10" fill="none" aria-hidden="true">` + `<path d="M1 5h16M13 1l4 4-4 4" stroke="currentColor" stroke-width="1.6" ` + `stroke-linecap="round" stroke-linejoin="round"></path></svg>`, "var(--color-ink-6)");
}
const $62a79e86ee9eca2d$var$SVG_NS = "http://www.w3.org/2000/svg";
function $62a79e86ee9eca2d$export$3f78d04baf17558a(glyph, size, fill) {
    const width = glyph.w > 0 ? glyph.w : 512;
    const svg = document.createElementNS($62a79e86ee9eca2d$var$SVG_NS, "svg");
    svg.setAttribute("width", String(size));
    svg.setAttribute("height", String(size));
    svg.setAttribute("viewBox", `0 -448 ${width} 512`);
    svg.setAttribute("aria-hidden", "true");
    svg.setAttribute("focusable", "false");
    const path = document.createElementNS($62a79e86ee9eca2d$var$SVG_NS, "path");
    path.setAttribute("d", glyph.d);
    path.setAttribute("fill", fill);
    path.setAttribute("transform", "scale(1,-1)");
    svg.appendChild(path);
    return svg;
}
function $62a79e86ee9eca2d$export$55d7f61db91b7b70(size = 11, fill = "#FFFFFF") {
    const paint = $62a79e86ee9eca2d$var$paintOf(fill);
    return $62a79e86ee9eca2d$var$painted($62a79e86ee9eca2d$var$PHOSPHOR(size, $62a79e86ee9eca2d$var$PATHS.check, paint.attr), paint.span);
}
// ── the PQFORCE lockup ────────────────────────────────────────────────────────
/**
 * The mark and the wordmark, transcribed from
 * `packages/js/uikit-icons/custom/icons/pqforce.svg` — the same artwork the
 * portal chrome renders as `<span uk-icon="pqforce">`, so the app and the
 * portal show one logo rather than two that nearly agree.
 *
 * ⚠ THE `<style>` BLOCK IS DELIBERATELY GONE. The source file paints its paths
 * through `.st0` / `.st1` in a `<style>` element, and a `<style>` inside INLINE
 * svg is not scoped to that svg — it is a document stylesheet. Shipping it
 * would let `.st0` repaint anything else on the page carrying that class. The
 * two fills are attributes here instead. The Illustrator preamble (`version`,
 * `id`, `x`, `y`, `enable-background`, `xml:space`) is dropped for the same
 * reason it is harmless: none of it does anything in an HTML document.
 *
 * ⚠ THE COLOURS ARE THE ARTWORK'S — #1F2146 navy and #DC5956 red — NOT the
 * app's `--color-text` / `--color-accent` (#07074E, #DB5656). Those are the
 * brand PDF's values for the same two colours and they do not match the logo
 * file. A logo is artwork, so it is reproduced as authored; retinting it to the
 * tokens is one line, but it is a decision rather than a tidy-up.
 *
 * ⚠ IT IS ARTWORK, NEVER LIVE TEXT. There is no `<text>` here and none should
 * be added: a font that fails to load would silently reset the wordmark.
 */ const $62a79e86ee9eca2d$var$LOGO_NAVY = [
    "M37.6,6.5c0.6-0.3,1.3-0.5,2.1-0.5c0.9,0,1.8,0.2,2.5,0.7c0.7,0.5,1.3,1.1,1.8,2c0.4,0.9,0.6,1.9,0.6,3 s-0.2,2.2-0.6,3c-0.4,0.9-1,1.5-1.8,2c-0.7,0.5-1.6,0.7-2.5,0.7c-0.8,0-1.5-0.2-2-0.5c-0.6-0.3-1.1-0.7-1.4-1.2v6.9h-3.4V6.2h3.4 v1.6C36.6,7.3,37.1,6.9,37.6,6.5z M40.4,9.8C40,9.3,39.4,9,38.7,9C38,9,37.4,9.3,37,9.8c-0.5,0.5-0.7,1.2-0.7,2s0.2,1.5,0.7,2 c0.5,0.5,1.1,0.7,1.7,0.7s1.3-0.3,1.7-0.8c0.5-0.5,0.7-1.2,0.7-2S40.9,10.3,40.4,9.8z",
    "M45.8,8.9c0.5-0.9,1.1-1.5,1.9-2c0.8-0.5,1.6-0.7,2.5-0.7c0.7,0,1.3,0.1,1.9,0.4c0.6,0.2,1,0.6,1.4,1V6.2h3.4 v16.5h-3.4V16c-0.4,0.5-0.9,0.8-1.4,1.1c-0.6,0.3-1.2,0.4-1.9,0.4c-0.9,0-1.7-0.2-2.5-0.7c-0.8-0.5-1.4-1.2-1.8-2 c-0.5-0.9-0.7-1.9-0.7-3C45.1,10.7,45.4,9.7,45.8,8.9z M53.2,10.3c-0.2-0.4-0.5-0.7-0.9-0.9c-0.4-0.2-0.8-0.3-1.2-0.3 c-0.4,0-0.8,0.1-1.2,0.3c-0.4,0.2-0.7,0.5-0.9,0.9c-0.2,0.4-0.4,0.9-0.4,1.5c0,0.6,0.1,1.1,0.4,1.5c0.2,0.4,0.5,0.7,0.9,0.9 c0.4,0.2,0.8,0.3,1.2,0.3c0.4,0,0.8-0.1,1.2-0.3c0.4-0.2,0.7-0.5,0.9-0.9c0.2-0.4,0.3-0.9,0.3-1.5S53.4,10.7,53.2,10.3z",
    "M64.4,9.1h-1.8v8.3h-3.4V9.1h-1.2V6.2h1.2V5.9c0-1.4,0.4-2.4,1.2-3.1c0.8-0.7,1.9-1.1,3.5-1.1 c0.3,0,0.4,0,0.6,0v2.9c-0.7,0-1.1,0.1-1.4,0.3c-0.3,0.2-0.4,0.6-0.4,1.2v0.1h1.8V9.1z",
    "M67.5,16.8c-0.9-0.5-1.6-1.1-2.1-2c-0.5-0.9-0.8-1.9-0.8-3c0-1.1,0.3-2.2,0.8-3c0.5-0.9,1.2-1.5,2.1-2 c0.9-0.5,1.9-0.7,3-0.7c1.1,0,2.1,0.2,3,0.7s1.6,1.1,2.1,2c0.5,0.9,0.8,1.9,0.8,3s-0.3,2.2-0.8,3c-0.5,0.9-1.2,1.5-2.1,2 s-1.9,0.7-3,0.7C69.4,17.5,68.4,17.3,67.5,16.8z M72.2,13.9c0.5-0.5,0.7-1.2,0.7-2.1c0-0.9-0.2-1.6-0.7-2.1 c-0.4-0.5-1-0.7-1.6-0.7c-0.7,0-1.2,0.2-1.7,0.7c-0.4,0.5-0.7,1.2-0.7,2.1c0,0.9,0.2,1.6,0.7,2.1c0.4,0.5,1,0.7,1.6,0.7 C71.1,14.6,71.7,14.3,72.2,13.9z",
    "M82.5,6.6c0.6-0.4,1.3-0.5,2-0.5v3.6h-0.9c-0.9,0-1.5,0.2-1.9,0.5c-0.4,0.4-0.6,1-0.6,1.9v5.2h-3.4V6.2H81 v1.9C81.4,7.5,81.9,7,82.5,6.6z",
    "M85.5,8.8c0.5-0.9,1.1-1.5,2-2s1.8-0.7,2.9-0.7c1.4,0,2.6,0.4,3.5,1.1c0.9,0.7,1.6,1.8,1.8,3.1h-3.6 C91.8,9.4,91.2,9,90.4,9c-0.6,0-1.1,0.2-1.5,0.7s-0.6,1.2-0.6,2.1c0,0.9,0.2,1.6,0.6,2.1c0.4,0.5,0.9,0.7,1.5,0.7 c0.9,0,1.5-0.4,1.8-1.3h3.6c-0.3,1.3-0.9,2.3-1.9,3.1c-0.9,0.7-2.1,1.1-3.5,1.1c-1.1,0-2.1-0.2-2.9-0.7s-1.5-1.1-2-2 c-0.5-0.9-0.7-1.9-0.7-3S85.1,9.6,85.5,8.8z",
    "M107.5,12.6h-7.7c0.1,0.7,0.3,1.2,0.7,1.6c0.4,0.4,0.9,0.5,1.5,0.5c0.9,0,1.4-0.4,1.8-1.1h3.6 c-0.2,0.7-0.5,1.4-1,2c-0.5,0.6-1.1,1-1.8,1.4s-1.6,0.5-2.5,0.5c-1.1,0-2.1-0.2-2.9-0.7c-0.9-0.5-1.5-1.1-2-2 c-0.5-0.9-0.7-1.9-0.7-3s0.2-2.2,0.7-3c0.5-0.9,1.1-1.5,2-2s1.8-0.7,2.9-0.7c1.1,0,2,0.2,2.9,0.7s1.5,1.1,2,1.9s0.7,1.8,0.7,2.9 C107.5,11.9,107.5,12.3,107.5,12.6z M104,10.7c0-0.6-0.2-1.1-0.6-1.4c-0.4-0.3-0.9-0.5-1.5-0.5c-0.6,0-1.1,0.2-1.5,0.5 c-0.4,0.3-0.6,0.8-0.7,1.4H104z"
];
const $62a79e86ee9eca2d$var$LOGO_RED = "M24.3,17.6H21c1.1-1.8,1.7-3.8,1.7-5.9c0-6.1-5-11.1-11.1-11.1S0.5,5.5,0.5,11.6v12.7c0,1.4,1.2,2.6,2.6,2.6 s2.6-1.2,2.6-2.6V21c1.8,1.1,3.8,1.7,5.9,1.7h12.8c1.4,0,2.6-1.2,2.6-2.6S25.8,17.6,24.3,17.6z M17.5,11.6c0,3.3-2.7,5.9-5.9,5.9 s-5.9-2.7-5.9-5.9s2.7-5.9,5.9-5.9S17.5,8.4,17.5,11.6z";
function $62a79e86ee9eca2d$export$db3c28d99448dcff(height = 27) {
    // The viewBox is 108 × 27.1, so the width follows from the height rather
    // than being a second number to keep in step.
    const width = (height * 108 / 27.1).toFixed(1);
    return (0, $afadd878e68b56c8$export$98e05ab619d63cba)(`<svg width="${width}" height="${height}" viewBox="0 0 108 27.1" role="img" aria-label="PQFORCE">` + `<g class="pqm-logo-ink" fill="#1F2146">${$62a79e86ee9eca2d$var$LOGO_NAVY.map((d)=>`<path d="${d}"/>`).join("")}</g>` + `<path fill="#DC5956" d="${$62a79e86ee9eca2d$var$LOGO_RED}"/>` + `</svg>`);
}


// ─────────────────────────────────────────────────────────────────────────────
// PQFORCE Mobile — onboarding screens.
//
// ⚠ IN-PROGRESS TEXT LIVES IN THE DOM, NOT IN THE STORE.
// These screens are rebuilt whenever the STEP changes, never on a keystroke. If
// a field's value were stored on every `input`, each keystroke would re-render
// the screen and take the caret and the focus with it — the same fault the news
// page documents for its search box. So text is read from the field at submit
// time, and live feedback (clearing an error, enabling a button) is applied to
// the one affected node directly.
// ─────────────────────────────────────────────────────────────────────────────





/** What the address pill shows. The app's own host, not the tenant's. */ function $2d8359f39da97b6c$var$browserChrome() {
    return (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-chrome"
    }, [
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-chrome-pill"
        }, [
            (0, $62a79e86ee9eca2d$export$1ca1ec8b29a4ce27)("lock", 13, "var(--color-ink-5)"),
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                class: "pqm-chrome-url",
                text: window.location.host
            })
        ])
    ]);
}
/** Brand row + the four-segment progress rail. */ function $2d8359f39da97b6c$var$brandRow(state) {
    const segments = (0, $ea7cc6cc52c8cad1$export$86f9d0ab9b6b686c)(state.onboarding).map((filled)=>(0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: `pqm-rail-seg${filled ? " is-filled" : ""}`
        }));
    // The lockup is ARTWORK. It used to be a rounded square plus the word
    // "PQFORCE" set in the body font — a stand-in for the real logo, which the
    // repo did not carry. It does: `logo()` transcribes the portal's own file.
    // 24px rather than the 27px the app screens use, matching the 22px placeholder
    // it replaces, so this row stays subordinate to the step heading below it.
    return (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-brand"
    }, [
        (0, $62a79e86ee9eca2d$export$db3c28d99448dcff)(24),
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-rail",
            role: "progressbar",
            "aria-label": (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInProgress", "Setup progress")
        }, segments)
    ]);
}
/** Step 1 — which installation does this person work in? */ function $2d8359f39da97b6c$var$stepTenant(state, actions) {
    const input = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("input", {
        class: "input",
        style: "min-height:48px;font-size:15px",
        type: "url",
        inputmode: "url",
        autocapitalize: "none",
        autocorrect: "off",
        spellcheck: "false",
        id: "pqm-tenant",
        placeholder: "demo.pqforce.com",
        value: state.tenantInput
    });
    const errorText = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-field-error-text"
    });
    const error = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-field-error",
        hidden: true,
        role: "alert"
    }, [
        (0, $62a79e86ee9eca2d$export$1ca1ec8b29a4ce27)("warningCircle", 14, "var(--color-accent-650)"),
        errorText
    ]);
    const showError = (message)=>{
        errorText.textContent = message;
        error.hidden = false;
        input.setAttribute("aria-invalid", "true");
    };
    // "Error clears on the next keystroke."
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(input, "input", ()=>{
        error.hidden = true;
        input.removeAttribute("aria-invalid");
    });
    if (state.tenantError) showError(state.tenantError);
    const submit = ()=>{
        const raw = input.value.trim();
        if (!(0, $ea7cc6cc52c8cad1$export$3ebeddf6921c9442)((0, $ea7cc6cc52c8cad1$export$da45a345dadb2e43)(raw))) {
            showError((0, $ea7cc6cc52c8cad1$export$bf294736920fd217)());
            input.focus();
            return;
        }
        actions.submitTenant(raw);
    };
    const button = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
        class: "btn btn-primary btn-block",
        style: "min-height:50px;font-size:15px",
        type: "button",
        text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInContinue", "Continue")
    });
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(button, "click", submit);
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(input, "keydown", (event)=>{
        if (event.key === "Enter") submit();
    });
    // Only on the road in from the app: there is a signed-in tenant to go back to. A first run or a
    // sign-out has nowhere behind this screen.
    const back = [];
    if (state.pairingFromApp) {
        const button = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
            class: "btn btn-ghost pqm-step-back",
            type: "button"
        }, [
            (0, $62a79e86ee9eca2d$export$6038895ac52bd044)(),
            document.createTextNode((0, $498477530f916680$export$625550452a3fa3ec)("mobileViewBack", "Back"))
        ]);
        (0, $afadd878e68b56c8$export$2385a24977818dd0)(button, "click", actions.backToApp);
        back.push(button);
    }
    return (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-step"
    }, [
        ...back,
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-step-head"
        }, [
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                class: "pqm-h pqm-step-title",
                text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInWhereTitle", "Where does your team work?")
            }),
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                class: "pqm-step-body",
                text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInWhereBody", "Enter the address of your PQFORCE tenant. Your administrator can give it to you \u2014 it looks like the one you use in the browser.")
            })
        ]),
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "field"
        }, [
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("label", {
                for: "pqm-tenant",
                text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInAddressLabel", "PQFORCE address")
            }),
            input,
            error
        ]),
        button,
        // The one promise this screen makes about where the data goes.
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-reassure",
            text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInReassure", "Nothing is sent to us \u2014 the app talks only to the address you enter.")
        })
    ]);
}
/**
 * The returning and recovery screen.
 *
 * ⚠ AN EXPIRED SESSION IS NOT A FIRST RUN, and must not look like one. The
 * person has paired before, we know their installation and which method they
 * used, and sending them back to an empty address field asks them to re-supply
 * something we already have. So it opens with a "Welcome back" card offering the
 * method they used last time, over the list of everything this device knows.
 */ function $2d8359f39da97b6c$var$stepReturning(state, actions) {
    const children = [];
    const current = (0, $9a394e9e7ce3770e$export$3cd89ddcdf90b64f)(state.knownTenants);
    if (state.sessionExpired && current) {
        const reauth = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
            class: "btn btn-primary btn-block",
            style: "min-height:48px",
            type: "button",
            text: current.methodTitle || (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInAgain", "Sign in again")
        });
        (0, $afadd878e68b56c8$export$2385a24977818dd0)(reauth, "click", ()=>actions.useTenant(current));
        children.push((0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-recover",
            role: "status"
        }, [
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                class: "pqm-recover-title",
                text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInSessionExpired", "Session expired")
            }),
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                class: "pqm-recover-body",
                // Names what is at stake. Unsent local work must survive a
                // re-auth, so saying so is a promise the app has to keep.
                text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInSessionExpiredBody", "Your access to {installation} has ended. Sign in again to pick up where you left off.", {
                    installation: current.displayName || current.host
                })
            }),
            reauth
        ]));
    }
    const rows = state.knownTenants.map((tenant)=>{
        const isActive = current !== null && tenant.host === current.host;
        const row = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
            class: "pqm-method",
            type: "button"
        }, [
            // A 3px bar rather than a dot: accent for the active installation,
            // neutral otherwise. Red as a bar is allowed; as a fill it is not.
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                class: "pqm-prio",
                style: `background:${isActive ? "var(--color-accent)" : "var(--color-neutral-400)"};align-self:stretch`
            }),
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                class: "pqm-method-text"
            }, [
                (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                    class: "pqm-method-title",
                    text: tenant.host
                }),
                (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                    class: "pqm-method-sub",
                    text: (0, $9a394e9e7ce3770e$export$7be77b29b66ac800)(tenant)
                })
            ]),
            isActive && !state.sessionExpired ? (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
                class: "tag tag-accent",
                text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInActive", "Active")
            }) : (0, $62a79e86ee9eca2d$export$dc84183312f0c201)("var(--color-row-chevron)")
        ]);
        (0, $afadd878e68b56c8$export$2385a24977818dd0)(row, "click", ()=>actions.useTenant(tenant));
        return row;
    });
    const add = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
        class: "btn btn-secondary btn-block",
        type: "button",
        text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInAddInstallation", "Add another tenant")
    });
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(add, "click", actions.addAnother);
    children.push((0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-section"
    }, [
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-kicker",
            text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInYourInstallations", "Your tenants")
        }),
        ...rows
    ]), add);
    return (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-step"
    }, children);
}
/** A transient waiting state: discovery, and the SSO handoff. */ function $2d8359f39da97b6c$var$stepWaiting(title, body, onCancel) {
    const children = [
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-waiting-head"
        }, [
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                class: "pqm-pulse"
            }),
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                class: "pqm-h pqm-waiting-title",
                text: title
            })
        ]),
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-waiting-body",
            text: body
        })
    ];
    if (onCancel) {
        const cancel = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
            class: "btn btn-ghost",
            style: "font-size:12.5px",
            type: "button",
            text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInCancel", "Cancel")
        });
        (0, $afadd878e68b56c8$export$2385a24977818dd0)(cancel, "click", onCancel);
        children.push(cancel);
    }
    return (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-waiting",
        role: "status",
        "aria-live": "polite"
    }, children);
}
/**
 * Step 2 — the sign-in methods.
 *
 * ⚠ Built from the discovery response. One row per offered method; nothing here
 * assumes there are two, or that one of them is SSO.
 */ function $2d8359f39da97b6c$var$stepMethods(state, actions) {
    const discovery = state.discovery;
    const host = discovery?.host ?? (0, $ea7cc6cc52c8cad1$export$da45a345dadb2e43)(state.tenantInput);
    const methods = discovery?.methods ?? [];
    const rows = methods.map((method)=>{
        const preferred = method.preferred === true;
        const button = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
            class: `pqm-method${preferred ? " pqm-method-preferred" : ""}`,
            type: "button"
        }, [
            (0, $62a79e86ee9eca2d$export$1ca1ec8b29a4ce27)(method.kind === "sso" ? "lockKey" : "envelope", 19, preferred ? "var(--color-accent-600)" : "var(--color-ink-2)"),
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                class: "pqm-method-text"
            }, [
                (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                    class: "pqm-method-title",
                    text: method.title
                }),
                (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                    class: "pqm-method-sub",
                    text: method.subtitle
                })
            ]),
            (0, $62a79e86ee9eca2d$export$dc84183312f0c201)(preferred ? "var(--color-accent-600)" : "var(--color-row-chevron)")
        ]);
        (0, $afadd878e68b56c8$export$2385a24977818dd0)(button, "click", ()=>actions.pickMethod(method));
        return button;
    });
    const different = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
        class: "btn btn-ghost",
        style: "align-self:flex-start;font-size:12.5px",
        type: "button",
        text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInDifferentAddress", "Use a different address")
    });
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(different, "click", actions.back);
    return (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-step",
        style: "gap:18px"
    }, [
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-step-head",
            style: "gap:7px"
        }, [
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                class: "pqm-kicker pqm-kicker-accent",
                text: host
            }),
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                class: "pqm-h pqm-step-title",
                text: discovery?.displayName || (0, $ea7cc6cc52c8cad1$export$246988ca1430edd)(host)
            }),
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                class: "pqm-step-body",
                text: (0, $ea7cc6cc52c8cad1$export$ec3ad3f87e3e5eca)(methods.length)
            })
        ]),
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-section"
        }, rows),
        different
    ]);
}
/** Step 3 — interactive sign-in. */ function $2d8359f39da97b6c$var$stepLogin(state, actions) {
    const host = state.discovery?.host ?? (0, $ea7cc6cc52c8cad1$export$da45a345dadb2e43)(state.tenantInput);
    const email = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("input", {
        class: "input",
        style: "min-height:46px;font-size:15px",
        type: "email",
        inputmode: "email",
        autocapitalize: "none",
        autocomplete: "username",
        spellcheck: "false",
        id: "pqm-email",
        placeholder: "nadia.reber@company.ch"
    });
    const password = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("input", {
        class: "input",
        style: "min-height:46px;font-size:15px",
        type: "password",
        autocomplete: "current-password",
        id: "pqm-password",
        placeholder: "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022"
    });
    // Default on: it is what drives refresh-token persistence.
    const trust = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("input", {
        type: "checkbox",
        checked: true
    });
    const error = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-field-error",
        hidden: true,
        role: "alert"
    }, [
        (0, $62a79e86ee9eca2d$export$1ca1ec8b29a4ce27)("warningCircle", 14, "var(--color-accent-650)"),
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-field-error-text",
            text: state.onboardingError
        })
    ]);
    if (state.onboardingError) error.hidden = false;
    const submit = ()=>actions.signIn(email.value, password.value, trust.checked);
    const signIn = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
        class: "btn btn-primary btn-block",
        style: "min-height:50px;font-size:15px",
        type: "button",
        text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInSubmit", "Sign in")
    });
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(signIn, "click", submit);
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(password, "keydown", (event)=>{
        if (event.key === "Enter") submit();
    });
    const back = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
        class: "btn btn-ghost",
        style: "font-size:12.5px",
        type: "button",
        text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInBack", "Back")
    });
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(back, "click", actions.back);
    // Not designed yet, and not invented here — it goes to the tenant, which owns
    // the credential. Left as a link-shaped control the shell can point somewhere.
    const forgot = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
        class: "btn btn-ghost",
        style: "font-size:12.5px",
        type: "button",
        text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInForgotPassword", "Forgot password")
    });
    return (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-step pqm-step-tight"
    }, [
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-step-head",
            style: "gap:6px"
        }, [
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                class: "pqm-kicker pqm-kicker-accent",
                text: host
            }),
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                class: "pqm-h pqm-step-title-sm",
                text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInTitle", "Sign in")
            })
        ]),
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "field"
        }, [
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("label", {
                for: "pqm-email",
                text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInEmail", "Email")
            }),
            email
        ]),
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "field"
        }, [
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("label", {
                for: "pqm-password",
                text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInPassword", "Password")
            }),
            password
        ]),
        error,
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("label", {
            class: "radio",
            style: "gap:10px;font-size:13px"
        }, [
            trust,
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
                class: "dot"
            }),
            document.createTextNode((0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInKeepSignedIn", "Keep me signed in on this device"))
        ]),
        signIn,
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-actions-row"
        }, [
            back,
            forgot
        ])
    ]);
}
/**
 * Step 4 — token setup.
 *
 * The three milestones are real requests, so a failure is shown ON THE STEP THAT
 * FAILED rather than as one generic error. That is the difference between "try
 * again" and "your administrator has not switched the mobile endpoint on".
 */ function $2d8359f39da97b6c$var$stepToken(state, actions) {
    const host = state.discovery?.host ?? (0, $ea7cc6cc52c8cad1$export$da45a345dadb2e43)(state.tenantInput);
    const labels = (0, $ea7cc6cc52c8cad1$export$b836dcfe272671b)(host);
    const states = (0, $ea7cc6cc52c8cad1$export$f65cf35775105606)(state.tokenStep, state.tokenFailedAt);
    const rows = labels.map((label, index)=>{
        const status = states[index];
        const mark = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-check-mark"
        }, status === "done" ? [
            (0, $62a79e86ee9eca2d$export$55d7f61db91b7b70)()
        ] : []);
        if (status === "failed") mark.appendChild((0, $62a79e86ee9eca2d$export$1ca1ec8b29a4ce27)("warningCircle", 11, "currentColor"));
        return (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: `pqm-check is-${status}`
        }, [
            mark,
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                class: "pqm-check-label",
                text: label
            })
        ]);
    });
    const children = [
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-h",
            style: "font-size:22px;letter-spacing:-.02em",
            text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInSettingUp", "Setting up your session")
        }),
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-checklist",
            role: "status",
            "aria-live": "polite"
        }, rows)
    ];
    // ⚠ Tell them to COME BACK. Approval happens in another tab, and a backgrounded tab does not
    // run timers — so nothing at all moves here until this tab is on screen again. Without saying
    // so, the wait is indistinguishable from a hang, which is exactly how it was reported on dev.
    if (state.tokenFailedAt === null) {
        children.push((0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-step-body",
            text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInApproveHint", "Approve the request on the page that opened, then come back to this tab.")
        }));
        const check = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
            class: "btn btn-ghost",
            type: "button",
            text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInCheckNow", "I have approved \u2014 check now")
        });
        (0, $afadd878e68b56c8$export$2385a24977818dd0)(check, "click", actions.checkNow);
        children.push(check);
    }
    if (state.tokenFailedAt !== null) {
        children.push((0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-step-body",
            text: state.onboardingError
        }));
        const retry = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
            class: "btn btn-primary btn-block",
            type: "button",
            text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInTryAgain", "Try again")
        });
        (0, $afadd878e68b56c8$export$2385a24977818dd0)(retry, "click", actions.retry);
        children.push(retry);
    }
    return (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-step",
        style: "gap:18px"
    }, children);
}
/** The approval hand-off: the user is away in the browser approving the token. */ function $2d8359f39da97b6c$var$stepAwaitingApproval(state, actions) {
    // ⚠ Do not describe a tab that may not exist. window.open() is called from a promise callback,
    // which Safari on iOS blocks — it returns null rather than throwing, so the app carries on
    // waiting for an approval the user was never given the chance to make. Saying "confirm in the
    // tab that just opened" and offering to open it "again" left an iPhone with no way forward and
    // nothing on screen suggesting one, while the same build worked on a desktop browser.
    const blocked = state.approvalOpened === false;
    const wrapper = $2d8359f39da97b6c$var$stepWaiting(blocked ? (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInOneMoreStep", "One more step") : (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInWaitingApproval", "Waiting for your approval"), blocked ? (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInPopupBlocked", "Your browser would not open the approval page on its own. Tap the button below to open it, approve the request there, then come back to this tab.") : (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInConfirmInTab", "Confirm the access request in the tab that just opened. You will come back here automatically. Do not close this tab."), actions.cancelSso);
    // The tenant bounds how long the request stays approvable; saying so beats a
    // spinner that could mean anything.
    if (state.authorizationDeadline) {
        const when = new Date(state.authorizationDeadline);
        if (!Number.isNaN(when.getTime())) {
            const pad = (n)=>String(n).padStart(2, "0");
            wrapper.appendChild((0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                class: "pqm-hint",
                text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInExpiresAt", "The request expires at {time}.", {
                    time: `${pad(when.getHours())}:${pad(when.getMinutes())}`
                })
            }));
        }
    }
    if (state.authorizationUrl) {
        // A real anchor tapped by a person is a user gesture, so it opens where window.open() could
        // not — which is why this is the primary action, not a footnote, when the popup was blocked.
        const reopen = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("a", {
            class: blocked ? "btn btn-primary btn-block" : "btn btn-secondary",
            href: state.authorizationUrl,
            target: "_blank",
            rel: "noopener noreferrer",
            text: blocked ? (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInOpenApproval", "Open the approval page") : (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInOpenApprovalAgain", "Open the approval page again")
        });
        wrapper.appendChild(reopen);
    }
    return wrapper;
}
/** Discovery failed. Named copy from the handoff, plus the two offered actions. */ function $2d8359f39da97b6c$var$stepFailed(state, actions) {
    const host = (0, $ea7cc6cc52c8cad1$export$da45a345dadb2e43)(state.tenantInput);
    const retry = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
        class: "btn btn-primary btn-block",
        type: "button",
        text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInTryAgain", "Try again")
    });
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(retry, "click", actions.retry);
    const different = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
        class: "btn btn-ghost",
        style: "align-self:flex-start;font-size:12.5px",
        type: "button",
        text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInDifferentAddress", "Use a different address")
    });
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(different, "click", actions.back);
    return (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-step"
    }, [
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-step-head"
        }, [
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                class: "pqm-h pqm-step-title-sm",
                text: (0, $ea7cc6cc52c8cad1$export$74bc47fd0c9e3311)(state.onboardingErrorStatus)
            }),
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                class: "pqm-step-body",
                text: state.onboardingError || (0, $ea7cc6cc52c8cad1$export$5331fe171634f115)(host)
            })
        ]),
        retry,
        different
    ]);
}
/** The end of onboarding — and the first moment an install prompt is appropriate. */ function $2d8359f39da97b6c$var$stepDone(state, actions) {
    const host = state.profile?.tenantHost ?? (0, $ea7cc6cc52c8cad1$export$da45a345dadb2e43)(state.tenantInput);
    const firstName = (state.profile?.name ?? "").split(/\s+/)[0] ?? "";
    const open = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
        class: "btn btn-primary btn-block",
        style: "min-height:50px;font-size:15px",
        type: "button",
        text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInOpenApp", "Open PQFORCE")
    });
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(open, "click", actions.openApp);
    return (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-step"
    }, [
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-success-mark"
        }, [
            (0, $62a79e86ee9eca2d$export$1ca1ec8b29a4ce27)("check", 22, "currentColor")
        ]),
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-step-head"
        }, [
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                class: "pqm-h pqm-step-title-sm",
                text: firstName ? (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInDoneNamed", "You're in, {name}", {
                    name: firstName
                }) : (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInDone", "You're in")
            }),
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                class: "pqm-step-body",
                text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInDoneBody", "Signed in to {host}. The address is remembered, so next time you go straight to your TODOs.", {
                    host: host
                })
            })
        ]),
        open
    ]);
}
function $2d8359f39da97b6c$export$3e6cf6adc32ea205(root, state, actions) {
    let body;
    switch(state.onboarding){
        case "discovering":
            body = $2d8359f39da97b6c$var$stepWaiting((0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInChecking", "Checking {host}", {
                host: (0, $ea7cc6cc52c8cad1$export$da45a345dadb2e43)(state.tenantInput)
            }), (0, $498477530f916680$export$625550452a3fa3ec)("mobileSignInCheckingBody", "Asking which sign-in methods this tenant offers."));
            break;
        case "options":
            body = $2d8359f39da97b6c$var$stepMethods(state, actions);
            break;
        case "login":
            body = $2d8359f39da97b6c$var$stepLogin(state, actions);
            break;
        case "sso":
            body = $2d8359f39da97b6c$var$stepAwaitingApproval(state, actions);
            break;
        case "validating":
            body = $2d8359f39da97b6c$var$stepToken(state, actions);
            break;
        case "done":
            body = $2d8359f39da97b6c$var$stepDone(state, actions);
            break;
        case "failed":
            body = $2d8359f39da97b6c$var$stepFailed(state, actions);
            break;
        case "url":
        default:
            // A device that has paired before opens on its installations, not on
            // an empty address field — unless the user asked to add another.
            body = state.knownTenants.length > 0 && !state.addingTenant ? $2d8359f39da97b6c$var$stepReturning(state, actions) : $2d8359f39da97b6c$var$stepTenant(state, actions);
            break;
    }
    (0, $afadd878e68b56c8$export$9563e054e6f787fb)(root, [
        $2d8359f39da97b6c$var$browserChrome(),
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-scroll pqm-onb-body"
        }, [
            $2d8359f39da97b6c$var$brandRow(state),
            body
        ])
    ]);
}


// ─────────────────────────────────────────────────────────────────────────────
// PQFORCE Mobile — starting in the right language, following the account's, and the person's own
// choice over both.
//
// See `app/mobile/i18n.ts` for the rules. This module only reads the page, keeps the two remembered
// values in localStorage and marks <html lang>:
//
// - `pqf.mobile.language` — the ACCOUNT's language as PQFORCE last stated it (absent: states none).
// - `pqf.mobile.languageChoice` — what the person picked in the account sheet: `de`, `en`, or absent
//   for "from PQFORCE". A choice wins over the account, survives a reload and a re-pairing, and is
//   sent to the API (`X-Pqf-Language`), so the server's own sentences and the tenant's state names
//   follow it too.
// ─────────────────────────────────────────────────────────────────────────────

const $d88fccb0df7b88b9$var$LANGUAGE_KEY = "pqf.mobile.language";
const $d88fccb0df7b88b9$var$CHOICE_KEY = "pqf.mobile.languageChoice";
/** The phone's own language, as the page decided it from `Accept-Language`. */ let $d88fccb0df7b88b9$var$phoneLanguage = "en";
/** The account's language, as PQFORCE last stated it — null while it states none. */ let $d88fccb0df7b88b9$var$accountLanguage = null;
let $d88fccb0df7b88b9$var$choice = "auto";
function $d88fccb0df7b88b9$var$read(key) {
    try {
        return window.localStorage.getItem(key);
    } catch  {
        return null; // private mode: nothing survives, which is the phone's language until `me` answers
    }
}
function $d88fccb0df7b88b9$var$write(key, value) {
    try {
        if (value === null) window.localStorage.removeItem(key);
        else window.localStorage.setItem(key, value);
    } catch  {
    /* private mode: it is still applied for this session */ }
}
function $d88fccb0df7b88b9$var$applyLanguage(language) {
    (0, $498477530f916680$export$4b6bf64406ec64af)(language);
    // Screen readers pick their voice from this; hyphenation and quotes follow it too.
    document.documentElement.lang = language;
}
function $d88fccb0df7b88b9$export$544ea8661fa0df65(root) {
    let strings = null;
    try {
        strings = JSON.parse(root.dataset.strings || "null");
    } catch  {
        strings = null; // the code's own English, everywhere
    }
    (0, $498477530f916680$export$8a1db0e5d8aa5f24)(strings);
    $d88fccb0df7b88b9$var$phoneLanguage = (0, $498477530f916680$export$7248d74115066)(root.dataset.lang);
    const stored = $d88fccb0df7b88b9$var$read($d88fccb0df7b88b9$var$LANGUAGE_KEY);
    // The stored one is the ACCOUNT's, learned on an earlier start — without it a German user on an
    // English phone would see an English first frame every time until `me` answered.
    $d88fccb0df7b88b9$var$accountLanguage = stored === "de" || stored === "en" ? stored : null;
    $d88fccb0df7b88b9$var$choice = (0, $498477530f916680$export$e80e2ac6cfd81322)($d88fccb0df7b88b9$var$read($d88fccb0df7b88b9$var$CHOICE_KEY));
    const language = (0, $498477530f916680$export$1d03e05656460a20)($d88fccb0df7b88b9$var$choice, $d88fccb0df7b88b9$var$accountLanguage, $d88fccb0df7b88b9$var$phoneLanguage);
    $d88fccb0df7b88b9$var$applyLanguage(language);
    return language;
}
function $d88fccb0df7b88b9$export$e9a443f3e1c479ea() {
    return $d88fccb0df7b88b9$var$choice;
}
function $d88fccb0df7b88b9$export$acae4bd1ab2a50cd() {
    return $d88fccb0df7b88b9$var$choice === "auto" ? null : $d88fccb0df7b88b9$var$choice;
}
function $d88fccb0df7b88b9$export$7e156cfdd8f555d0(language, current) {
    $d88fccb0df7b88b9$var$accountLanguage = language === "de" || language === "en" ? language : null;
    $d88fccb0df7b88b9$var$write($d88fccb0df7b88b9$var$LANGUAGE_KEY, $d88fccb0df7b88b9$var$accountLanguage);
    const next = (0, $498477530f916680$export$1d03e05656460a20)($d88fccb0df7b88b9$var$choice, $d88fccb0df7b88b9$var$accountLanguage, $d88fccb0df7b88b9$var$phoneLanguage);
    if (next === current) return false;
    $d88fccb0df7b88b9$var$applyLanguage(next);
    return true;
}
function $d88fccb0df7b88b9$export$6401e5c1b69c061b(next, current) {
    $d88fccb0df7b88b9$var$choice = next;
    $d88fccb0df7b88b9$var$write($d88fccb0df7b88b9$var$CHOICE_KEY, next === "auto" ? null : next);
    const language = (0, $498477530f916680$export$1d03e05656460a20)($d88fccb0df7b88b9$var$choice, $d88fccb0df7b88b9$var$accountLanguage, $d88fccb0df7b88b9$var$phoneLanguage);
    if (language === current) return false;
    $d88fccb0df7b88b9$var$applyLanguage(language);
    return true;
}



// ─────────────────────────────────────────────────────────────────────────────
// PQFORCE Mobile — applying light or dark to the page.
//
// The theme is `data-theme` on <html>; `mobile.scss` redefines its colour tokens
// under `:root[data-theme="dark"]`. Everything that paints — the stylesheet, the
// inline SVG, the state circles — reads those tokens, so this attribute is the
// whole switch.
//
// ⚠ IT RUNS FROM THE BUNDLE, and the bundle loads at the end of <body>: the
// page's CSP forbids an inline script in <head>, which is the only thing that
// could apply a stored choice before the first paint. `MobilePage.ts` calls
// `applyStoredAppearance()` as its very first statement, so the gap is the
// parsing of an empty <div> — but a dark-mode device may see one white frame.
// ─────────────────────────────────────────────────────────────────────────────

const $237371521f1a88b0$var$APPEARANCE_KEY = "pqf.mobile.appearance";
/** The browser's own chrome — status bar, address bar — per theme. Mirrors `--color-bg`. */ const $237371521f1a88b0$var$THEME_COLORS = {
    light: "#FFFFFF",
    dark: "#0E0E2C"
};
const $237371521f1a88b0$var$DARK_QUERY = "(prefers-color-scheme: dark)";
/** The one listener for the phone's own setting, while `system` is chosen. */ let $237371521f1a88b0$var$deviceListener = null;
function $237371521f1a88b0$export$dde01dc24facb6b0() {
    try {
        return (0, $ca2b09cd067c2e8d$export$f50c4d358998cba5)(window.localStorage.getItem($237371521f1a88b0$var$APPEARANCE_KEY));
    } catch  {
        // Private mode, or site data blocked: the default, and nothing breaks.
        return (0, $ca2b09cd067c2e8d$export$f50c4d358998cba5)(null);
    }
}
function $237371521f1a88b0$export$26ebbdba2049c0c4(appearance) {
    try {
        window.localStorage.setItem($237371521f1a88b0$var$APPEARANCE_KEY, appearance);
    } catch  {
    /* private mode: the choice simply does not survive a reload */ }
}
function $237371521f1a88b0$var$deviceIsDark() {
    return typeof window.matchMedia === "function" && window.matchMedia($237371521f1a88b0$var$DARK_QUERY).matches;
}
function $237371521f1a88b0$export$48c323151b232e54(appearance) {
    if ($237371521f1a88b0$var$deviceListener) {
        $237371521f1a88b0$var$deviceListener.query.removeEventListener("change", $237371521f1a88b0$var$deviceListener.handler);
        $237371521f1a88b0$var$deviceListener = null;
    }
    const theme = (0, $ca2b09cd067c2e8d$export$12cea13604c831a8)(appearance, $237371521f1a88b0$var$deviceIsDark());
    $237371521f1a88b0$var$paintTheme(theme);
    if (appearance === "system" && typeof window.matchMedia === "function") {
        const query = window.matchMedia($237371521f1a88b0$var$DARK_QUERY);
        const handler = ()=>$237371521f1a88b0$var$paintTheme((0, $ca2b09cd067c2e8d$export$12cea13604c831a8)("system", query.matches));
        query.addEventListener("change", handler);
        $237371521f1a88b0$var$deviceListener = {
            query: query,
            handler: handler
        };
    }
    return theme;
}
function $237371521f1a88b0$export$8ebb91242307036f() {
    return $237371521f1a88b0$export$48c323151b232e54($237371521f1a88b0$export$dde01dc24facb6b0());
}
function $237371521f1a88b0$var$paintTheme(theme) {
    document.documentElement.dataset.theme = theme;
    // The status bar of an installed web app takes this colour; left white, a dark app would sit
    // under a white strip.
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", $237371521f1a88b0$var$THEME_COLORS[theme]);
}


// ─────────────────────────────────────────────────────────────────────────────
// PQFORCE Mobile — the bottom sheets.
//
// All over the same scrim: change the status, change the priority, the assignees,
// edit, and create. Each is built fresh when it opens and thrown away when it closes, so
// there is no stale draft to reconcile.
//
// As on the detail screen, the text a user is typing lives in the field until it
// is submitted — the confirm button is enabled in place rather than by writing a
// draft into the store and re-rendering the sheet under the caret.
// ─────────────────────────────────────────────────────────────────────────────








// ─────────────────────────────────────────────────────────────────────────────
// The date picker: a month grid on a sheet of its own, over whatever is already
// open.
//
// ⚠ IT MOUNTS ITSELF AND IS NOT PART OF THE DECLARATIVE RENDER, deliberately.
// Every form on this app keeps its values in the DOM and the whole tree is
// rebuilt on any state change — so routing the picker through the store would
// rebuild the New TODO sheet underneath it and throw away the half-written todo
// the user opened it from. An overlay that appends itself, owns its own little
// state and hands back one date on Save leaves everything beneath it untouched.
//
// The one thing that follows: it must clean up after itself on every exit path.
// `close()` is the only way out and every path goes through it.
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// The month grid behind the date picker. Pure — no DOM, no clock of its own.
//
// ⚠ EVERY DATE HERE IS A LOCAL CALENDAR DATE, written as `YYYY-MM-DD` and never
// as a `Date` crossing a boundary. `due.ts` states the reason at length: a
// date-only string is parsed as UTC by spec, so a calendar built out of instants
// puts the month's first day in the previous month west of Greenwich.
// ─────────────────────────────────────────────────────────────────────────────

function $2dc81dd6713cfcf2$export$4aba2f109a5d5f39() {
    const format = new Intl.DateTimeFormat((0, $498477530f916680$export$a6ce7dcbdf9c4fcc)(), {
        weekday: "short"
    });
    return Array.from({
        length: 7
    }, (_, i)=>format.format(new Date(2026, 0, 5 + i)).replace(".", "").slice(0, 2).toUpperCase());
}
const $2dc81dd6713cfcf2$var$MONTHS = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December"
];
function $2dc81dd6713cfcf2$export$b1bc68b38a4b1ea7(date) {
    const pad = (n)=>String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}
function $2dc81dd6713cfcf2$export$95957f813195c39c(iso) {
    return /^\d{4}-\d{2}/.exec(String(iso ?? ""))?.[0] ?? "";
}
function $2dc81dd6713cfcf2$export$7621dcaaa4455741(month, delta) {
    const parsed = /^(\d{4})-(\d{2})$/.exec(String(month ?? ""));
    if (!parsed) return month;
    const [, y, m] = parsed;
    // Day 1 rather than the current day: the 31st of a month shifted into a
    // 30-day one would roll into the next month and skip it entirely.
    const date = new Date(Number(y), Number(m) - 1 + delta, 1);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}
function $2dc81dd6713cfcf2$export$9be0b05917bbf584(month) {
    const parsed = /^(\d{4})-(\d{2})$/.exec(String(month ?? ""));
    if (!parsed) return "";
    if ((0, $498477530f916680$export$324a75438e87a537)() === "en") return `${$2dc81dd6713cfcf2$var$MONTHS[Number(parsed[2]) - 1]} ${parsed[1]}`;
    return new Intl.DateTimeFormat((0, $498477530f916680$export$a6ce7dcbdf9c4fcc)(), {
        month: "long",
        year: "numeric"
    }).format(new Date(Number(parsed[1]), Number(parsed[2]) - 1, 1));
}
function $2dc81dd6713cfcf2$export$2cb1dca7fa4861ea(month) {
    const parsed = /^(\d{4})-(\d{2})$/.exec(String(month ?? ""));
    if (!parsed) return [];
    const year = Number(parsed[1]);
    const index = Number(parsed[2]) - 1;
    const first = new Date(year, index, 1);
    // getDay() is Sunday-first; this is the Monday-first offset of the 1st.
    const lead = (first.getDay() + 6) % 7;
    const cells = [];
    for(let i = 0; i < 42; i++){
        const date = new Date(year, index, 1 - lead + i);
        cells.push({
            iso: $2dc81dd6713cfcf2$export$b1bc68b38a4b1ea7(date),
            day: date.getDate(),
            outside: date.getMonth() !== index
        });
    }
    return cells;
}
function $2dc81dd6713cfcf2$export$b3ca22798d054140(now) {
    const today = new Date(now);
    const base = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const plus = (days)=>$2dc81dd6713cfcf2$export$b1bc68b38a4b1ea7(new Date(base.getFullYear(), base.getMonth(), base.getDate() + days));
    const toMonday = (8 - base.getDay()) % 7 || 7;
    return [
        {
            label: (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextDueToday", "Today"),
            iso: plus(0)
        },
        {
            label: (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextDueTomorrow", "Tomorrow"),
            iso: plus(1)
        },
        {
            label: (0, $498477530f916680$export$625550452a3fa3ec)("mobileTextNextMonday", "Next Monday"),
            iso: plus(toMonday)
        }
    ];
}






function $4394c6b98ca59841$export$621a48d450e82cf(host, options) {
    let selected = String(options.selected ?? "");
    // Open on the month of the chosen date, or on this one — never on a month
    // nobody asked about.
    let month = (0, $2dc81dd6713cfcf2$export$95957f813195c39c)(selected) || (0, $2dc81dd6713cfcf2$export$95957f813195c39c)((0, $2dc81dd6713cfcf2$export$b1bc68b38a4b1ea7)(new Date(options.now)));
    const today = (0, $2dc81dd6713cfcf2$export$b1bc68b38a4b1ea7)(new Date(options.now));
    const grid = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-cal-grid"
    });
    const title = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-cal-title"
    });
    const summary = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-cal-summary-text"
    });
    const scrim = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-scrim pqm-scrim-date"
    });
    let closed = false;
    const close = ()=>{
        if (closed) return;
        closed = true;
        document.removeEventListener("keydown", onKey, true);
        scrim.remove();
    };
    /** Escape closes, from anywhere — the sheet under this one has focus of its own. */ const onKey = (event)=>{
        if (event.key === "Escape") {
            event.stopPropagation();
            close();
        }
    };
    const paint = ()=>{
        title.textContent = (0, $2dc81dd6713cfcf2$export$9be0b05917bbf584)(month);
        const facts = (0, $439e2ca410399204$export$566d600030c4efae)(selected, options.now);
        summary.textContent = selected ? (0, $498477530f916680$export$625550452a3fa3ec)("mobileDateDue", "Due {date}", {
            date: facts.label
        }) : (0, $498477530f916680$export$625550452a3fa3ec)("mobileDateNoDueDate", "No due date");
        grid.replaceChildren(...(0, $2dc81dd6713cfcf2$export$2cb1dca7fa4861ea)(month).map((cell)=>{
            const classes = [
                "pqm-cal-day"
            ];
            if (cell.outside) classes.push("pqm-cal-outside");
            if (cell.iso === today) classes.push("pqm-cal-today");
            if (cell.iso === selected) classes.push("pqm-cal-selected");
            // Past days stay selectable — dating something in the past is how a
            // missed deadline gets recorded — but they are dimmed, so the grid
            // says which way time runs.
            if (cell.iso < today) classes.push("pqm-cal-past");
            const button = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
                class: classes.join(" "),
                type: "button",
                "aria-pressed": cell.iso === selected ? "true" : "false",
                "aria-label": cell.iso,
                text: String(cell.day)
            });
            (0, $afadd878e68b56c8$export$2385a24977818dd0)(button, "click", ()=>{
                selected = cell.iso;
                // ⚠ Picking a leading or trailing day NAVIGATES to its month, as
                // the design says. Without this the selection would be invisible
                // the moment it was made.
                month = (0, $2dc81dd6713cfcf2$export$95957f813195c39c)(cell.iso);
                paint();
            });
            return button;
        }));
    };
    const step = (delta)=>()=>{
            month = (0, $2dc81dd6713cfcf2$export$7621dcaaa4455741)(month, delta);
            paint();
        };
    const prev = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
        class: "pqm-cal-step",
        type: "button",
        "aria-label": (0, $498477530f916680$export$625550452a3fa3ec)("mobileDatePreviousMonth", "Previous month"),
        text: "\u2039"
    });
    const next = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
        class: "pqm-cal-step",
        type: "button",
        "aria-label": (0, $498477530f916680$export$625550452a3fa3ec)("mobileDateNextMonth", "Next month"),
        text: "\u203A"
    });
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(prev, "click", step(-1));
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(next, "click", step(1));
    const quick = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-cal-quick"
    }, (0, $2dc81dd6713cfcf2$export$b3ca22798d054140)(options.now).map((option)=>{
        const button = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
            class: "pqm-pick pqm-pick-chip",
            type: "button",
            text: option.label
        });
        (0, $afadd878e68b56c8$export$2385a24977818dd0)(button, "click", ()=>{
            selected = option.iso;
            month = (0, $2dc81dd6713cfcf2$export$95957f813195c39c)(option.iso);
            paint();
        });
        return button;
    }));
    // ⚠ Clear is not decoration: "a due date can be removed — absence is a
    // legitimate value". It saves an EMPTY date rather than cancelling, which is
    // why it sits beside Save and not beside the close button.
    const clear = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
        class: "btn btn-ghost",
        type: "button",
        text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileDateClear", "Clear")
    });
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(clear, "click", ()=>{
        options.onSave("");
        close();
    });
    const save = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
        class: "btn btn-primary",
        type: "button",
        text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileDateSave", "Save due date")
    });
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(save, "click", ()=>{
        options.onSave(selected);
        close();
    });
    // A cross, like every card's close.
    const cancel = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
        class: "pqm-appbar-icon pqm-sheet-close",
        type: "button",
        "aria-label": (0, $498477530f916680$export$625550452a3fa3ec)("mobileDateCancel", "Cancel")
    }, [
        (0, $62a79e86ee9eca2d$export$1ca1ec8b29a4ce27)("x", 18)
    ]);
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(cancel, "click", close);
    const panel = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-sheet pqm-cal-sheet",
        role: "dialog",
        "aria-modal": "true",
        "aria-label": (0, $498477530f916680$export$625550452a3fa3ec)("mobileDateTitle", "Due date")
    }, [
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-sheet-head"
        }, [
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                class: "pqm-h pqm-sheet-title",
                text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileDateTitle", "Due date")
            }),
            cancel
        ]),
        quick,
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-cal-head"
        }, [
            prev,
            title,
            next
        ]),
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-cal-weekdays"
        }, (0, $2dc81dd6713cfcf2$export$4aba2f109a5d5f39)().map((day)=>(0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                class: "pqm-cal-weekday",
                text: day
            }))),
        grid,
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-cal-summary"
        }, [
            summary,
            clear
        ]),
        save
    ]);
    scrim.appendChild(panel);
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(scrim, "click", (event)=>{
        if (event.target === scrim) close();
    });
    document.addEventListener("keydown", onKey, true);
    paint();
    host.appendChild(scrim);
    return close;
}



// ─────────────────────────────────────────────────────────────────────────────
// PQFORCE Mobile — the TODO screens: home, list, detail, and the three sheets.
//
// ⚠ THE DETAIL SCREEN NEVER SHOWS A STATUS PICKER. It shows the transitions the
// API said this user may fire, as buttons, and says plainly when there are none.
// The lifecycle is tenant configuration, not UI: which moves exist, and who may
// make them, is a property of the tenant and the caller's relation to the item.
// Anything that reconstructs that list locally is a bug, however plausible its
// table looks — see the header of `app/mobile/types.ts`.
// ─────────────────────────────────────────────────────────────────────────────








// ─────────────────────────────────────────────────────────────────────────────
// PQFORCE Mobile — a lifecycle state, drawn.
//
// One circle, used wherever the app shows a state: the list rows, the detail header,
// the activity log, the transition buttons and the transition sheet. Shared so that a
// state looks the same before and after a move — the button promises exactly the paint
// the row will wear afterwards.
// ─────────────────────────────────────────────────────────────────────────────



function $98524becae549989$export$8614a336e4173002(transition) {
    return {
        status: transition.to,
        statusCategory: transition.toCategory,
        statusColor: transition.toColor,
        statusIcon: transition.toIcon
    };
}
function $98524becae549989$export$7c7b858b9f86c22a(state, /** The CIRCLE's size — both 24px since 2026-09-28 (`sm` was 20px). The glyph inside it is 20; see below. */ size = "md", /** True where the name is printed right beside the circle, so a screen reader hears it once. */ decorative = false) {
    // Neither colour nor category is a real case — a history entry whose endpoint the tenant
    // never described — and stateColor() answers it with the neutral colour, not a guess.
    const color = (0, $0a55aae61f0d5dc0$export$38e0108eebce6b1c)(state.statusCategory, state.statusColor);
    const status = state.status;
    const dot = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
        class: `pqm-state pqm-state-${size}`,
        role: decorative ? null : "img",
        "aria-label": decorative ? null : status,
        "aria-hidden": decorative ? "true" : null,
        title: decorative ? null : status,
        // Only the colour is inline; the stylesheet draws icon, ring and tint from it per theme.
        style: `--state:${color}`
    });
    if (state.statusIcon && state.statusIcon.d) // ⚠ ONE SIZE, AND IT IS DELIBERATELY BIGGER THAN ITS CIRCLE'S CLEARANCE (2026-09-23). It was
    // 13 in the small circle and 15 in the big one; at that size a tenant's icon was a smudge on
    // a phone, which is the whole reason the word left these rows. 20 fills the 24px circle to
    // within 2px.
    //
    // What that costs, measured against the MDI font rather than guessed: a typical glyph inks
    // only ~83% of its box (`check-circle`, `progress-clock`, `help-circle`, `progress-alert`),
    // so 20 draws ~16.7px and still clears the 1.5px ring in the big circle. `block-helper` —
    // "Blocked", which real tenants use — inks 100%, edge to edge, so in the SMALL circle, while it
    // was 20px, it covered the ring and its corners sat outside the curve. That is why
    // `.pqm-state-sm` became 24px too (2026-09-28): the fix is a bigger circle, never a smaller glyph.
    // `lg` is the detail screen's status control (32px), where the glyph grows with it.
    dot.appendChild((0, $62a79e86ee9eca2d$export$3f78d04baf17558a)(state.statusIcon, size === "lg" ? 26 : 20, "currentColor"));
    else dot.appendChild((0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
        class: "pqm-state-initial",
        "aria-hidden": "true",
        text: $98524becae549989$var$initialOf(status)
    }));
    return dot;
}
/** The first letter of a state's name, for a state with no icon. Code points, so an emoji is whole. */ function $98524becae549989$var$initialOf(status) {
    const first = Array.from(status.trim())[0];
    return first ? first.toLocaleUpperCase() : "?";
}


/**
 * The home header's avatar, as the way into the account sheet.
 *
 * ⚠ IT IS A BUTTON, and it is the app's only route to switching installation or signing out. It
 * was a decorative `div` — which is where the whole feature was missing from: the tenant picker
 * exists but renders on the signed-out branch, and a signed-in device cannot reach it.
 *
 * 44px, because it is now a touch target and no longer a 36px ornament; the avatar keeps its own
 * size inside it, so the header does not change shape.
 */ function $817c29ac222633b5$var$accountButton(name, actions) {
    const button = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
        class: "pqm-account-button",
        type: "button",
        "aria-label": (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewAccountButton", "Account and tenants")
    }, [
        $817c29ac222633b5$var$avatar(name || "?")
    ]);
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(button, "click", actions.openAccount);
    return button;
}
function $817c29ac222633b5$var$avatar(name, size = "lg") {
    const cls = size === "sm" ? "pqm-avatar pqm-avatar-sm" : size === "md" ? "pqm-avatar pqm-avatar-md" : "pqm-avatar";
    return (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: cls,
        text: (0, $0a55aae61f0d5dc0$export$f6a098653d630c23)(name),
        "aria-hidden": "true"
    });
}
function $817c29ac222633b5$export$e0891a306dc0f31(value) {
    const priority = (0, $2336e268632bd98b$export$29a85ef5d4ea90eb)(value);
    if (!priority) return null;
    const name = (0, $2336e268632bd98b$export$bf7f685c4fbf1078)(priority.id);
    return (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
        class: "pqm-prio-chip",
        style: `background:${priority.color};color:${(0, $2336e268632bd98b$export$34aad111a23000c4)(priority.color)}`,
        title: (0, $498477530f916680$export$625550452a3fa3ec)("mobilePriorityChipLabel", "{priority} priority", {
            priority: name
        }),
        text: name
    });
}
/**
 * The tone of a due group's heading chip — a CSS class suffix, never a colour: the chips are drawn
 * from the theme's own tokens in mobile.scss, so they follow dark mode like everything else.
 */ function $817c29ac222633b5$var$dueChipTone(bucket) {
    switch(bucket){
        case "Overdue":
            return "overdue";
        case "Today":
            return "today";
        case "This week":
            return "week";
        case "None":
            return "none";
        default:
            return "later";
    }
}
/** One row in the grouped list. `compact` is the home screen's shorter variant. */ function $817c29ac222633b5$var$todoRow(todo, actions, now, compact = false) {
    const due = (0, $439e2ca410399204$export$566d600030c4efae)(todo.dueISO, now);
    const meta = [
        (0, $98524becae549989$export$7c7b858b9f86c22a)(todo),
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: `pqm-due ${due.overdue ? "pqm-due-overdue" : "pqm-due-normal"}`,
            text: due.label
        })
    ];
    const chip = $817c29ac222633b5$export$e0891a306dc0f31(todo.priority);
    if (chip) meta.push(chip);
    if (compact) meta.push((0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-row-who",
        style: "margin-left:auto",
        text: (0, $0a55aae61f0d5dc0$export$ec59b59d53da5a56)(todo)
    }));
    else {
        if (todo.comments.length > 0) meta.push((0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
            class: "pqm-comments"
        }, [
            (0, $62a79e86ee9eca2d$export$1ca1ec8b29a4ce27)("chat", 12, "var(--color-ink-6)"),
            document.createTextNode(String(todo.comments.length))
        ]));
        // ⚠ NO NAME, NO BLOCK. A todo nobody is assigned to put the word "Assignee" beside an empty
        // avatar — a label pointing at nothing, which reads as a row that failed to load rather than
        // as a todo waiting for somebody. The detail screen is where the absence is stated in words;
        // a list row has too little space to say it. Covers the ownerless case the same way.
        const other = (0, $0a55aae61f0d5dc0$export$9d312342c5da567c)(todo);
        if (other.name) meta.push((0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
            class: "pqm-row-right"
        }, [
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
                class: "pqm-row-who",
                text: other.label
            }),
            $817c29ac222633b5$var$avatar(other.name, "sm")
        ]));
    }
    // The code first, as PQFORCE's own lists show it: `T-12` is what people quote to each other.
    const body = [
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-row-title"
        }, [
            ...todo.code ? [
                (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
                    class: "pqm-code pqm-code-sm",
                    text: todo.code
                })
            ] : [],
            document.createTextNode(todo.title)
        ])
    ];
    if (!compact) {
        // ⚠ ONE LINK OR NONE, and absence is stated. This line used to read `project · task` and
        // printed " · —" on every todo the server had failed to resolve — which was all of them.
        const linked = todo.ppm || (todo.related ?? []).length > 0;
        body.push((0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: `pqm-row-sub${linked ? "" : " pqm-row-sub-none"}`,
            text: (0, $0a55aae61f0d5dc0$export$63902713e18bfd31)(todo)
        }));
    }
    body.push((0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-row-meta"
    }, meta));
    // No priority bar on the left any more (removed 2026-09-28): the chip in the meta line says it,
    // in words as well as colour, and a bar only on SOME rows put their text out of line with the rest.
    const row = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
        class: "pqm-row",
        type: "button"
    }, [
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-row-body"
        }, body)
    ]);
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(row, "click", ()=>actions.openTodo(todo));
    return row;
}
function $817c29ac222633b5$export$da2a053ec9c45f2a(state, options) {
    const address = state.profile?.tenantUrl || state.profile?.tenantHost || "";
    if (options.home) {
        const children = [
            (0, $62a79e86ee9eca2d$export$db3c28d99448dcff)(24)
        ];
        if (address) children.push((0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-appbar-tenant",
            title: address,
            text: address
        }));
        children.push(options.home.search, options.home.account);
        return (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("header", {
            class: "pqm-appbar pqm-appbar-home"
        }, children);
    }
    const children = [];
    if (options.onBack) {
        const back = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
            class: "pqm-appbar-back",
            type: "button",
            "aria-label": (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewBack", "Back")
        }, [
            (0, $62a79e86ee9eca2d$export$6038895ac52bd044)()
        ]);
        (0, $afadd878e68b56c8$export$2385a24977818dd0)(back, "click", options.onBack);
        children.push(back);
    }
    // One line: the code (never cut), then the title with an ellipsis.
    const titleRow = options.code ? (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-appbar-title-row"
    }, [
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
            class: "pqm-code",
            text: options.code
        }),
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-h pqm-appbar-title",
            text: options.title ?? ""
        })
    ]) : (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-h pqm-appbar-title",
        text: options.title ?? ""
    });
    const text = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-appbar-text"
    }, [
        titleRow
    ]);
    if (address) text.appendChild((0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-appbar-sub",
        title: address,
        text: address
    }));
    children.push(text);
    if (options.actions?.length) children.push((0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-appbar-actions"
    }, options.actions));
    return (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("header", {
        class: "pqm-appbar"
    }, children);
}
/**
 * One MODULE's block on the home screen: its name, then one row per list it offers, each with its
 * count and leading to that list. TODOs are the first module; projects and tasks will add blocks of
 * the same shape, which is why this is a block and not two tiles.
 */ function $817c29ac222633b5$var$moduleBlock(title, glyph, rows) {
    return (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("section", {
        class: "pqm-home-block",
        "aria-label": title
    }, [
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-home-block-head"
        }, [
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
                class: "pqm-home-block-icon",
                "aria-hidden": "true"
            }, [
                (0, $62a79e86ee9eca2d$export$1ca1ec8b29a4ce27)(glyph, 18)
            ]),
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                class: "pqm-h pqm-home-block-title",
                text: title
            })
        ]),
        ...rows.map((row)=>{
            const button = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
                class: "pqm-home-row",
                type: "button"
            }, [
                (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
                    class: "pqm-home-row-label",
                    text: row.label
                }),
                (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
                    class: "pqm-home-row-count pqm-tabular",
                    text: row.count
                }),
                (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
                    class: "pqm-home-row-chevron",
                    "aria-hidden": "true"
                }, [
                    (0, $62a79e86ee9eca2d$export$dc84183312f0c201)()
                ])
            ]);
            (0, $afadd878e68b56c8$export$2385a24977818dd0)(button, "click", row.onOpen);
            return button;
        })
    ]);
}
/**
 * The TODOs block: how many OPEN TODOs are on this person's two lists (a state of any category but
 * `CLOSED`, `isOpenTodo()`), each named as the TENANT names it
 * (`relationListName()` — `To dos` / `Eigene Todos` on the measured tenant). Counts only, no
 * break-down per state: the list is one tap away. "…" while the first read is still on its way, so a
 * zero never stands in for "not known yet".
 */ function $817c29ac222633b5$var$todosBlock(state, actions) {
    const count = (role)=>state.loading && state.todos.length === 0 ? "\u2026" : String((0, $0a55aae61f0d5dc0$export$371eecdf1cc71c1)((0, $0a55aae61f0d5dc0$export$7887e07d7b5d5fe1)(state.todos, role)));
    return $817c29ac222633b5$var$moduleBlock((0, $498477530f916680$export$625550452a3fa3ec)("mobileViewTodosTitle", "TODOs"), "checkSquare", [
        {
            label: $817c29ac222633b5$var$scopeLabel("assignee"),
            count: count("assignee"),
            onOpen: ()=>actions.openRelation("assignee")
        },
        {
            label: $817c29ac222633b5$var$scopeLabel("owner"),
            count: count("owner"),
            onOpen: ()=>actions.openRelation("owner")
        }
    ]);
}
function $817c29ac222633b5$export$5e5c8210e5686582(state, actions, now = Date.now()) {
    const body = [
        $817c29ac222633b5$var$todosBlock(state, actions)
    ];
    if (state.loadError) body.push((0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-note"
    }, [
        (0, $62a79e86ee9eca2d$export$1ca1ec8b29a4ce27)("warningCircle", 15, "var(--color-ink-5)"),
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-note-text",
            text: state.loadError
        })
    ]));
    // ⚠ NO "See all TODOs" BUTTON. The tab bar under it reaches the list from every screen, so the
    // button was a second door to the room next door; searching is on the LIST, where the rows are.
    const profileName = state.profile?.name ?? "";
    // The greeting scrolls with the page: it is a welcome, not a control, and the header it used
    // to share a row with is now as slim as the other screens'.
    body.unshift((0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-greeting"
    }, [
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-greeting-kicker",
            text: $817c29ac222633b5$var$greeting()
        }),
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-h pqm-greeting-title",
            text: profileName ? (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewHelloName", "Hello, {name}", {
                name: profileName.split(/\s+/)[0]
            }) : (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewHello", "Hello")
        })
    ]));
    return [
        $817c29ac222633b5$export$da2a053ec9c45f2a(state, {
            home: {
                search: $817c29ac222633b5$var$searchButton(state, actions),
                account: $817c29ac222633b5$var$accountButton(profileName, actions)
            }
        }),
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-scroll pqm-home-body"
        }, body)
    ];
}
/** The kicker over the greeting — today's date, spelled the way the design shows it. */ function $817c29ac222633b5$var$greeting() {
    const now = new Date();
    // German reads "Dienstag, 22. September" from the platform; the English keeps the design's own
    // spelling, which no locale produces exactly.
    if ((0, $498477530f916680$export$324a75438e87a537)() === "de") return new Intl.DateTimeFormat((0, $498477530f916680$export$a6ce7dcbdf9c4fcc)(), {
        weekday: "long",
        day: "numeric",
        month: "long"
    }).format(now);
    const days = [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday"
    ];
    const months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December"
    ];
    return `${days[now.getDay()]} ${now.getDate()} ${months[now.getMonth()]}`;
}
// ── search ───────────────────────────────────────────────────────────────────
/**
 * What is typed into the search card, across repaints.
 *
 * ⚠ NOT IN THE STORE, for the reason `assigneeQuery` is not: every store update repaints the whole
 * app, so a keystroke written there would rebuild the field under the caret. Only the card's RESULTS
 * are repainted as you type; this is read back when something else repaints the app (the TODOs
 * re-read, a toast), which would otherwise clear a search somebody is halfway through. Kept when
 * the card closes, so opening it again picks up where it was; cleared with `clearTodoSearch()` when
 * the installation changes.
 */ let $817c29ac222633b5$var$searchQuery = "";
/** The header's search button, on every app screen. Its label names what it searches. */ function $817c29ac222633b5$var$searchButton(state, actions) {
    const button = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
        class: "pqm-appbar-icon",
        type: "button",
        "aria-label": $817c29ac222633b5$var$searchLabels((0, $0a55aae61f0d5dc0$export$96d1499a68ee10b5)(state)).button,
        "aria-haspopup": "dialog"
    }, [
        (0, $62a79e86ee9eca2d$export$1ca1ec8b29a4ce27)("magnifier", 20)
    ]);
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(button, "click", actions.openSearch);
    return button;
}
/** The words of one search context — one today; a second module adds its own here. */ function $817c29ac222633b5$var$searchLabels(_context) {
    return {
        button: (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewSearchTodosButton", "Search TODOs"),
        placeholder: (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewSearchTodos", "Search code, title and description")
    };
}
function $817c29ac222633b5$export$6893370b63db6be(state, actions, now = Date.now()) {
    const labels = $817c29ac222633b5$var$searchLabels((0, $0a55aae61f0d5dc0$export$96d1499a68ee10b5)(state));
    // ⚠ Whether the field HAD focus is read before this render replaces it.
    const hadFocus = document.activeElement?.classList.contains("pqm-search-input") ?? false;
    const input = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("input", {
        class: "input pqm-search-input",
        type: "search",
        // No placeholder: the hint under the field already says what is searched. The name stays
        // for a screen reader.
        "aria-label": labels.placeholder,
        autocomplete: "off",
        enterkeyhint: "search"
    });
    input.value = $817c29ac222633b5$var$searchQuery;
    /**
     * Empties the field and keeps the card open — a filled circle inside the field, the shape phones
     * use for exactly this. Shown only while there is something to clear. The browsers' own clear
     * button on `type=search` is hidden in the stylesheet: Safari on iOS draws none, so it could not
     * be relied on, and two crosses side by side would be one too many.
     */ const clear = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
        class: "pqm-search-clear",
        type: "button",
        "aria-label": (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewClearSearch", "Clear search")
    }, [
        (0, $62a79e86ee9eca2d$export$1ca1ec8b29a4ce27)("xCircle", 18)
    ]);
    clear.hidden = $817c29ac222633b5$var$searchQuery === "";
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(clear, "click", ()=>{
        $817c29ac222633b5$var$searchQuery = "";
        input.value = "";
        clear.hidden = true;
        paint();
        input.focus();
    });
    // A plain CROSS, like every card's close (2026-10-18). The clear button inside the field is a
    // FILLED circle with a cross, so the two shapes stay apart.
    const close = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
        class: "pqm-appbar-icon",
        type: "button",
        "aria-label": (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewCloseSearch", "Close search")
    }, [
        (0, $62a79e86ee9eca2d$export$1ca1ec8b29a4ce27)("x", 18)
    ]);
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(close, "click", actions.closeSearch);
    const results = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-search-results",
        role: "list"
    });
    /** The results for whatever is typed — rebuilt by the field itself, never through the store. */ const paint = ()=>{
        const query = $817c29ac222633b5$var$searchQuery.trim();
        if (!query) {
            (0, $afadd878e68b56c8$export$9563e054e6f787fb)(results, [
                (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                    class: "pqm-search-hint",
                    text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewSearchHint", "Type to search the code, title and description of every TODO you hold.")
                })
            ]);
            return;
        }
        const found = (0, $0a55aae61f0d5dc0$export$4f5121a84b794f1e)(state.todos, query);
        if (found.length === 0) {
            (0, $afadd878e68b56c8$export$9563e054e6f787fb)(results, [
                (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                    class: "pqm-search-hint",
                    // ⚠ A search that found nothing must say SO, naming what was searched for.
                    text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewNoTodoMatches", "No TODO matches \u201C{query}\u201D.", {
                        query: query
                    })
                })
            ]);
            return;
        }
        (0, $afadd878e68b56c8$export$9563e054e6f787fb)(results, [
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                class: "pqm-kicker pqm-search-count",
                text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewMatchCount", "{count} found", {
                    count: found.length
                })
            }),
            ...found.map((todo)=>$817c29ac222633b5$var$todoRow(todo, actions, now, true))
        ]);
    };
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(input, "input", ()=>{
        $817c29ac222633b5$var$searchQuery = input.value;
        clear.hidden = $817c29ac222633b5$var$searchQuery === "";
        paint();
    });
    paint();
    const card = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-search-card",
        role: "dialog",
        "aria-label": labels.button
    }, [
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-search-field"
        }, [
            (0, $62a79e86ee9eca2d$export$1ca1ec8b29a4ce27)("magnifier", 16, "var(--color-ink-5)"),
            input,
            clear,
            close
        ]),
        results
    ]);
    const scrim = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-scrim pqm-search-scrim",
        "data-overlay": "search",
        tabindex: "-1"
    }, [
        card
    ]);
    // Only a tap on the scrim itself closes — not one inside the card that bubbles up to it.
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(scrim, "click", (event)=>{
        if (event.target === scrim) actions.closeSearch();
    });
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(scrim, "keydown", (event)=>{
        if (event.key === "Escape") actions.closeSearch();
    });
    window.setTimeout(()=>{
        // Opening the card puts the caret in the field; a repaint gives it back only if it had it.
        if (!scrim.classList.contains("pqm-steady") || hadFocus) {
            input.focus();
            input.setSelectionRange(input.value.length, input.value.length);
        }
    }, 0);
    return scrim;
}
function $817c29ac222633b5$export$4dbd032533367935() {
    $817c29ac222633b5$var$searchQuery = "";
}
/** A list segment's name — the header's title, and each segment button's accessible name. */ function $817c29ac222633b5$var$scopeLabel(scope) {
    // The tenant's own names for a person's two lists win (relationNames.ts — `To dos`,
    // `Eigene Todos` on the measured tenant); the app's words where the tenant has none.
    if (scope === "owner") return (0, $0a4952cb2d3e704b$export$38e3edeba43dc984)("owner", (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewIOwn", "I own"));
    if (scope === "all") return (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewAllTodos", "All TODOs");
    return (0, $0a4952cb2d3e704b$export$38e3edeba43dc984)("assignee", (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewAssignedToMe", "Assigned to me"));
}
function $817c29ac222633b5$export$4bf387f59bfc6d67(state, actions, now = Date.now()) {
    const add = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
        class: "btn btn-primary btn-icon",
        style: "width:34px;height:34px",
        type: "button",
        "aria-label": (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewNewTodo", "New TODO")
    }, [
        (0, $62a79e86ee9eca2d$export$1ca1ec8b29a4ce27)("plus", 17)
    ]);
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(add, "click", ()=>actions.openNewSheet());
    // ⚠ ICONS, NOT WORDS (2026-10-18), so the segments share ONE line with the count and the tools
    // instead of taking a line of their own. Each option keeps its name — as the radio's label for a
    // screen reader and as a tooltip — and the CHOSEN one is named in the header's title.
    const segment = (label, relation, glyph)=>{
        const input = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("input", {
            type: "radio",
            name: "pqm-relation",
            checked: state.relation === relation,
            "aria-label": label
        });
        const option = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("label", {
            class: "seg-opt pqm-seg-icon",
            title: label
        }, [
            input,
            (0, $62a79e86ee9eca2d$export$1ca1ec8b29a4ce27)(glyph, 18)
        ]);
        (0, $afadd878e68b56c8$export$2385a24977818dd0)(input, "change", ()=>actions.setRelation(relation));
        return option;
    };
    // Steps through the groupings in turn (nextGrouping()). It SHOWS only the grouping's name, to fit
    // the shared line; its accessible name says the whole sentence.
    const [groupName, groupSentence] = state.grouping === "status" ? [
        (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewGroupingStatus", "Status"),
        (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewGroupedByStatus", "Grouped by Status")
    ] : state.grouping === "priority" ? [
        (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewGroupingPriority", "Priority"),
        (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewGroupedByPriority", "Grouped by Priority")
    ] : state.grouping === "project" ? [
        (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewGroupingProject", "Project"),
        (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewGroupedByProject", "Grouped by Project")
    ] : [
        (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewGroupingDue", "Due date"),
        (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewGroupedByDue", "Grouped by Due date")
    ];
    const group = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
        class: "btn btn-ghost pqm-group-toggle",
        type: "button",
        text: groupName,
        "aria-label": groupSentence,
        title: groupSentence
    });
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(group, "click", actions.toggleGrouping);
    const count = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-list-count"
    });
    /**
     * Collapse or expand every group ON SCREEN at once. Hidden while there is no group to act on.
     */ const foldAll = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
        class: "pqm-appbar-icon pqm-fold-all",
        type: "button"
    });
    let shownKeys = [];
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(foldAll, "click", ()=>{
        actions.setGroupsCollapsed(shownKeys, (0, $0a55aae61f0d5dc0$export$44f691e0cb516e9e)(state.collapsedGroups, shownKeys));
    });
    const body = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-list-groups"
    });
    const groupBlock = (g)=>{
        // ⚠ The heading is a BUTTON, not a clickable div: it has to be reachable by keyboard and
        // announce its state, and `aria-expanded` is what a screen reader reads that from. The
        // count stays visible when folded — it is the one thing a folded group still has to say.
        const collapsed = state.collapsedGroups.includes(g.key);
        const head = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
            class: `pqm-group-head${collapsed ? " pqm-group-collapsed" : ""}`,
            type: "button",
            "aria-expanded": collapsed ? "false" : "true"
        }, [
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
                class: "pqm-group-chevron"
            }, [
                (0, $62a79e86ee9eca2d$export$dc84183312f0c201)()
            ]),
            // A status group's state circle — the same one its rows draw. Decorative: the state's
            // name is printed right beside it, and a screen reader should hear it once.
            ...g.state ? [
                (0, $98524becae549989$export$7c7b858b9f86c22a)(g.state, "sm", true)
            ] : [],
            // A PRIORITY group is headed by the chip its rows carry, a DUE group by a chip coloured
            // for how pressing it is — each says the group's name, so it replaces the label
            // rather than repeating it beside it.
            $817c29ac222633b5$export$e0891a306dc0f31(g.priority) ?? (g.due ? (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
                class: `pqm-due-chip pqm-due-chip-${$817c29ac222633b5$var$dueChipTone(g.due)}`,
                text: g.label
            }) : (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
                class: "pqm-kicker",
                text: g.label
            })),
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
                class: "pqm-group-count",
                text: String(g.count)
            })
        ]);
        (0, $afadd878e68b56c8$export$2385a24977818dd0)(head, "click", ()=>actions.toggleGroup(g.key));
        // A project group gets a + after its name: a new TODO in THIS project. Beside the heading,
        // never inside it — the heading is itself a button (fold), and buttons do not nest.
        let headRow = head;
        if (g.projectId) {
            const projectId = g.projectId;
            const addHere = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
                class: "pqm-group-add",
                type: "button",
                "aria-label": (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewNewTodoInProject", "New TODO in {project}", {
                    project: g.label
                })
            }, [
                (0, $62a79e86ee9eca2d$export$1ca1ec8b29a4ce27)("plus", 16)
            ]);
            (0, $afadd878e68b56c8$export$2385a24977818dd0)(addHere, "click", ()=>actions.openNewSheet(projectId));
            headRow = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                class: "pqm-group-headrow"
            }, [
                head,
                addHere
            ]);
        }
        return (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-group"
        }, [
            headRow,
            ...collapsed ? [] : g.items.map((todo)=>$817c29ac222633b5$var$todoRow(todo, actions, now))
        ]);
    };
    /** The groups, the count and the empty line. */ const paint = ()=>{
        const list = (0, $0a55aae61f0d5dc0$export$4db3194d3a8e1f24)(state.todos, state.allTodos, state.relation);
        const groups = (0, $0a55aae61f0d5dc0$export$3110bb3f95eace5)(list, state.grouping, now);
        shownKeys = groups.map((g)=>g.key);
        foldAll.hidden = shownKeys.length === 0;
        // An icon, named by what it will do — collapse while any group is open, expand once none is.
        const collapse = (0, $0a55aae61f0d5dc0$export$44f691e0cb516e9e)(state.collapsedGroups, shownKeys);
        const foldLabel = collapse ? (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewCollapseAll", "Collapse all") : (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewExpandAll", "Expand all");
        foldAll.setAttribute("aria-label", foldLabel);
        foldAll.title = foldLabel;
        foldAll.replaceChildren((0, $62a79e86ee9eca2d$export$1ca1ec8b29a4ce27)(collapse ? "collapseAll" : "expandAll", 18));
        // OPEN only (`isOpenTodo()`): the rows still include the closed ones, but they are not open.
        count.textContent = (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewOpenCount", "{count} open", {
            count: (0, $0a55aae61f0d5dc0$export$371eecdf1cc71c1)(list)
        });
        const blocks = groups.map(groupBlock);
        if (groups.length === 0) blocks.push((0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-empty",
            text: state.relation === "all" && state.allError ? state.allError : state.loading || state.relation === "all" && (state.allLoading || !state.allTodos) ? (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewLoading", "Loading\u2026") : state.relation === "all" ? (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewNothingVisible", "There are no TODOs you can see.") : state.relation === "owner" ? (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewNothingOwned", "Nothing in \u201C{list}\u201D yet. TODOs you create appear here.", {
                list: $817c29ac222633b5$var$scopeLabel("owner")
            }) : (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewNothingAssigned", "Nothing in \u201C{list}\u201D right now.", {
                list: $817c29ac222633b5$var$scopeLabel("assignee")
            })
        }));
        (0, $afadd878e68b56c8$export$9563e054e6f787fb)(body, blocks);
    };
    paint();
    // ⚠ The segments and the tools scroll WITH the list rather than sitting under the header: they
    // are set once and then out of the way, and the rows are what the screen is for. Searching is
    // the header's (searchButton()), as on every screen.
    return [
        $817c29ac222633b5$export$da2a053ec9c45f2a(state, {
            // The CHOSEN segment, named — the segments themselves are icons now.
            title: $817c29ac222633b5$var$scopeLabel(state.relation),
            actions: [
                $817c29ac222633b5$var$searchButton(state, actions),
                add
            ]
        }),
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-scroll pqm-list-body"
        }, [
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                class: "pqm-list-tools"
            }, [
                (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                    class: "seg pqm-seg-icons",
                    role: "radiogroup",
                    "aria-label": (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewTodosTitle", "TODOs")
                }, [
                    segment($817c29ac222633b5$var$scopeLabel("assignee"), "assignee", "person"),
                    segment($817c29ac222633b5$var$scopeLabel("owner"), "owner", "crown"),
                    // Every TODO this person may see, held or not — read when first chosen.
                    segment($817c29ac222633b5$var$scopeLabel("all"), "all", "listAll")
                ]),
                count,
                (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                    class: "pqm-list-tool-buttons"
                }, [
                    foldAll,
                    group
                ])
            ]),
            body
        ])
    ];
}
// ── detail ───────────────────────────────────────────────────────────────────
/**
 * The facts: what the TODO is linked to, and who it is for and by — one block per link slot
 * (`linkRows()`), each shaped like the description's: a heading, the controls at its end, the
 * values underneath (2026-10-18; they were rows of a zebra table).
 *
 * Each block is three things, and they are kept apart on purpose:
 * - the LABEL, as the block's heading;
 * - the VALUES, as their own element — where a later module hooks "open this project / task" in;
 * - at the heading's end, a separate CHANGE control (a pencil), only where the slot is editable and
 *   PQFORCE lets this person link (see linkRows()).
 *
 * So "open the linked object" and "change the link" never compete for one tap — which matters the
 * day a projects module makes the PPM link both openable and changeable.
 */ function $817c29ac222633b5$var$factsTable(todo, state, actions) {
    const self = state.profile?.resourceId ?? "";
    const change = (label, onClick)=>{
        const button = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
            class: "pqm-fact-action",
            type: "button",
            "aria-label": (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewChangeLink", "Change {label}", {
                label: label
            })
        }, [
            (0, $62a79e86ee9eca2d$export$1ca1ec8b29a4ce27)("pencil", 16)
        ]);
        (0, $afadd878e68b56c8$export$2385a24977818dd0)(button, "click", onClick);
        return button;
    };
    const row = (label, value, tail)=>$817c29ac222633b5$var$factBlock(label, value, tail);
    const rows = (0, $0a55aae61f0d5dc0$export$c54a0983716bc386)(todo).map(({ slot: slot, editable: editable })=>{
        switch(slot){
            case "ppm":
                return $817c29ac222633b5$var$ppmFact(todo);
            case "related":
                // ⚠ A SECOND ROW, never merged into the first. A TODO may belong to a task AND to a
                // risk — the PPM link answers "where does this sit", these "what is it about".
                return row((0, $498477530f916680$export$625550452a3fa3ec)("mobileViewRelatedTo", "Related to"), (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                    class: "pqm-fact-val"
                }, (todo.related ?? []).map($817c29ac222633b5$var$relatedEntry)), []);
            case "assignee":
                {
                    // The tenant's own name for each role wins (relationNames.ts); the app's word otherwise.
                    const label = (0, $0a4952cb2d3e704b$export$8cfece6d5e778803)("assignee", todo.assignees.length > 1 ? (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewAssignees", "Assignees") : (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewAssignee", "Assignee"));
                    const tail = [];
                    // Reminding the assignees: offered only with somebody to remind (every assignee but
                    // you), and only to somebody allowed to look after the TODO — the server refuses
                    // the rest, and a button that can only be refused is worse than none.
                    const targets = (0, $0a55aae61f0d5dc0$export$293dc77768dec4fd)(todo, self);
                    if (todo.permissions?.update === true && targets.length) {
                        const bell = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
                            class: "pqm-fact-action",
                            type: "button",
                            "aria-label": (0, $0a55aae61f0d5dc0$export$77cf39232a7a6ce8)(targets)
                        }, [
                            (0, $62a79e86ee9eca2d$export$1ca1ec8b29a4ce27)("bell", 16)
                        ]);
                        (0, $afadd878e68b56c8$export$2385a24977818dd0)(bell, "click", actions.nudge);
                        tail.push(bell);
                    }
                    if (editable) tail.push(change(label, actions.openAssigneesSheet));
                    return row(label, (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                        class: "pqm-fact-val",
                        text: (0, $0a55aae61f0d5dc0$export$c6f240c4f4db706a)(todo.assignees, self) || "\u2014"
                    }), tail);
                }
            case "owner":
                {
                    const label = (0, $0a4952cb2d3e704b$export$8cfece6d5e778803)("owner", todo.owners.length > 1 ? (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewOwners", "Owners") : (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewOwner", "Owner"));
                    // ⚠ The reader reads as "You" in BOTH people rows — see namesWithSelf().
                    return row(label, (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                        class: "pqm-fact-val",
                        text: (0, $0a55aae61f0d5dc0$export$c6f240c4f4db706a)(todo.owners, self) || "\u2014"
                    }), editable ? [
                        change(label, actions.openOwnerSheet)
                    ] : []);
                }
        }
    });
    return (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-facts"
    }, rows);
}
/** One risk or report: the same chip-and-context shape as the PPM link, so they read alike. */ function $817c29ac222633b5$var$relatedEntry(link) {
    const children = [
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-ppm-head"
        }, [
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
                class: "pqm-ppm-chip",
                text: (0, $0a55aae61f0d5dc0$export$53ca5286fdb64f67)(link.kind)
            }),
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
                class: "pqm-ppm-name",
                text: link.name
            })
        ])
    ];
    const context = (0, $0a55aae61f0d5dc0$export$fc10d3c2823c1c52)(link);
    if (context) children.push((0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-ppm-context",
        text: context
    }));
    return (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-related-entry"
    }, children);
}
/**
 * The one "Linked to" row: a neutral kind chip, the name, and the levels above it underneath.
 *
 * ⚠ NOT two rows. A TODO has one link into PPM, and a Project row beside a Task row invites the
 * reading that it has both — which is what the previous pair did, with an em dash in whichever of
 * them did not apply.
 *
 * ⚠ Nothing linked is a SENTENCE, not an empty value. "—" in a fact row reads as a value that
 * failed to load; standing on its own is a real and ordinary state.
 */ /** One fact as a block: its heading with the controls at the end, then the values. */ function $817c29ac222633b5$var$factBlock(label, value, tail = []) {
    return (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-section pqm-fact"
    }, [
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-section-head"
        }, [
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                class: "pqm-kicker pqm-fact-key",
                text: label
            }),
            ...tail.length ? [
                (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                    class: "pqm-fact-actions"
                }, tail)
            ] : []
        ]),
        value
    ]);
}
function $817c29ac222633b5$var$ppmFact(todo) {
    const ppm = todo.ppm ?? null;
    const label = (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewLinkedTo", "Linked to");
    if (!ppm) return $817c29ac222633b5$var$factBlock(label, (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-fact-val pqm-ppm-none",
        text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewNothingLinked", "Nothing \u2014 this TODO stands on its own")
    }));
    const head = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-ppm-head"
    }, [
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
            class: "pqm-ppm-chip",
            text: (0, $0a55aae61f0d5dc0$export$13ed3280aeacbdb1)(ppm.kind)
        }),
        // A link nothing could be named for still says it is a link — the chip carries that — so
        // the name may legitimately be empty here.
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
            class: "pqm-ppm-name",
            text: ppm.name
        })
    ]);
    const value = [
        head
    ];
    const context = (0, $0a55aae61f0d5dc0$export$f742eb0bd8e19d1c)(ppm);
    if (context) value.push((0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-ppm-context",
        text: context
    }));
    return $817c29ac222633b5$var$factBlock(label, (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-fact-val pqm-ppm"
    }, value));
}
function $817c29ac222633b5$var$activitySection(todo, state, actions) {
    // ⚠ THE RIGHT TO COMMENT IS THE RIGHT TO FIRE THE SELF-TRANSITION. The API returns only
    // transitions this user may fire, so its absence is a permission fact — the log is read-only
    // and says who is holding the item, rather than offering a field whose write the server would
    // refuse with a 422 about the installation's configuration.
    const selfMove = (0, $0a55aae61f0d5dc0$export$9dfebb7ead965eaa)(todo);
    if (!selfMove) return (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-section"
    }, [
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-kicker",
            text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewActivity", "Activity")
        }),
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-note"
        }, [
            (0, $62a79e86ee9eca2d$export$1ca1ec8b29a4ce27)("warningCircle", 15, "var(--color-ink-5)"),
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                class: "pqm-note-text",
                text: (0, $0a55aae61f0d5dc0$export$7e2424d55072c6e6)(todo)
            })
        ]),
        ...$817c29ac222633b5$var$activityLog(todo)
    ]);
    const placeholder = (0, $0a55aae61f0d5dc0$export$420aa3698f8d9bbc)(selfMove);
    const input = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("input", {
        class: "input",
        style: "min-height:44px",
        type: "text",
        placeholder: placeholder,
        "aria-label": placeholder
    });
    const send = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
        class: "btn btn-primary",
        style: "min-height:44px",
        type: "button",
        text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewSend", "Send"),
        disabled: true
    });
    // Enable/disable in place. Writing the draft into the store on every keystroke
    // would re-render this screen and take the caret with it.
    const sync = ()=>{
        send.disabled = state.commentBusy || input.value.trim().length === 0;
    };
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(input, "input", sync);
    sync();
    const submit = ()=>{
        const text = input.value.trim();
        if (!text) return;
        input.value = "";
        sync();
        actions.sendComment(text);
    };
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(send, "click", submit);
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(input, "keydown", (event)=>{
        if (event.key === "Enter") submit();
    });
    const children = [
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-kicker",
            text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewActivity", "Activity")
        }),
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-comment-form"
        }, [
            input,
            send
        ])
    ];
    children.push(...$817c29ac222633b5$var$activityLog(todo));
    return (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-activity"
    }, children);
}
/**
 * The log itself — shared by both branches of activitySection.
 *
 * Extracted when the comment field became conditional: a read-only log is still a log, and the
 * entries must render identically whether or not this user may add to them. Returns an array so a
 * log with no entries contributes nothing rather than an empty box.
 */ function $817c29ac222633b5$var$activityLog(todo) {
    const comments = (0, $0a55aae61f0d5dc0$export$6d9937f48401e58a)(todo.comments);
    if (comments.length === 0) return [];
    return [
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-comments-list"
        }, comments.map((comment)=>{
            // One line: what the entry did, who did it, when. A state change shows the state it
            // MOVED TO, where the person's avatar used to stand; a plain comment a speech bubble.
            // ⚠ THE ENTRY'S OWN STATE, not the item's: the endpoint is the tenant's state at the
            // time, and painting it with the todo's CURRENT state made every past step wear
            // today's colour. An endpoint with no colour or category gets the neutral chip;
            // nothing is ever derived from the word.
            const mark = (0, $0a55aae61f0d5dc0$export$64474058fdddf6fa)(comment) ? (0, $98524becae549989$export$7c7b858b9f86c22a)({
                status: comment.to,
                statusCategory: comment.toCategory,
                statusColor: comment.toColor,
                statusIcon: comment.toIcon
            }, "sm") : (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
                class: "pqm-comment-mark",
                "aria-hidden": "true"
            }, [
                (0, $62a79e86ee9eca2d$export$1ca1ec8b29a4ce27)("chat", 16, "var(--color-ink-6)")
            ]);
            const entry = [
                (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                    class: "pqm-comment-head"
                }, [
                    mark,
                    (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                        class: "pqm-comment-who",
                        text: comment.who
                    }),
                    (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                        class: "pqm-comment-when",
                        text: (0, $0a55aae61f0d5dc0$export$a12a26c5f76005ed)(comment.ts)
                    })
                ])
            ];
            if (comment.text) // ⚠ HTML, not text — the remark field is rich text and the server has already
            // scrubbed it (pqfMobileRemarkHtml). `richTextHtml` scrubs it again on insert.
            entry.push((0, $afadd878e68b56c8$export$b563501506ada1a0)(comment.text, "pqm-richtext pqm-comment-text"));
            return (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                class: "pqm-comment"
            }, entry);
        }))
    ];
}
/**
 * The three facts that DESCRIBE the TODO — status, due date, priority — in one row, each shown ONCE
 * and changed where it is shown. They used to appear twice: in a line under the title, and again
 * in a section of their own with a "Change…" button.
 *
 * Who may change what is PQFORCE's answer, not the relation (`Todo.permissions`):
 * - the STATUS opens the status sheet when there is a move to make — `transitions` are already the
 *   moves PQFORCE lets this person make, so no further permission is involved;
 * - the DUE DATE and the PRIORITY need `update`.
 *
 * A control that cannot be used is plain text, so its look already says so. While the permissions
 * are not known yet (the TODO is being read whole), due date and priority stay plain.
 *
 * Both the due date and the priority are OPTIONAL on a real tenant: "No due date" and "No priority"
 * are sentences, never a dangling "Due" or a literal "null".
 */ /** A fact that is not set, as an icon alone — its words are its accessible name and its tooltip. */ function $817c29ac222633b5$var$unsetMark(glyph, label) {
    return (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
        class: "pqm-detail-unset",
        role: "img",
        "aria-label": label,
        title: label
    }, [
        (0, $62a79e86ee9eca2d$export$1ca1ec8b29a4ce27)(glyph, 18, "currentColor")
    ]);
}
function $817c29ac222633b5$var$detailControls(todo, actions, now) {
    const mayUpdate = todo.permissions?.update === true && !todo.partial;
    const moves = todo.partial ? [] : (0, $0a55aae61f0d5dc0$export$13e2f80d35473793)(todo);
    const control = (label, content, onClick, extra = "")=>{
        if (!onClick) return (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
            class: `pqm-control pqm-control-static ${extra}`
        }, content);
        const button = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
            class: `pqm-control ${extra}`,
            type: "button",
            "aria-label": label
        }, [
            ...content,
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
                class: "pqm-control-chevron",
                "aria-hidden": "true"
            }, [
                (0, $62a79e86ee9eca2d$export$dc84183312f0c201)()
            ])
        ]);
        (0, $afadd878e68b56c8$export$2385a24977818dd0)(button, "click", onClick);
        return button;
    };
    const status = control((0, $498477530f916680$export$625550452a3fa3ec)("mobileViewChangeStatus", "Change status"), [
        (0, $98524becae549989$export$7c7b858b9f86c22a)(todo, "lg", true),
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
            class: "pqm-detail-status",
            text: todo.status
        })
    ], moves.length > 0 ? actions.openStatusSheet : null, "pqm-control-status");
    // The date alone, with its year — no "Due", no "Today": the row is about facts, not sentences.
    const due = (0, $439e2ca410399204$export$566d600030c4efae)(todo.dueISO, now);
    const dueControl = control(todo.dueISO ? (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewChangeDueDate", "Change due date") : (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewSetDueDate", "Set a due date"), [
        // No date: an empty calendar, not the words "No due date" — the row has no room for them.
        todo.dueISO ? (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
            class: `pqm-detail-due${due.overdue ? " pqm-due-overdue" : ""}`,
            text: (0, $439e2ca410399204$export$6814d6bdd416543f)(todo.dueISO)
        }) : $817c29ac222633b5$var$unsetMark("calendarBlank", (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewNoDueDate", "No due date"))
    ], mayUpdate ? ()=>{
        // The picker mounts itself over the whole screen — see openDatePicker().
        // `document.body` is the fallback for a detached render, which the tests do.
        const host = dueControl.closest(".pqm-app") ?? document.body;
        (0, $4394c6b98ca59841$export$621a48d450e82cf)(host, {
            selected: todo.dueISO,
            now: now,
            onSave: (iso)=>actions.setDueDate(iso)
        });
    } : null);
    // The same chip a list row carries, so a priority looks the same on both screens.
    const priority = control((0, $498477530f916680$export$625550452a3fa3ec)("mobileViewChangePriority", "Change priority"), [
        // No priority: an empty flag, not the words "No priority".
        $817c29ac222633b5$export$e0891a306dc0f31(todo.priority) ?? $817c29ac222633b5$var$unsetMark("flag", (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewNoPriority", "No priority"))
    ], mayUpdate ? actions.openPrioritySheet : null);
    // One line: status, due date, priority.
    const out = [
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-detail-controls"
        }, [
            status,
            dueControl,
            priority
        ])
    ];
    // No move to make: say who is holding it, in one line, rather than an unexplained plain status.
    if (!todo.partial && moves.length === 0) out.push((0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-hint pqm-detail-nomove",
        text: (0, $0a55aae61f0d5dc0$export$1963261d6999187)(todo)
    }));
    return out;
}
/**
 * The description, under a heading of its own, with a pencil to change it.
 *
 * The pencil is offered where PQFORCE lets this person update the TODO AND the app can edit this
 * description: one written with formatting (`descriptionEditable === false`) is shown, but can
 * only be changed in PQFORCE — a pencil there would open a sheet that says exactly that. An empty
 * description is a section only for somebody who may write one ("No description" and the pencil);
 * for anybody else there is nothing to show.
 */ function $817c29ac222633b5$var$descriptionSection(todo, actions) {
    const editable = todo.permissions?.update === true && !todo.partial && (!todo.description || todo.descriptionEditable !== false);
    if (!todo.description && !editable) return null;
    const label = (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetTodoDescription", "Description");
    const head = [
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-kicker",
            text: label
        })
    ];
    if (editable) {
        const edit = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
            class: "pqm-title-edit",
            type: "button",
            "aria-label": (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewEditDescription", "Edit description")
        }, [
            (0, $62a79e86ee9eca2d$export$1ca1ec8b29a4ce27)("pencil", 16)
        ]);
        (0, $afadd878e68b56c8$export$2385a24977818dd0)(edit, "click", actions.openDetailsSheet);
        head.push(edit);
    }
    return (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-section pqm-detail-desc-section"
    }, [
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-section-head"
        }, head),
        todo.description ? // own with a `<!-- pqf-html-v2 -->` sentinel. The server scrubbed it; this scrubs again.
        (0, $afadd878e68b56c8$export$b563501506ada1a0)(todo.description, "pqm-richtext pqm-detail-desc") : (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-detail-desc pqm-detail-desc-none",
            text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewNoDescription", "No description")
        })
    ]);
}
function $817c29ac222633b5$export$83faea0403bc9d12(todo, state, actions, now = Date.now()) {
    // The title, and beside it the way to change it and the description — PQFORCE's `update`.
    const title = [
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-h pqm-detail-title",
            text: todo.title
        })
    ];
    if (todo.permissions?.update === true && !todo.partial) {
        const edit = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
            class: "pqm-title-edit",
            type: "button",
            "aria-label": (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewEditTitle", "Edit title and description")
        }, [
            (0, $62a79e86ee9eca2d$export$1ca1ec8b29a4ce27)("pencil", 18)
        ]);
        (0, $afadd878e68b56c8$export$2385a24977818dd0)(edit, "click", actions.openDetailsSheet);
        title.push(edit);
    }
    // While the TODO is being read whole (GET todos/{id}) — always for a row from "All TODOs", which
    // carries no activity — the body stays EMPTY: no title and state from the list row that are about
    // to be replaced; the screen fills in once, when everything has arrived.
    const loading = todo.partial === true || state.todoLoading === todo.id;
    const body = [];
    // Empty while loading: the busy chip over the screen says so (the shell draws it).
    if (!loading) {
        body.push((0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-section"
        }, [
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                class: "pqm-title-row"
            }, title),
            ...$817c29ac222633b5$var$detailControls(todo, actions, now)
        ]));
        // The description first, right under the controls — what the TODO is about — then its links.
        const description = $817c29ac222633b5$var$descriptionSection(todo, actions);
        if (description) body.push(description);
        body.push($817c29ac222633b5$var$factsTable(todo, state, actions));
        body.push($817c29ac222633b5$var$activitySection(todo, state, actions));
    }
    // At the very bottom, apart from everything else: deleting, where PQFORCE grants it.
    if (!loading && todo.permissions?.delete === true && !todo.partial) {
        const remove = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
            class: "btn btn-secondary btn-block pqm-detail-delete",
            type: "button"
        }, [
            (0, $62a79e86ee9eca2d$export$1ca1ec8b29a4ce27)("trash", 18),
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
                text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewDeleteTodo", "Delete TODO")
            })
        ]);
        (0, $afadd878e68b56c8$export$2385a24977818dd0)(remove, "click", actions.openDeleteSheet);
        body.push(remove);
    }
    // The header repeats the title in one line: the big one below scrolls away, and a long TODO
    // has to stay identifiable while its comments are being read.
    return [
        $817c29ac222633b5$export$da2a053ec9c45f2a(state, {
            title: todo.title,
            code: todo.code,
            onBack: actions.back,
            actions: [
                $817c29ac222633b5$var$searchButton(state, actions)
            ]
        }),
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-scroll pqm-detail-body"
        }, body)
    ];
}
function $817c29ac222633b5$export$465592710b708c6f(state, actions) {
    return [
        $817c29ac222633b5$export$da2a053ec9c45f2a(state, {
            title: "",
            onBack: actions.back,
            actions: [
                $817c29ac222633b5$var$searchButton(state, actions)
            ]
        }),
        // Empty: the shell's busy chip says "Loading…".
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-scroll pqm-detail-body"
        }, [])
    ];
}



/**
 * The scrim + panel.
 *
 * A click on the scrim itself closes; a click inside the panel does not. Escape
 * closes too — this app runs on phones but is a web page, and a keyboard user
 * must not be trapped in a sheet.
 */ function $4293432b28b7e6e8$var$sheet(title, children, onClose, high = false, /**
     * What closing MEANS, for the close cross's accessible name: "Cancel" where closing discards a
     * draft; "Done" where every change was already saved as it was made (the assignees, the
     * account), so there is nothing left to cancel.
     */ closeLabel = "Cancel") {
    // A CROSS, not a word (2026-10-18): one shape for "close this card" on every sheet, and no word
    // taking a third of the title row. Its name still says what closing does.
    const cancel = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
        class: "pqm-appbar-icon pqm-sheet-close",
        type: "button",
        "aria-label": closeLabel === "Done" ? (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetDone", "Done") : (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetCancel", "Cancel")
    }, [
        (0, $62a79e86ee9eca2d$export$1ca1ec8b29a4ce27)("x", 18)
    ]);
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(cancel, "click", onClose);
    const panel = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-sheet",
        role: "dialog",
        "aria-modal": "true",
        "aria-label": title
    }, [
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-sheet-head"
        }, [
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                class: "pqm-h pqm-sheet-title",
                text: title
            }),
            cancel
        ]),
        ...children
    ]);
    const scrim = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: `pqm-scrim${high ? " pqm-scrim-high" : ""}`
    }, [
        panel
    ]);
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(scrim, "click", (event)=>{
        if (event.target === scrim) onClose();
    });
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(scrim, "keydown", (event)=>{
        if (event.key === "Escape") onClose();
    });
    // Focusable so Escape reaches it even before anything inside is focused. Focusing it is the
    // SHELL's job, and only when the sheet opens — see `steadyOverlays()` in ui_mobile_shell.ts:
    // this function runs on every repaint, and focusing here stole focus on each one.
    scrim.tabIndex = -1;
    return scrim;
}
/** A state as circle AND word — the sheet is where somebody checks what they are about to do. */ function $4293432b28b7e6e8$var$namedState(state) {
    return (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
        class: "pqm-named-state"
    }, [
        (0, $98524becae549989$export$7c7b858b9f86c22a)(state, "sm", true),
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
            text: state.status
        })
    ]);
}
/**
 * A list of options where at most ONE is picked, and nothing is picked until somebody taps.
 *
 * The pick lives in the sheet — `aria-pressed` on the rows, and `onPick` telling the sheet — not in
 * the store: writing it there would repaint the sheet and throw away a comment being typed.
 */ function $4293432b28b7e6e8$var$pickList(options, picked, onPick) {
    const rows = options.map((option)=>{
        const row = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
            class: "pqm-pick pqm-pick-row pqm-pick-option",
            type: "button",
            "aria-pressed": option.value === picked ? "true" : "false",
            "aria-label": option.label
        }, option.content);
        (0, $afadd878e68b56c8$export$2385a24977818dd0)(row, "click", ()=>{
            for (const other of rows)other.setAttribute("aria-pressed", other === row ? "true" : "false");
            onPick(option.value);
        });
        return row;
    });
    return (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-picks-col",
        role: "group"
    }, rows);
}
/** "Currently: …" above a sheet's options, so the choice is made knowing where it starts. */ function $4293432b28b7e6e8$var$currently(value) {
    return (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-currently"
    }, [
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
            class: "pqm-currently-label",
            text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetCurrently", "Currently")
        }),
        value
    ]);
}
/**
 * Change the status: choose the target, then confirm.
 *
 * ⚠ TWO STEPS, ON PURPOSE. The detail screen used to show every target as a button, and a stray tap
 * on a phone was a move. Here nothing is preselected — the Confirm button says "Choose a status"
 * and is disabled until a target is picked — and only Confirm fires. A move the tenant refuses
 * keeps the sheet open with the pick and the comment intact (both ride in the draft).
 *
 * The targets are the MOVES the API offers this person, each as the state it leads to — never a
 * transition's own name (see transitionLabel()). The self-transition is not here: it is the
 * comment field on the detail screen.
 */ function $4293432b28b7e6e8$var$statusSheet(todo, state, actions) {
    const draft = state.transitionDraft;
    const moves = (0, $0a55aae61f0d5dc0$export$13e2f80d35473793)(todo);
    let picked = draft.transition ? moves.find((m)=>m.id === draft.transition.id) ?? null : null;
    const note = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("textarea", {
        class: "input",
        style: "min-height:88px",
        placeholder: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetCommentPlaceholder", "What should the others know?"),
        "aria-label": (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetComment", "Comment")
    });
    // The draft carries the note across a re-render, so a refused move does not also throw away the
    // reason the user just typed.
    note.value = draft.note;
    const confirm = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
        class: "btn btn-primary btn-block pqm-move pqm-confirm",
        style: "min-height:48px",
        type: "button"
    });
    // The confirm button says what it does once there is a target — "Set to <state>", with the
    // state's circle as the list will paint it — and what is missing until then. A note is always optional.
    const paintConfirm = ()=>{
        confirm.disabled = draft.busy || picked === null;
        if (picked) confirm.replaceChildren((0, $98524becae549989$export$7c7b858b9f86c22a)((0, $98524becae549989$export$8614a336e4173002)(picked), "sm", true), (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
            text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetSetTo", "Set to {state}", {
                state: (0, $0a55aae61f0d5dc0$export$798a7b69c8458ebd)(picked)
            })
        }));
        else confirm.replaceChildren((0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
            text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetChooseStatus", "Choose a status")
        }));
    };
    paintConfirm();
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(confirm, "click", ()=>{
        if (!picked || confirm.disabled) return;
        confirm.disabled = true;
        actions.confirmTransition(picked, (0, $0a55aae61f0d5dc0$export$798a7b69c8458ebd)(picked), note.value);
    });
    const options = $4293432b28b7e6e8$var$pickList(moves.map((move)=>({
            value: move,
            label: (0, $0a55aae61f0d5dc0$export$798a7b69c8458ebd)(move),
            content: [
                (0, $98524becae549989$export$7c7b858b9f86c22a)((0, $98524becae549989$export$8614a336e4173002)(move), "sm", true),
                (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
                    class: "pqm-move-label",
                    text: (0, $0a55aae61f0d5dc0$export$798a7b69c8458ebd)(move)
                })
            ]
        })), picked, (move)=>{
        picked = move;
        paintConfirm();
    });
    const children = [
        $4293432b28b7e6e8$var$currently($4293432b28b7e6e8$var$namedState(todo)),
        options,
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "field"
        }, [
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("label", {
                text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetComment", "Comment")
            }),
            note,
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                class: "pqm-hint",
                style: "padding-top:6px",
                text: (0, $0a55aae61f0d5dc0$export$e7e86996b7d67fe9)()
            })
        ])
    ];
    if (draft.error) children.push((0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-note"
    }, [
        (0, $62a79e86ee9eca2d$export$1ca1ec8b29a4ce27)("warningCircle", 15, "var(--color-accent-650)"),
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-note-text",
            text: draft.error
        })
    ]));
    children.push(confirm);
    return $4293432b28b7e6e8$var$sheet((0, $498477530f916680$export$625550452a3fa3ec)("mobileViewChangeStatus", "Change status"), children, actions.closeSheet, true);
}
/**
 * Change the priority: choose, then confirm. Owner only — the server refuses anyone else.
 *
 * Nothing is preselected; the current value is stated above the options instead, and picking it
 * leaves Confirm disabled, since sending it would change nothing. The pick lives in the STORE
 * (`priorityPick`, reset when the sheet opens), so the repaints a change causes keep it: the sheet
 * closes when the server confirms, and stays open with the pick when it refuses — see setPriority().
 */ function $4293432b28b7e6e8$var$prioritySheet(todo, state, actions) {
    const current = (0, $2336e268632bd98b$export$29a85ef5d4ea90eb)(todo.priority)?.id ?? "";
    const picked = state.priorityPick;
    const confirm = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
        class: "btn btn-primary btn-block pqm-confirm",
        style: "min-height:48px",
        type: "button"
    });
    const paintConfirm = ()=>{
        confirm.disabled = state.priorityBusy || picked === null || picked === current;
        confirm.textContent = picked === null || picked === current ? (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetChoosePriority", "Choose a priority") : picked === "" ? (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetRemovePriority", "Remove priority") : (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetSetPriority", "Set to {priority}", {
            priority: (0, $2336e268632bd98b$export$bf7f685c4fbf1078)(picked)
        });
    };
    paintConfirm();
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(confirm, "click", ()=>{
        if (picked === null || confirm.disabled) return;
        confirm.disabled = true;
        actions.setPriority(picked);
    });
    const dot = (color)=>(0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
            class: "pqm-prio-dot",
            style: `background:${color}`,
            "aria-hidden": "true"
        });
    const options = $4293432b28b7e6e8$var$pickList([
        {
            value: "",
            label: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetNoPriority", "None"),
            content: [
                (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
                    text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetNoPriority", "None")
                })
            ]
        },
        ...(0, $2336e268632bd98b$export$6a1b098444ac7587).map((p)=>({
                value: p.id,
                label: (0, $2336e268632bd98b$export$bf7f685c4fbf1078)(p.id),
                content: [
                    dot(p.color),
                    (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
                        text: (0, $2336e268632bd98b$export$bf7f685c4fbf1078)(p.id)
                    })
                ]
            }))
    ], picked, (value)=>actions.pickPriority(value));
    return $4293432b28b7e6e8$var$sheet((0, $498477530f916680$export$625550452a3fa3ec)("mobileViewChangePriority", "Change priority"), [
        $4293432b28b7e6e8$var$currently((0, $817c29ac222633b5$export$e0891a306dc0f31)(todo.priority) ?? (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
            class: "pqm-change-value",
            text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetNoPriority", "None")
        })),
        options,
        confirm
    ], actions.closeSheet);
}
/**
 * The assignees: who has this TODO, and who could be given it too.
 *
 * ⚠ ONE PERSON PER TAP. A TODO may have several assignees, so there is no "reassign" — each row adds
 * or removes exactly that person and leaves everybody else as they were. Each tap is saved at once
 * and the sheet stays open, because changing two people is an ordinary thing to want.
 *
 * While one change is in flight every button is disabled: each answers with the whole TODO, and a
 * second tap before the first returns would repaint over it.
 */ /**
 * What is typed into the assignees sheet's search, across repaints.
 *
 * ⚠ NOT IN THE STORE. Every store update repaints the whole app, so writing each keystroke there
 * would rebuild the field under the caret. The rows are filtered IN PLACE instead, and this is only
 * read back when something ELSE repaints the sheet — the "…" on a button, the answer, the toast after
 * an add. Without it one tap cleared the search the person had just used to find somebody. Reset
 * whenever the sheet is not open, so it opens empty the next time.
 */ let $4293432b28b7e6e8$var$assigneeQuery = "";
/** The title and description being typed in the details sheet — see detailsSheet(). */ let $4293432b28b7e6e8$var$detailsDraft = null;
function $4293432b28b7e6e8$var$assigneesSheet(todo, state, actions) {
    const { assigned: assigned, others: others } = (0, $0a55aae61f0d5dc0$export$b2f7348c8e4be141)(todo, state.createOptions?.people ?? []);
    const busy = state.assigneeBusy;
    const row = (person, verb, onClick)=>{
        const name = person.name || (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetUnnamedResource", "Unnamed resource");
        const label = verb === "Add" ? (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetAdd", "Add") : (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetRemove", "Remove");
        const button = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
            class: `btn ${verb === "Add" ? "btn-secondary" : "btn-ghost"} pqm-assignee-action`,
            type: "button",
            text: busy === person.id ? "\u2026" : label,
            "aria-label": verb === "Add" ? (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetAddPerson", "Add {name}", {
                name: name
            }) : (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetRemovePerson", "Remove {name}", {
                name: name
            })
        });
        button.disabled = busy !== null;
        (0, $afadd878e68b56c8$export$2385a24977818dd0)(button, "click", onClick);
        return (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-assignee-row"
        }, [
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                class: "pqm-avatar pqm-avatar-md",
                text: $4293432b28b7e6e8$var$initials(name),
                "aria-hidden": "true"
            }),
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                class: "pqm-assignee-name",
                text: name
            }),
            button
        ]);
    };
    const children = [
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-kicker",
            text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetAssigned", "Assigned")
        }),
        assigned.length ? (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-picks-col"
        }, assigned.map((a)=>row(a, "Remove", ()=>actions.unassign(a.id)))) : (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-empty",
            text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetNobodyAssigned", "Nobody is assigned.")
        }),
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-kicker",
            text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetAdd", "Add")
        }),
        others.length ? $4293432b28b7e6e8$var$addList(others.map((p)=>({
                name: p.name,
                row: row(p, "Add", ()=>actions.assign(p.id))
            }))) : (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-empty",
            text: state.createOptions ? (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetNobodyElse", "Nobody else can be assigned.") : (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetLoadingPeople", "Loading who can be assigned\u2026")
        }),
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-hint",
            text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetAssigneesHint", "Each change is saved straight away. The status and the comment history stay as they are.")
        })
    ];
    return $4293432b28b7e6e8$var$sheet((0, $0a4952cb2d3e704b$export$8cfece6d5e778803)("assignee", (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetAssignees", "Assignees")), children, actions.closeSheet, true, "Done");
}
/**
 * The "Add" list with its search field above it. Only this list is searched: "Assigned" is a
 * handful of people, and hiding one of them behind a query would read as having removed them.
 *
 * ⚠ The field is rebuilt on every repaint like everything else, so a field that HAD focus gets it
 * back — otherwise the toast after an add, repainting a second later, would close the phone's
 * keyboard in the middle of the next word. It is deliberately not focused on OPENING: the keyboard
 * would cover the list the person came to read.
 */ function $4293432b28b7e6e8$var$addList(entries) {
    const hadFocus = document.activeElement?.classList.contains("pqm-assignee-search") ?? false;
    const label = (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetSearchPeople", "Search by name");
    const search = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("input", {
        class: "input pqm-assignee-search",
        type: "search",
        placeholder: label,
        "aria-label": label,
        autocomplete: "off",
        enterkeyhint: "search"
    });
    search.value = $4293432b28b7e6e8$var$assigneeQuery;
    const none = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-empty"
    });
    const list = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-picks-col"
    }, entries.map((e)=>e.row));
    const apply = ()=>{
        let shown = 0;
        for (const entry of entries){
            const match = (0, $0a55aae61f0d5dc0$export$1ad433f9ad7133a0)(entry.name, $4293432b28b7e6e8$var$assigneeQuery);
            entry.row.hidden = !match;
            if (match) shown++;
        }
        none.hidden = shown > 0;
        none.textContent = (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetNoPersonMatches", "Nobody matches \u201C{query}\u201D.", {
            query: $4293432b28b7e6e8$var$assigneeQuery.trim()
        });
    };
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(search, "input", ()=>{
        $4293432b28b7e6e8$var$assigneeQuery = search.value;
        apply();
    });
    apply();
    if (hadFocus) window.setTimeout(()=>{
        search.focus();
        search.setSelectionRange(search.value.length, search.value.length);
    }, 0);
    return (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-assignee-add"
    }, [
        search,
        list,
        none
    ]);
}
function $4293432b28b7e6e8$var$initials(name) {
    return name.split(/\s+/).filter(Boolean).map((w)=>w[0]).join("").slice(0, 2).toUpperCase();
}
/**
 * A form field whose options fold away under a header: its label, the CURRENT choice, and a chevron.
 *
 * ⚠ STARTS FOLDED. The project and people lists can run to dozens, and open they pushed the due
 * date, the priority and the Create button off a phone's screen. The header states the choice, so
 * a folded field still says what will be sent — nothing is hidden that the form is about to use.
 *
 * Opened, the list has a SEARCH field above it, focused, filtering the options by their
 * `data-name` with the app's one matching rule (`personMatchesQuery()`: every word, any order, case
 * and accents ignored). An option marked `data-always` — "No project", "Nobody yet" — is never
 * filtered out: it is the way back, and must not be something a query can hide.
 *
 * Returns the field, `close()` (a pick calls it: choosing is the end of the job, and it clears the
 * search so the list opens whole next time) and `show()` for the header's value.
 */ function $4293432b28b7e6e8$var$collapsibleField(label, options, search) {
    const value = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
        class: "pqm-fold-value"
    });
    const head = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
        class: "pqm-fold-head pqm-group-collapsed",
        type: "button",
        "aria-expanded": "false"
    }, [
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
            class: "pqm-fold-label",
            text: label
        }),
        value,
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
            class: "pqm-group-chevron"
        }, [
            (0, $62a79e86ee9eca2d$export$dc84183312f0c201)()
        ])
    ]);
    const input = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("input", {
        class: "input pqm-fold-search",
        type: "search",
        placeholder: search.placeholder,
        "aria-label": search.placeholder,
        autocomplete: "off",
        enterkeyhint: "search"
    });
    const none = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-empty pqm-fold-none"
    });
    const body = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-fold-body"
    }, [
        input,
        options,
        none
    ]);
    body.hidden = true;
    const filter = ()=>{
        let shown = 0;
        for (const option of Array.from(options.querySelectorAll("[data-name]"))){
            const match = option.hasAttribute("data-always") || (0, $0a55aae61f0d5dc0$export$1ad433f9ad7133a0)(option.dataset.name ?? "", input.value);
            option.hidden = !match;
            if (match && !option.hasAttribute("data-always")) shown++;
        }
        const query = input.value.trim();
        none.hidden = query === "" || shown > 0;
        none.textContent = query ? search.noMatch(query) : "";
    };
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(input, "input", filter);
    const setOpen = (open)=>{
        body.hidden = !open;
        head.setAttribute("aria-expanded", open ? "true" : "false");
        head.classList.toggle("pqm-group-collapsed", !open);
        if (open) input.focus();
    };
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(head, "click", ()=>setOpen(body.hidden));
    filter();
    return {
        field: (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "field pqm-fold"
        }, [
            head,
            body
        ]),
        close: ()=>{
            input.value = "";
            filter();
            setOpen(false);
        },
        show: (choice)=>{
            value.textContent = choice;
        }
    };
}
/** Create a TODO. It starts in the lifecycle's own initial state, not one named here. */ function $4293432b28b7e6e8$var$newTodoSheet(state, actions) {
    const options = state.createOptions;
    const projects = options?.projects ?? [];
    const people = options?.people ?? [];
    // ⚠ NOTHING IS PRESELECTED — no project, no assignee, like the due date and the priority below.
    // Taking the first of each filed every new TODO under whichever project and person the list
    // happened to start with, whether or not anybody meant to. Both are optional: a TODO may belong to
    // no project and be assigned to nobody yet — PQFORCE accepts both, so only the title is required.
    const draft = {
        // Empty — unless the sheet was opened from a project group's +, which names its project.
        projectId: state.newTodoProjectId ?? "",
        assigneeId: "",
        // ⚠ A DATE, and empty by default. The four labels this replaced —
        // Today / Tomorrow / This week / Later — could not express any other day,
        // and "This week" as a default dated every new TODO whether or not
        // anybody meant to. Nothing is due until somebody says so.
        dueISO: "",
        // ⚠ NONE by default, like the due date: most TODOs carry no priority, and a default would
        // set one on every TODO whether or not anybody meant to.
        priority: ""
    };
    const title = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("input", {
        class: "input",
        style: "min-height:46px",
        type: "text",
        placeholder: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetTitlePlaceholder", "e.g. Confirm night tariff rounding"),
        "aria-label": (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetWhatNeedsDoing", "What needs doing?")
    });
    /** A group of pick buttons where exactly one is selected. */ const pickGroup = (items, current, set, cls, render)=>items.map((item)=>{
            const button = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
                class: `pqm-pick ${cls}`,
                type: "button",
                "aria-pressed": current() === item.id ? "true" : "false",
                // What a folding field's search matches, and the "none" entry it never hides.
                "data-name": item.name,
                "data-always": item.id === "" ? true : null
            }, render ? render(item) : [
                document.createTextNode(item.name)
            ]);
            (0, $afadd878e68b56c8$export$2385a24977818dd0)(button, "click", ()=>{
                set(item.id);
                const parent = button.parentElement;
                if (parent) for (const sibling of Array.from(parent.children))sibling.setAttribute("aria-pressed", sibling === button ? "true" : "false");
            });
            return button;
        });
    // "No project" / "Nobody yet" first: the start, and the only way back once something is picked.
    const projectOptions = [
        {
            id: "",
            name: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetNoProject", "No project")
        },
        ...projects
    ];
    const assigneeOptions = [
        {
            id: "",
            name: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetNoAssignee", "Nobody yet")
        },
        ...people
    ];
    const nameOf = (options, id)=>options.find((option)=>option.id === id)?.name ?? "";
    const projectRow = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-picks-col"
    }, pickGroup(projectOptions, ()=>draft.projectId, (id)=>{
        draft.projectId = id;
        projectField.show(nameOf(projectOptions, id));
        projectField.close();
    }, ""));
    const assigneeRow = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-picks-row"
    }, pickGroup(assigneeOptions, ()=>draft.assigneeId, (id)=>{
        draft.assigneeId = id;
        assigneeField.show(nameOf(assigneeOptions, id));
        assigneeField.close();
    }, "pqm-pick-person", (person)=>[
            // No avatar for "Nobody yet": initials of that phrase would read as a person.
            ...person.id ? [
                (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
                    class: "pqm-avatar pqm-avatar-sm",
                    text: $4293432b28b7e6e8$var$initials(person.name),
                    "aria-hidden": "true"
                })
            ] : [],
            document.createTextNode(person.name)
        ]));
    // Folded under their current choice — see collapsibleField(). Built after the rows, which they
    // wrap; the rows' pick handlers only reach them on a tap, by which time both exist.
    const projectField = $4293432b28b7e6e8$var$collapsibleField((0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetProject", "Project"), projectRow, {
        placeholder: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetSearchProjects", "Search projects"),
        noMatch: (query)=>(0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetNoProjectMatches", "No project matches \u201C{query}\u201D.", {
                query: query
            })
    });
    projectField.show(nameOf(projectOptions, draft.projectId));
    const assigneeField = $4293432b28b7e6e8$var$collapsibleField((0, $0a4952cb2d3e704b$export$8cfece6d5e778803)("assignee", (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetAssignee", "Assignee")), assigneeRow, {
        placeholder: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetSearchPeople", "Search by name"),
        noMatch: (query)=>(0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetNoPersonMatches", "Nobody matches \u201C{query}\u201D.", {
                query: query
            })
    });
    assigneeField.show(nameOf(assigneeOptions, draft.assigneeId));
    /**
     * One button that opens the picker, rather than a row of chips.
     *
     * ⚠ The picker MOUNTS ITSELF over this sheet and changes no state, which is what lets the
     * half-written todo survive it — see `openDatePicker()`. Routing it through the store would
     * rebuild this sheet and empty the title field.
     */ const dueButton = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
        class: "pqm-pick pqm-pick-chip pqm-due-button",
        type: "button"
    });
    const paintDue = ()=>{
        dueButton.textContent = draft.dueISO ? (0, $439e2ca410399204$export$566d600030c4efae)(draft.dueISO, Date.now()).label : (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetNoDueDate", "No due date");
    };
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(dueButton, "click", ()=>{
        const host = dueButton.closest(".pqm-scrim")?.parentElement ?? document.body;
        (0, $4394c6b98ca59841$export$621a48d450e82cf)(host, {
            selected: draft.dueISO,
            now: Date.now(),
            onSave: (iso)=>{
                draft.dueISO = iso;
                paintDue();
            }
        });
    });
    paintDue();
    const dueRow = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-picks-tight"
    }, [
        dueButton
    ]);
    const priorityRow = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-picks-tight"
    }, // The PQFORCE client's own three, in its order, after "None" — which is a real answer and
    // the only way back once a chip is picked.
    pickGroup([
        {
            id: "",
            name: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetNoPriority", "None"),
            color: ""
        },
        ...(0, $2336e268632bd98b$export$6a1b098444ac7587).map((p)=>({
                id: p.id,
                name: (0, $2336e268632bd98b$export$bf7f685c4fbf1078)(p.id),
                color: p.color
            }))
    ], ()=>draft.priority, (id)=>{
        draft.priority = (0, $2336e268632bd98b$export$29a85ef5d4ea90eb)(id)?.id ?? "";
    }, "pqm-pick-chip", (option)=>[
            ...option.color ? [
                (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
                    class: "pqm-prio-dot",
                    style: `background:${option.color}`,
                    "aria-hidden": "true"
                })
            ] : [],
            document.createTextNode(option.name)
        ]));
    const create = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
        class: "btn btn-primary btn-block",
        style: "min-height:48px",
        type: "button",
        text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetCreateTodo", "Create TODO")
    });
    /** What is still missing before Create can work — said, never left to a greyed-out button. */ const missing = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-hint pqm-sheet-missing",
        role: "status"
    });
    /**
     * Create waits for a TITLE, the one thing a TODO cannot be without — and says so rather than
     * greying out silently. (It used to stay disabled for good after a tap with no title.)
     */ const refresh = ()=>{
        const noTitle = title.value.trim() === "";
        create.disabled = noTitle;
        missing.textContent = noTitle ? (0, $498477530f916680$export$625550452a3fa3ec)("mobileShellTitleMissing", "Give the TODO a title first.") : "";
        missing.hidden = noTitle === false;
    };
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(title, "input", refresh);
    refresh();
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(create, "click", ()=>{
        if (create.disabled) return;
        create.disabled = true;
        actions.createTodo({
            title: title.value.trim(),
            projectId: draft.projectId,
            assigneeId: draft.assigneeId,
            dueISO: draft.dueISO,
            priority: draft.priority
        });
    });
    return $4293432b28b7e6e8$var$sheet((0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetNewTodo", "New TODO"), [
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "field"
        }, [
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("label", {
                text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetWhatNeedsDoing", "What needs doing?")
            }),
            title
        ]),
        projectField.field,
        assigneeField.field,
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            style: "display:flex;gap:14px;flex-wrap:wrap"
        }, [
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                class: "field",
                style: "flex:1"
            }, [
                (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("label", {
                    text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetDue", "Due")
                }),
                dueRow
            ]),
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                class: "field",
                style: "flex:none"
            }, [
                (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("label", {
                    text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetPriority", "Priority")
                }),
                priorityRow
            ])
        ]),
        create,
        missing,
        // The initial state is the tenant's, so this says what happens without
        // naming a state this app does not own.
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-hint",
            text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetNewTodoHint", "It starts in the first state of your tenant's lifecycle. The assignee gets it in \u201C{list}\u201D.", {
                list: (0, $0a4952cb2d3e704b$export$38e3edeba43dc984)("assignee", (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewAssignedToMe", "Assigned to me"))
            })
        })
    ], actions.closeSheet);
}
/**
 * Who you are, where you are, and the two ways out.
 *
 * ⚠ THE ONLY ROUTE TO EITHER. Until this existed the app had no sign-out control at all — the
 * `signOut()` method was written, the route was written, and nothing called it — and no way to
 * reach another installation short of clearing the browser's site data. The tenant picker existed
 * but rendered only on the signed-out branch, which a signed-in device cannot get to.
 *
 * Reached from the avatar on the home header, which is where a person looks for their account.
 */ function $4293432b28b7e6e8$var$accountSheet(state, actions) {
    const profile = state.profile;
    const current = (0, $9a394e9e7ce3770e$export$3cd89ddcdf90b64f)(state.knownTenants);
    const children = [];
    if (profile) children.push((0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-account-who"
    }, [
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-avatar pqm-avatar-md",
            text: $4293432b28b7e6e8$var$initials(profile.name || "?"),
            "aria-hidden": "true"
        }),
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-account-text"
        }, [
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                class: "pqm-account-name",
                text: profile.name || (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetSignedIn", "Signed in")
            }),
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                class: "pqm-account-sub",
                text: profile.tenantName || current?.host || ""
            })
        ])
    ]));
    // Every OTHER installation this device knows. The active one is named above rather than listed
    // as a choice — offering to switch to where you already are is a control that does nothing.
    const others = state.knownTenants.filter((t)=>!current || t.host !== current.host);
    if (others.length > 0) {
        children.push((0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-kicker",
            text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetSwitchInstallation", "Switch tenant")
        }));
        for (const tenant of others){
            const row = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
                class: "pqm-method",
                type: "button"
            }, [
                (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                    class: "pqm-method-text"
                }, [
                    (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                        class: "pqm-method-title",
                        text: tenant.displayName || tenant.host
                    }),
                    (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                        class: "pqm-method-sub",
                        text: (0, $9a394e9e7ce3770e$export$7be77b29b66ac800)(tenant)
                    })
                ]),
                (0, $62a79e86ee9eca2d$export$dc84183312f0c201)("var(--color-row-chevron)")
            ]);
            (0, $afadd878e68b56c8$export$2385a24977818dd0)(row, "click", ()=>actions.switchTenant(tenant));
            children.push(row);
        }
    }
    // ⚠ DEVICE settings, not account ones, and they sit here only because this is the app's one
    // settings screen. Stored in this browser; they apply to every tenant on it. Radio buttons, one
    // option per line: a segmented control squeezed "Match device" and "From PQFORCE" into thirds of
    // a phone's width, and these are choices to read, not to toggle.
    children.push($4293432b28b7e6e8$var$radioGroup("pqm-appearance", (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetAppearance", "Appearance"), (0, $ca2b09cd067c2e8d$export$9aa713c49cf52433).map((appearance)=>({
            value: appearance,
            label: (0, $ca2b09cd067c2e8d$export$2273adc37b58ffd0)(appearance)
        })), state.appearance, (appearance)=>actions.setAppearance(appearance)));
    // Overrides the PQFORCE account's language on this device only; "From PQFORCE" hands the
    // decision back to the account.
    children.push($4293432b28b7e6e8$var$radioGroup("pqm-language", (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetLanguage", "Language"), (0, $498477530f916680$export$b29a60a5d482bc74).map((choice)=>({
            value: choice,
            label: (0, $498477530f916680$export$69b3add48d105f59)(choice),
            // Each language is named in itself, and marked so a screen reader says it right.
            lang: choice === "auto" ? undefined : choice
        })), state.languageChoice, (choice)=>actions.setLanguageChoice(choice)));
    const add = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
        class: "btn btn-secondary btn-block",
        type: "button",
        text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetAddInstallation", "Add another tenant")
    });
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(add, "click", actions.addInstallation);
    children.push(add);
    const out = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
        class: "btn btn-ghost btn-block pqm-account-signout",
        type: "button"
    }, [
        // Names the installation, because signing out of ONE of several is what this does — and on
        // a device paired with two, "Delete the access token" alone does not say which.
        document.createTextNode(current && others.length > 0 ? (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetSignOutOf", "Delete the access token for {host}", {
            host: current.host
        }) : (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetSignOut", "Delete the access token"))
    ]);
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(out, "click", actions.signOut);
    children.push(out);
    children.push((0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-hint",
        // The consequence, stated: the other pairings survive, this one does not.
        text: others.length > 0 ? (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetSignOutHintSeveral", "Deleting the access token ends this tenant's access on this device. The others stay signed in.") : (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetSignOutHintOne", "Deleting the access token ends this device's access. You will need to approve it again next time.")
    }));
    // "Done", not "Cancel": every control here — the appearance, the installation, signing out —
    // acts as it is touched, so there is no draft this button could discard. Same reason the
    // assignees sheet says it.
    return $4293432b28b7e6e8$var$sheet((0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetAccount", "Account"), children, actions.closeSheet, false, "Done");
}
/**
 * Rename a TODO and rewrite its description. Owner only — the button that opens it is.
 *
 * ⚠ THE DESCRIPTION IS HTML AND THIS IS A TEXTAREA. A value carrying lists, links, emphasis or
 * inline styles is shown READ-ONLY with a sentence saying where to edit it, because flattening
 * somebody's formatting to text and saving it cannot be undone — the rule is
 * `pqfMobileDescriptionIsPlain()` on the server, which decides it from the raw value and refuses
 * the write as well. Renaming stays possible either way: the title has nothing to do with it.
 *
 * As everywhere in these sheets, what is being typed lives in the fields until Save — routing a
 * keystroke through the store would repaint the sheet under the caret.
 */ function $4293432b28b7e6e8$var$detailsSheet(todo, actions) {
    const editable = todo.descriptionEditable === true;
    // ⚠ WHAT IS TYPED SURVIVES A REPAINT, and it has to: a failed save toasts, a toast is a store
    // update, and every store update rebuilds this sheet — so without the draft the fields came
    // back holding the tenant's values and the edit that had just failed was gone, which is the one
    // moment it is the only copy. Kept out of the store for the usual reason (a keystroke written
    // there would rebuild the field under the caret) and cleared by renderSheet() when this sheet
    // is not the one open, so it opens on the tenant's values next time.
    if (!$4293432b28b7e6e8$var$detailsDraft || $4293432b28b7e6e8$var$detailsDraft.id !== todo.id) $4293432b28b7e6e8$var$detailsDraft = {
        id: todo.id,
        title: todo.title,
        description: todo.descriptionText ?? ""
    };
    const draft = $4293432b28b7e6e8$var$detailsDraft;
    const focused = document.activeElement;
    const title = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("input", {
        class: "input",
        style: "min-height:46px",
        type: "text",
        "aria-label": (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetTodoTitle", "Title")
    });
    title.value = draft.title;
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(title, "input", ()=>{
        draft.title = title.value;
    });
    const description = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("textarea", {
        class: "input",
        style: "min-height:140px",
        "aria-label": (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetTodoDescription", "Description"),
        placeholder: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetDescriptionPlaceholder", "What is this about?")
    });
    description.value = draft.description;
    description.disabled = !editable;
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(description, "input", ()=>{
        draft.description = description.value;
    });
    const save = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
        class: "btn btn-primary btn-block",
        style: "min-height:48px",
        type: "button",
        text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetSaveChanges", "Save changes")
    });
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(save, "click", ()=>{
        save.disabled = true;
        // ⚠ The description is sent ONLY when this sheet was allowed to edit it. A key the route
        // does not receive is a field it does not touch, so a rename cannot flatten a formatted
        // description on its way past.
        actions.saveDetails(title.value.trim(), editable ? description.value : undefined);
    });
    const fields = [
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "field"
        }, [
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("label", {
                text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetTodoTitle", "Title")
            }),
            title
        ]),
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "field"
        }, [
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("label", {
                text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetTodoDescription", "Description")
            }),
            description
        ])
    ];
    if (!editable) fields.push((0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-note"
    }, [
        (0, $62a79e86ee9eca2d$export$1ca1ec8b29a4ce27)("warningCircle", 15, "var(--color-ink-5)"),
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-note-text",
            text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetDescriptionFormatted", "This description was written with formatting, so it can only be changed in PQFORCE. You can still rename the TODO here.")
        })
    ]));
    fields.push(save);
    // Whichever field was being typed into gets the caret back, at the end of what is in it.
    for (const [node, was] of [
        [
            title,
            "pqm-details-title"
        ],
        [
            description,
            "pqm-details-description"
        ]
    ]){
        node.classList.add(was);
        if (focused?.classList.contains(was)) window.setTimeout(()=>{
            node.focus();
            node.setSelectionRange(node.value.length, node.value.length);
        }, 0);
    }
    return $4293432b28b7e6e8$var$sheet((0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetEditTodo", "Edit TODO"), fields, actions.closeSheet);
}
function $4293432b28b7e6e8$export$85dfa746d856176d(state, actions) {
    // The people search is shared by the assignees and the owner sheet; it is kept while one is open.
    if (state.sheet !== "assignees" && state.sheet !== "owner") $4293432b28b7e6e8$var$assigneeQuery = "";
    if (state.sheet !== "details") $4293432b28b7e6e8$var$detailsDraft = null;
    const scrim = $4293432b28b7e6e8$var$sheetFor(state, actions);
    // The sheet's identity across repaints — see `steadyOverlays()` in ui_mobile_shell.ts.
    if (scrim) scrim.dataset.overlay = `sheet:${state.sheet}`;
    return scrim;
}
function $4293432b28b7e6e8$var$sheetFor(state, actions) {
    // ⚠ BOTH LISTS: a TODO opened from "All TODOs" may be in neither of this person's own segments,
    // and its sheets must open all the same. This person's own copy first, as `store.selected()`.
    const todo = state.todos.find((t)=>t.id === state.selectedId) ?? state.allTodos?.find((t)=>t.id === state.selectedId) ?? null;
    switch(state.sheet){
        case "transition":
            return todo && state.transitionDraft ? $4293432b28b7e6e8$var$statusSheet(todo, state, actions) : null;
        case "priority":
            return todo ? $4293432b28b7e6e8$var$prioritySheet(todo, state, actions) : null;
        case "assignees":
            return todo ? $4293432b28b7e6e8$var$assigneesSheet(todo, state, actions) : null;
        case "owner":
            return todo ? $4293432b28b7e6e8$var$ownerSheet(todo, state, actions) : null;
        case "details":
            return todo ? $4293432b28b7e6e8$var$detailsSheet(todo, actions) : null;
        case "delete":
            return todo ? $4293432b28b7e6e8$var$deleteSheet(todo, actions) : null;
        case "new":
            return $4293432b28b7e6e8$var$newTodoSheet(state, actions);
        case "account":
            return $4293432b28b7e6e8$var$accountSheet(state, actions);
        default:
            return null;
    }
}
/**
 * Hand the TODO over: pick ONE person, then confirm. They replace the current owner(s) — which is
 * what PQFORCE's own client does, and why this is not an add/remove list like the assignees.
 *
 * ⚠ TWO STEPS, like the status and the priority: a hand-over can cost this person their own say
 * over the TODO, so a stray tap must not do it. Confirm names the person, and the consequence is
 * stated when the person handing over is an owner.
 *
 * The candidates are whom the OWNER relation accepts (`ownerPeople`), which need not be whom the
 * assignee relation accepts.
 */ function $4293432b28b7e6e8$var$ownerSheet(todo, state, actions) {
    const self = state.profile?.resourceId ?? "";
    const currentIds = new Set(todo.owners.map((o)=>o.id));
    const people = (state.createOptions?.ownerPeople ?? []).filter((p)=>!(currentIds.has(p.id) && currentIds.size === 1));
    const picked = people.find((p)=>p.id === state.ownerPick) ?? null;
    const busy = state.assigneeBusy !== null;
    const row = (person)=>{
        const name = person.name || (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetUnnamedResource", "Unnamed resource");
        const chosen = picked?.id === person.id;
        const button = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
            class: "pqm-pick pqm-pick-option pqm-pick-person",
            type: "button",
            "aria-pressed": chosen ? "true" : "false"
        }, [
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                class: "pqm-avatar pqm-avatar-md",
                text: $4293432b28b7e6e8$var$initials(name),
                "aria-hidden": "true"
            }),
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
                text: name
            })
        ]);
        button.disabled = busy;
        (0, $afadd878e68b56c8$export$2385a24977818dd0)(button, "click", ()=>actions.pickOwner(person.id));
        return button;
    };
    const confirm = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
        class: "btn btn-primary btn-block",
        type: "button",
        text: picked ? busy ? "\u2026" : (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetHandOverTo", "Hand over to {name}", {
            name: picked.name || "?"
        }) : (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetChooseOwner", "Choose who takes it over")
    });
    confirm.disabled = !picked || busy;
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(confirm, "click", actions.handOver);
    const children = [
        $4293432b28b7e6e8$var$currently((0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
            class: "pqm-change-value",
            text: (0, $0a55aae61f0d5dc0$export$c6f240c4f4db706a)(todo.owners, self) || "\u2014"
        })),
        people.length ? $4293432b28b7e6e8$var$addList(people.map((p)=>({
                name: p.name,
                row: row(p)
            }))) : (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-empty",
            text: state.createOptions ? (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetNobodyToHandOver", "There is nobody this TODO can be handed over to.") : (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetLoadingPeople", "Loading who can be assigned\u2026")
        })
    ];
    if (currentIds.has(self)) children.push((0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-hint",
        text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetHandOverHint", "You will no longer own this TODO. What you may still change then depends on your permissions in PQFORCE.")
    }));
    children.push(confirm);
    return $4293432b28b7e6e8$var$sheet((0, $0a4952cb2d3e704b$export$8cfece6d5e778803)("owner", (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewOwner", "Owner")), children, actions.closeSheet, true);
}
/** Deleting is asked once more, naming the TODO — it cannot be undone from the app. */ function $4293432b28b7e6e8$var$deleteSheet(todo, actions) {
    const confirm = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
        class: "btn btn-primary btn-block pqm-confirm",
        type: "button"
    }, [
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
            text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetDeleteConfirm", "Delete TODO")
        })
    ]);
    (0, $afadd878e68b56c8$export$2385a24977818dd0)(confirm, "click", ()=>{
        confirm.disabled = true;
        actions.deleteTodo();
    });
    return $4293432b28b7e6e8$var$sheet((0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetDeleteTitle", "Delete TODO?"), [
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-hint",
            text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileSheetDeleteText", "\u201C{title}\u201D will be deleted. This cannot be undone in the app.", {
                title: todo.code ? `${todo.code} ${todo.title}` : todo.title
            })
        }),
        confirm
    ], actions.closeSheet);
}
/** A titled group of radio buttons, one option per line — the account sheet's device settings. */ function $4293432b28b7e6e8$var$radioGroup(name, title, options, checked, onChange) {
    return (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
        class: "pqm-radio-group",
        role: "radiogroup",
        "aria-label": title
    }, [
        (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-kicker",
            text: title
        }),
        ...options.map((option)=>{
            const input = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("input", {
                type: "radio",
                name: name,
                value: option.value,
                checked: option.value === checked
            });
            (0, $afadd878e68b56c8$export$2385a24977818dd0)(input, "change", ()=>onChange(option.value));
            return (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("label", {
                class: "radio pqm-radio-row",
                lang: option.lang
            }, [
                input,
                (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
                    class: "dot",
                    "aria-hidden": "true"
                }),
                document.createTextNode(option.label)
            ]);
        })
    ]);
}


// ─────────────────────────────────────────────────────────────────────────────
// The phone's back gesture steps back INSIDE the app — closes a sheet, leaves a TODO — instead of
// leaving the app altogether.
//
// The app has no router: screens are store state. So the browser history carries a single TRAP
// entry while there is anything to go back from, rather than one entry per screen:
//
// - depth 0 → more: push the trap.
// - the back gesture (popstate): take ONE step back in the store (a sheet, the search card, a TODO); if there is still somewhere to
//   go back from, push the trap again.
// - more → 0 by the app's own hand (the header's back, a sheet's close button): pop the trap with
//   `history.back()`, and ignore the popstate that answers it.
//
// ⚠ One trap, not a stack, because the store can move between depths in ways no history stack
// mirrors — a sheet closed and another opened in one tick, a switch of installation that drops
// everything at once. With one entry the only thing to keep true is "there is a trap exactly while
// depth > 0", and the ignored popstate re-arms it when depth went back up in the meantime.
//
// Onboarding is out of scope: it has its own back button and pushes nothing here.
// ─────────────────────────────────────────────────────────────────────────────
function $e8b26be480e701db$export$6532cf91eb4fb0e5(state) {
    if (!state.ready || !state.signedIn) return 0;
    let depth = 0;
    if (state.tab === "todos" && state.todoView === "detail") depth += 1;
    if (state.sheet) depth += 1;
    if (state.searchOpen) depth += 1;
    return depth;
}
const $e8b26be480e701db$var$TRAP = {
    pqm: "back"
};
function $e8b26be480e701db$var$isTrap(state) {
    return Boolean(state && typeof state === "object" && state.pqm === $e8b26be480e701db$var$TRAP.pqm);
}
/** The one popstate listener; module-level so a second init (the tests mount many times) replaces it. */ let $e8b26be480e701db$var$popHandler = null;
function $e8b26be480e701db$export$9c0b714c3560f3aa(store, steps) {
    if ($e8b26be480e701db$var$popHandler) window.removeEventListener("popstate", $e8b26be480e701db$var$popHandler);
    // ⚠ A RELOAD KEEPS THE HISTORY, trap included: the page comes back on the entry it was on. So
    // the trap may already be there, and pushing another would leave a dead entry behind per reload.
    let armed = $e8b26be480e701db$var$isTrap(window.history.state);
    /** popstates still to come that answer our own history.back() — not the person's gesture. */ let ignorePops = 0;
    /** True while a popstate is being turned into a store step, so that drop is not popped again. */ let stepping = false;
    let depth = $e8b26be480e701db$export$6532cf91eb4fb0e5(store.getState());
    const arm = ()=>{
        window.history.pushState($e8b26be480e701db$var$TRAP, "");
        armed = true;
    };
    store.subscribe((state)=>{
        const next = $e8b26be480e701db$export$6532cf91eb4fb0e5(state);
        const previous = depth;
        depth = next;
        if (stepping || ignorePops > 0) return;
        if (next > 0 && !armed) arm();
        else if (next === 0 && previous > 0 && armed) {
            armed = false;
            ignorePops += 1;
            window.history.back();
        }
    });
    $e8b26be480e701db$var$popHandler = ()=>{
        if (ignorePops > 0) {
            ignorePops -= 1;
            // Depth went back up while our own pop was on its way: the trap it removed is needed.
            if (depth > 0 && !armed) arm();
            return;
        }
        armed = false;
        const state = store.getState();
        if ($e8b26be480e701db$export$6532cf91eb4fb0e5(state) === 0) return;
        stepping = true;
        try {
            if (state.sheet) steps.closeSheet();
            else if (state.searchOpen) steps.closeSearch();
            else steps.back();
        } finally{
            stepping = false;
        }
        if (depth > 0) arm();
    };
    window.addEventListener("popstate", $e8b26be480e701db$var$popHandler);
    if (depth > 0) arm();
}


// ─────────────────────────────────────────────────────────────────────────────
// The phone's keyboard must never cover the field being typed into.
//
// Three things, because the browsers disagree about what a keyboard does to the page:
//
// 1. Chrome on Android is told `interactive-widget=resizes-content` (the viewport meta in
//    pqforce-mobile.php): the keyboard then SHRINKS THE LAYOUT, so everything fixed to the screen —
//    the sheets, the search card — sits above it. Its default only shrank the visible area, and a
//    sheet fixed to the bottom of the screen stayed behind the keyboard.
// 2. Safari on iOS ignores that and shrinks only the VISUAL viewport. `--pqm-keyboard` on <html>
//    carries the height the keyboard covers (from `visualViewport`), and the stylesheet pads the
//    sheets' scrim by it. Under Chrome with (1) it is 0, so the two never add up.
// 3. Neither browser reliably scrolls a field that is already on screen out from under the
//    keyboard it is about to open, and none of them knows about the sticky header. So a focused
//    field is scrolled into the visible area here, once the keyboard has settled — inside its sheet
//    when it is in one, the page otherwise.
//
// While a field has the focus the root carries `pqm-typing`, which takes the module menu away: it
// would otherwise sit on top of the keyboard and take another 52px from what is left.
// ─────────────────────────────────────────────────────────────────────────────
/** The root class while a text field has the focus. */ const $8ec6ba565d4684f6$export$f86a5775335f6689 = "pqm-typing";
/** Below this, a shrunk visual viewport is the browser's address bar moving, not a keyboard. */ const $8ec6ba565d4684f6$var$KEYBOARD_MIN = 80;
/** Breathing room kept between a revealed field and the keyboard or the header. */ const $8ec6ba565d4684f6$var$REVEAL_MARGIN = 16;
/** How long the keyboard takes to slide in — after it, the visible area is final. */ const $8ec6ba565d4684f6$var$KEYBOARD_SETTLE_MS = 350;
/**
 * How long the visible area must stay put before a non-zero keyboard height is applied.
 *
 * ⚠ CHROME RESIZES IN TWO STEPS. With `resizes-content` the visual viewport shrinks first and the
 * layout follows a moment later — and in between, the visible area is short of the window by the
 * whole keyboard, which reads exactly like Safari's keyboard. Applied at once, it padded the sheets'
 * scrim by the keyboard's height for a frame or two: the translucent backdrop flashed up from the
 * bottom over the field. Waiting for the area to settle lets Chrome reach its final state (0)
 * first; Safari, where the gap is real, gets its lift this much later.
 */ const $8ec6ba565d4684f6$var$INSET_SETTLE_MS = 200;
function $8ec6ba565d4684f6$export$52917ab796e0510(innerHeight, visualHeight, visualOffsetTop) {
    const covered = Math.round(innerHeight - visualHeight - visualOffsetTop);
    return covered >= $8ec6ba565d4684f6$var$KEYBOARD_MIN ? covered : 0;
}
function $8ec6ba565d4684f6$export$a2d74c4f458e19aa(element) {
    if (!element) return false;
    if (element instanceof HTMLTextAreaElement) return true;
    if (element instanceof HTMLInputElement) return ![
        "checkbox",
        "radio",
        "button",
        "submit",
        "reset",
        "file",
        "range",
        "color",
        "hidden"
    ].includes(element.type);
    return element instanceof HTMLElement && element.isContentEditable === true;
}
function $8ec6ba565d4684f6$export$2ce61944cdd034e6(rect, visibleTop, visibleBottom, margin = $8ec6ba565d4684f6$var$REVEAL_MARGIN) {
    const top = visibleTop + margin;
    const bottom = visibleBottom - margin;
    if (rect.top >= top && rect.bottom <= bottom) return 0;
    if (rect.bottom - rect.top > bottom - top || rect.top < top) return Math.round(rect.top - top);
    return Math.round(rect.bottom - bottom);
}
/** The nearest ancestor that scrolls vertically — a sheet's panel, the search results — or null. */ function $8ec6ba565d4684f6$var$scrollParentOf(element) {
    for(let node = element.parentElement; node && node !== document.body; node = node.parentElement){
        const overflow = getComputedStyle(node).overflowY;
        if ((overflow === "auto" || overflow === "scroll") && node.scrollHeight > node.clientHeight) return node;
    }
    return null;
}
function $8ec6ba565d4684f6$export$14356dd5f091a5f3(field) {
    if (!field.isConnected || document.activeElement !== field) return;
    const viewport = window.visualViewport;
    const visibleTop = viewport ? viewport.offsetTop : 0;
    const visibleBottom = visibleTop + (viewport ? viewport.height : window.innerHeight);
    // The sticky header covers the top of the PAGE; a field inside an overlay is above it.
    const inOverlay = field.closest("[data-overlay]") !== null;
    const header = inOverlay ? null : document.querySelector(".pqm-appbar");
    const top = Math.max(visibleTop, header ? header.getBoundingClientRect().bottom : visibleTop);
    const delta = $8ec6ba565d4684f6$export$2ce61944cdd034e6(field.getBoundingClientRect(), top, visibleBottom);
    if (delta === 0) return;
    const parent = $8ec6ba565d4684f6$var$scrollParentOf(field);
    if (parent) parent.scrollTop += delta;
    else window.scrollBy(0, delta);
}
/** The listeners; module-level so a second init (the tests mount many times) replaces them. */ let $8ec6ba565d4684f6$var$handlers = null;
function $8ec6ba565d4684f6$export$dd52d3a8aed5a5e8(root) {
    const viewport = window.visualViewport;
    if ($8ec6ba565d4684f6$var$handlers) {
        document.removeEventListener("focusin", $8ec6ba565d4684f6$var$handlers.focusin);
        document.removeEventListener("focusout", $8ec6ba565d4684f6$var$handlers.focusout);
        viewport?.removeEventListener("resize", $8ec6ba565d4684f6$var$handlers.viewport);
        viewport?.removeEventListener("scroll", $8ec6ba565d4684f6$var$handlers.viewport);
    }
    let revealTimer = 0;
    let insetTimer = 0;
    const apply = (inset)=>document.documentElement.style.setProperty("--pqm-keyboard", `${inset}px`);
    const measure = ()=>{
        window.clearTimeout(insetTimer);
        const inset = viewport ? $8ec6ba565d4684f6$export$52917ab796e0510(window.innerHeight, viewport.height, viewport.offsetTop) : 0;
        // Taking the lift AWAY is never wrong and must not lag behind a closing keyboard; adding it
        // waits until the browser has finished resizing — see INSET_SETTLE_MS.
        if (inset === 0) {
            apply(0);
            return;
        }
        insetTimer = window.setTimeout(()=>{
            const settled = viewport ? $8ec6ba565d4684f6$export$52917ab796e0510(window.innerHeight, viewport.height, viewport.offsetTop) : 0;
            apply(settled);
        }, $8ec6ba565d4684f6$var$INSET_SETTLE_MS);
    };
    const scheduleReveal = (field)=>{
        window.clearTimeout(revealTimer);
        revealTimer = window.setTimeout(()=>$8ec6ba565d4684f6$export$14356dd5f091a5f3(field), $8ec6ba565d4684f6$var$KEYBOARD_SETTLE_MS);
    };
    $8ec6ba565d4684f6$var$handlers = {
        focusin: (event)=>{
            if (!$8ec6ba565d4684f6$export$a2d74c4f458e19aa(event.target)) return;
            root.classList.add($8ec6ba565d4684f6$export$f86a5775335f6689);
            scheduleReveal(event.target);
        },
        focusout: ()=>{
            // On the NEXT frame: moving from one field to the next is a focusout then a focusin, and
            // dropping the class in between would flash the menu.
            window.requestAnimationFrame(()=>{
                if (!$8ec6ba565d4684f6$export$a2d74c4f458e19aa(document.activeElement)) root.classList.remove($8ec6ba565d4684f6$export$f86a5775335f6689);
            });
        },
        viewport: ()=>{
            measure();
            // The keyboard changed the visible area: settle the focused field again.
            if ($8ec6ba565d4684f6$export$a2d74c4f458e19aa(document.activeElement)) scheduleReveal(document.activeElement);
        }
    };
    document.addEventListener("focusin", $8ec6ba565d4684f6$var$handlers.focusin);
    document.addEventListener("focusout", $8ec6ba565d4684f6$var$handlers.focusout);
    viewport?.addEventListener("resize", $8ec6ba565d4684f6$var$handlers.viewport);
    viewport?.addEventListener("scroll", $8ec6ba565d4684f6$var$handlers.viewport);
    measure();
}


// ─────────────────────────────────────────────────────────────────────────────
// The tab bar steps aside while the reader scrolls down, and comes back on the way up.
//
// ⚠ THE DOCUMENT IS THE SCROLLER, not a div inside it. Chrome on Android and Safari on iOS fold
// their own toolbars away only when the PAGE scrolls; the app used to pin `html`/`body` at 100%
// with `overflow: hidden` and scroll each screen inside a `.pqm-scroll`, which is why the browser
// chrome stayed put here while it collapsed on other sites. So this listens on `window`.
//
// ⚠ The state lives on the ROOT as a class (`pqm-tabbar-hidden`). Every store update repaints the
// whole app and the tab bar is a new element each time; the root is the one node that survives.
// ─────────────────────────────────────────────────────────────────────────────
/** The root class that slides the tab bar out of view. */ const $5c4add5e763a6b0c$export$53a4433124529a62 = "pqm-tabbar-hidden";
const $5c4add5e763a6b0c$export$16a1e6010efaef52 = 8;
const $5c4add5e763a6b0c$export$8bba6c261388d801 = 56;
function $5c4add5e763a6b0c$export$c2f43e98258530e4(prev, y, maxY, threshold = $5c4add5e763a6b0c$export$16a1e6010efaef52, topZone = $5c4add5e763a6b0c$export$8bba6c261388d801) {
    if (y <= topZone || y >= maxY - 2) return {
        y: y,
        hidden: false,
        travel: 0
    };
    const delta = y - prev.y;
    if (delta === 0) return prev;
    const sameWay = delta > 0 === prev.travel > 0 && prev.travel !== 0;
    const travel = (sameWay ? prev.travel : 0) + delta;
    let hidden = prev.hidden;
    if (travel > threshold) hidden = true;
    else if (travel < -threshold) hidden = false;
    return {
        y: y,
        hidden: hidden,
        travel: travel
    };
}
function $5c4add5e763a6b0c$export$ce37d8aad92b4f23(scrollY, viewport, scrollHeight, trackTop, trackBottom, minThumb = 28) {
    const track = trackBottom - trackTop;
    const maxY = scrollHeight - viewport;
    if (maxY <= 1 || track <= minThumb) return {
        visible: false,
        top: trackTop,
        height: 0
    };
    const height = Math.max(minThumb, Math.round(viewport / scrollHeight * track));
    const share = Math.min(1, Math.max(0, scrollY / maxY));
    return {
        visible: true,
        top: Math.round(trackTop + share * (track - height)),
        height: height
    };
}
/** The one scroll listener; module-level so a second init (the tests mount many times) replaces it. */ let $5c4add5e763a6b0c$var$scrollHandler = null;
let $5c4add5e763a6b0c$var$resizeHandler = null;
/**
 * The slim scroll indicator on the right edge — where in a long list the reader is (2026-10-18).
 *
 * ⚠ DRAWN BY THE APP, not the browser's scrollbar: on a phone that is an overlay that shows only
 * WHILE scrolling, and its styling is not reliably honoured. It is outside the app root, because
 * every repaint replaces the root's content, and it never takes a tap (`pointer-events: none`).
 */ function $5c4add5e763a6b0c$var$indicatorElement() {
    let track = document.querySelector("body > .pqm-scroll-indicator");
    if (!track) {
        track = document.createElement("div");
        track.className = "pqm-scroll-indicator";
        track.setAttribute("aria-hidden", "true");
        track.appendChild(document.createElement("div")).className = "pqm-scroll-thumb";
        document.body.appendChild(track);
    }
    return {
        track: track,
        thumb: track.firstElementChild
    };
}
function $5c4add5e763a6b0c$export$d5681e343986e7ff(root) {
    if ($5c4add5e763a6b0c$var$scrollHandler) window.removeEventListener("scroll", $5c4add5e763a6b0c$var$scrollHandler);
    if ($5c4add5e763a6b0c$var$resizeHandler) window.removeEventListener("resize", $5c4add5e763a6b0c$var$resizeHandler);
    let state = {
        y: window.scrollY,
        hidden: false,
        travel: 0
    };
    let frame = 0;
    const apply = ()=>root.classList.toggle($5c4add5e763a6b0c$export$53a4433124529a62, state.hidden);
    const drawIndicator = ()=>{
        const { track: track, thumb: thumb } = $5c4add5e763a6b0c$var$indicatorElement();
        // Below the sticky header, above the bottom edge — the part of the screen that scrolls.
        const header = root.querySelector(".pqm-appbar");
        const top = (header ? header.getBoundingClientRect().bottom : 0) + 4;
        const bar = $5c4add5e763a6b0c$export$ce37d8aad92b4f23(window.scrollY, window.innerHeight, document.documentElement.scrollHeight, top, window.innerHeight - 4);
        track.hidden = !bar.visible;
        thumb.style.transform = `translateY(${bar.top}px)`;
        thumb.style.height = `${bar.height}px`;
    };
    const measure = ()=>{
        frame = 0;
        const maxY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
        state = $5c4add5e763a6b0c$export$c2f43e98258530e4(state, window.scrollY, maxY);
        apply();
        drawIndicator();
    };
    $5c4add5e763a6b0c$var$scrollHandler = ()=>{
        if (frame) return;
        frame = window.requestAnimationFrame(measure);
    };
    window.addEventListener("scroll", $5c4add5e763a6b0c$var$scrollHandler, {
        passive: true
    });
    $5c4add5e763a6b0c$var$resizeHandler = drawIndicator;
    window.addEventListener("resize", $5c4add5e763a6b0c$var$resizeHandler);
    return {
        reset () {
            if (frame) {
                window.cancelAnimationFrame(frame);
                frame = 0;
            }
            state = {
                y: window.scrollY,
                hidden: false,
                travel: 0
            };
            apply();
            drawIndicator();
        },
        refresh: drawIndicator
    };
}



/**
 * Where the known installations are remembered. NOT a credential — a host, a
 * name and which method was used, and nothing that grants access to anything.
 * See the header of `app/mobile/tenants.ts`.
 */ const $9d251d88620741d4$var$TENANT_KEY = "pqf.mobile.tenant";
function $9d251d88620741d4$var$readStoredTenants() {
    try {
        return (0, $9a394e9e7ce3770e$export$bdd7edb13ed615d)(window.localStorage.getItem($9d251d88620741d4$var$TENANT_KEY));
    } catch  {
        // Private mode, or site data blocked. The user types it again; nothing breaks.
        return [];
    }
}
function $9d251d88620741d4$var$writeStoredTenants(list) {
    try {
        window.localStorage.setItem($9d251d88620741d4$var$TENANT_KEY, JSON.stringify(list));
    } catch  {
    /* see above */ }
}
/**
 * Which list groups are folded away. A reading preference for this device, nothing more — so it
 * lives beside the known installations rather than on the server.
 */ const $9d251d88620741d4$var$COLLAPSED_KEY = "pqf.mobile.collapsedGroups";
function $9d251d88620741d4$var$readCollapsedGroups() {
    try {
        return (0, $0a55aae61f0d5dc0$export$7a7fcaa2edee7e0b)(window.localStorage.getItem($9d251d88620741d4$var$COLLAPSED_KEY));
    } catch  {
        return [];
    }
}
function $9d251d88620741d4$var$writeCollapsedGroups(keys) {
    try {
        window.localStorage.setItem($9d251d88620741d4$var$COLLAPSED_KEY, JSON.stringify(keys.slice(-(0, $0a55aae61f0d5dc0$export$336db08e250a7b79))));
    } catch  {
    /* private mode: the folding simply does not survive a reload */ }
}
const $9d251d88620741d4$var$RELATION_NAMES_KEY = "pqf.mobile.relationNames";
/** The tenant's role names remembered for `tenant` in `language`, or null for none/another. */ function $9d251d88620741d4$var$readRelationNames(tenant, language) {
    try {
        const saved = JSON.parse(window.localStorage.getItem($9d251d88620741d4$var$RELATION_NAMES_KEY) || "null");
        return saved && saved.tenant === tenant && saved.language === language ? saved.names : null;
    } catch  {
        return null;
    }
}
function $9d251d88620741d4$var$writeRelationNames(tenant, language) {
    if (!tenant) return;
    try {
        window.localStorage.setItem($9d251d88620741d4$var$RELATION_NAMES_KEY, JSON.stringify({
            tenant: tenant,
            language: language,
            names: (0, $0a4952cb2d3e704b$export$ae1f3e529360b802)()
        }));
    } catch  {
    /* private mode: the app's own words until the options answer, on every start */ }
}
const $9d251d88620741d4$var$VIEW_KEY = "pqf.mobile.view";
/** The view this tab left behind, as stored — validated by viewToRestore(), not here. */ function $9d251d88620741d4$var$readSavedView() {
    try {
        return JSON.parse(window.sessionStorage.getItem($9d251d88620741d4$var$VIEW_KEY) || "null");
    } catch  {
        return null; // private mode or junk: start on Home, as before
    }
}
function $9d251d88620741d4$var$writeSavedView(view) {
    try {
        window.sessionStorage.setItem($9d251d88620741d4$var$VIEW_KEY, JSON.stringify(view));
    } catch  {
    /* private mode: a reload starts on Home */ }
}
/** The one set of view-memory handlers; module-level for the reason the wake handlers are. */ let $9d251d88620741d4$var$viewHandler = null;
/**
 * Remember the view so a reload comes back to it — see app/mobile/view.ts.
 *
 * Written on every store update that changes the VIEW (cheap: a short JSON string), and the scroll
 * offset on `pagehide` / when the tab is hidden, the last moments a page is sure to get — a scroll
 * listener writing storage on every frame would cost more than it is worth.
 */ function $9d251d88620741d4$var$initViewMemory(store) {
    // The app restores the offset itself once its content has loaded; the browser's own attempt
    // lands on a page that is still empty, and then fights the app's.
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
    let last = "";
    const save = ()=>{
        const view = (0, $ef9823cca3288d87$export$bc820e57bf78fba)(store.getState(), window.scrollY);
        if (!view) return;
        const json = JSON.stringify(view);
        if (json === last) return;
        last = json;
        $9d251d88620741d4$var$writeSavedView(view);
    };
    store.subscribe(save);
    if ($9d251d88620741d4$var$viewHandler) {
        window.removeEventListener("pagehide", $9d251d88620741d4$var$viewHandler);
        document.removeEventListener("visibilitychange", $9d251d88620741d4$var$viewHandler);
    }
    $9d251d88620741d4$var$viewHandler = save;
    window.addEventListener("pagehide", $9d251d88620741d4$var$viewHandler);
    document.addEventListener("visibilitychange", $9d251d88620741d4$var$viewHandler);
}
/** The one set of wake handlers; see where they are registered for why they are module-level. */ let $9d251d88620741d4$var$wakeHandlers = null;
function $9d251d88620741d4$export$e97df488f1a5cdc(root) {
    // ⚠ BEFORE anything renders: every string below is read through t() at paint time.
    (0, $d88fccb0df7b88b9$export$544ea8661fa0df65)(root);
    const store = new (0, $34dfe091e21ab98b$export$3ba7ded393c43e1e)();
    const client = new (0, $373193550cbe2f46$export$d68e436226202e2)({
        baseUrl: root.dataset.apiBase || "api/",
        language: (0, $d88fccb0df7b88b9$export$acae4bd1ab2a50cd),
        onSessionLost: ()=>{
            // ⚠ `sessionExpired`, not a plain sign-out: this person paired before,
            // and the returning screen offers their own method back rather than
            // an empty address field.
            (0, $817c29ac222633b5$export$4dbd032533367935)();
            store.update({
                searchOpen: false,
                pairingFromApp: false,
                signedIn: false,
                onboarding: "url",
                profile: null,
                todos: [],
                allTodos: null,
                sessionExpired: store.getState().knownTenants.length > 0,
                addingTenant: false
            });
        }
    });
    const api = new (0, $d2bd7d9b8ec176dc$export$b63f8c437a9f7168)(client);
    let toastTimer = 0;
    let pollTimer = 0;
    let pollDeadline = 0;
    const toast = (message)=>{
        window.clearTimeout(toastTimer);
        store.update({
            toast: message
        });
        toastTimer = window.setTimeout(()=>store.update({
                toast: null
            }), (0, $34dfe091e21ab98b$export$e71ef44e153d57d5));
    };
    const stopPolling = ()=>{
        window.clearTimeout(pollTimer);
        pollTimer = 0;
    };
    /**
     * ⚠ A BACKGROUNDED TAB DOES NOT RUN TIMERS, and approval happens in another tab by design.
     *
     * Measured on dev, 2026-08-23: a token was approved at 21:46:01Z and first used successfully at
     * 21:48:45Z — 2m44s later, on a 2-SECOND poll. Nothing was broken; the app tab was frozen the
     * whole time the user was on the tenant's approval page, and woke only when they switched back.
     * The screen meanwhile said "Validating it against …" with no error and no instruction, which
     * is indistinguishable from being stuck — and is what it was reported as, repeatedly.
     *
     * So the poll is driven by RETURNING as well as by the clock: coming back to the tab is the
     * strongest possible signal that something happened on the other one. Registered once for the
     * life of the app and cheap — it only acts while a pairing is actually in flight.
     */ // ⚠ Replaces any previous app instance's handler rather than adding a second one. initUIMobile()
    // runs once in the browser, but a listener on `document` outlives the closure that registered
    // it — so without this a remount leaves the OLD store still answering the event, polling for a
    // pairing that belongs to a page nobody is looking at.
    if ($9d251d88620741d4$var$wakeHandlers) {
        document.removeEventListener("visibilitychange", $9d251d88620741d4$var$wakeHandlers.visibility);
        window.removeEventListener("pageshow", $9d251d88620741d4$var$wakeHandlers.pageshow);
        window.removeEventListener("focus", $9d251d88620741d4$var$wakeHandlers.focus);
    }
    const wake = ()=>{
        if (document.visibilityState !== "visible") return;
        // ⚠ The condition is that a poll is SCHEDULED, not which screen is showing. The waiting
        // step is "sso" for a tenant method and "login" for a credential one, and enrollmentId
        // outlives a successful pairing — so testing either would wake a loop that has finished,
        // or fail to wake the one that matters. pollTimer is exactly "a pairing is in flight":
        // stopPolling() clears it on success, on cancel and on failure. This accelerates an
        // existing loop; it never starts one.
        if (!pollTimer) return;
        pollNow();
    };
    // ⚠ THREE events, because iOS needs all three and desktop needed none of this.
    //
    // Reported 2026-08-24: pairing completes on a desktop browser and not on an iPhone 12. The
    // handshake is identical, so what differs is the tab lifecycle — Safari on iOS suspends a
    // backgrounded tab far more aggressively than a desktop browser, and restores it from the
    // back/forward cache rather than merely unfreezing it.
    //
    //   visibilitychange — the ordinary tab switch, and the only one desktop needs.
    //   pageshow         — a bfcache RESTORE. The page resumes with its JS state intact and its
    //                      timers still frozen; on iOS this is the usual way back from another
    //                      tab, and it does not reliably fire visibilitychange.
    //   focus            — the standalone (Home Screen) case, where leaving for the approval page
    //                      hands control to Safari and coming back re-focuses the web app.
    //
    // All three route to one handler that does nothing unless a poll is scheduled, so firing two
    // or three times on a single return costs one request at most — pollNow() reschedules rather
    // than stacking, because pollForApproval() clears the pending timer first.
    $9d251d88620741d4$var$wakeHandlers = {
        visibility: wake,
        pageshow: wake,
        focus: wake
    };
    document.addEventListener("visibilitychange", $9d251d88620741d4$var$wakeHandlers.visibility);
    window.addEventListener("pageshow", $9d251d88620741d4$var$wakeHandlers.pageshow);
    window.addEventListener("focus", $9d251d88620741d4$var$wakeHandlers.focus);
    /** The message a failed call should show. `result` is what the user sees. */ /** Where an open TODO's back leads: the screen it was opened from — see openTodo(). */ let detailReturn = null;
    /** The offset to put a screen back at during the one repaint `back()` makes. */ let pendingScroll = null;
    const errorText = (error, fallback)=>{
        if (error instanceof (0, $373193550cbe2f46$export$f717aa8b913cec23)) {
            if (error.statusCode === 0) return (0, $498477530f916680$export$625550452a3fa3ec)("mobileShellNoConnection", "No connection. Check your network and try again.");
            return error.message || fallback;
        }
        return fallback;
    };
    /** The status errorText's message came from — null when it was not an HTTP failure at all. */ const errorStatus = (error)=>error instanceof (0, $373193550cbe2f46$export$f717aa8b913cec23) ? error.statusCode : null;
    // ── session ──────────────────────────────────────────────────────────────
    /**
     * @param restore the view a reload left behind (see app/mobile/view.ts) — only on the page's own
     *        start, never on a switch of installation or a fresh pairing.
     */ const loadApp = async (profile, restore = null)=>{
        // The ACCOUNT's language from here on. Applied before the update below, whose repaint then
        // draws every screen in it — nothing else needs telling, since t() is read at paint time.
        (0, $d88fccb0df7b88b9$export$7e156cfdd8f555d0)(profile.language, (0, $498477530f916680$export$324a75438e87a537)());
        (0, $0a4952cb2d3e704b$export$f9c9510d76d586c3)($9d251d88620741d4$var$readRelationNames((0, $ef9823cca3288d87$export$3c0dd642b9a3c1cb)(profile), (0, $498477530f916680$export$324a75438e87a537)()));
        store.update({
            signedIn: true,
            pairingFromApp: false,
            profile: profile,
            loading: true,
            loadError: "",
            ...restore?.patch ?? {}
        });
        try {
            const todos = await api.todos();
            store.update({
                todos: todos,
                loading: false
            });
            // A reload on "All TODOs" needs that list before it can tell whether its TODO is there.
            if (restore?.patch.relation === "all") await loadAllTodos();
            // A TODO the reload came back to that is gone (done elsewhere, reassigned) leaves the
            // list on screen; say so in the state too, or the back gesture would "leave" a TODO
            // nobody can see.
            if (restore?.patch.selectedId) {
                const back = store.selected();
                if (!back) store.update({
                    todoView: "list",
                    selectedId: null
                });
                else if (!back.permissions || back.partial) completeTodo(back.id);
            }
            // Only now is the screen as long as it was; before, the browser would clamp the offset.
            if (restore && restore.scrollY > 0) window.scrollTo(0, restore.scrollY);
        } catch (error) {
            store.update({
                loading: false,
                loadError: errorText(error, (0, $498477530f916680$export$625550452a3fa3ec)("mobileShellLoadFailed", "Could not load your TODOs."))
            });
        }
        // The composer's options are only needed once a sheet opens, so this is
        // deliberately not awaited — a slow project list must not delay the list.
        loadCreateOptions();
    };
    /**
     * The "All TODOs" list — every TODO the tenant lets this person see. Read when the segment is
     * chosen (again on every choice, so it is not a snapshot from the morning), never at start:
     * on a large tenant it is the biggest answer the app asks for.
     */ const loadAllTodos = async ()=>{
        store.update({
            allLoading: true,
            allError: ""
        });
        try {
            const allTodos = await api.allTodos();
            store.update({
                allTodos: allTodos,
                allLoading: false
            });
        } catch (error) {
            store.update({
                allLoading: false,
                allError: errorText(error, (0, $498477530f916680$export$625550452a3fa3ec)("mobileShellAllTodosFailed", "The list of all TODOs could not be loaded."))
            });
        }
    };
    /**
     * Read a TODO whole — with what this person may do to it (`permissions`), and for a row from
     * "All TODOs" its moves and activity. A failure on such a row goes back to the list rather than
     * leaving a detail that says "Loading…" for good.
     */ const completeTodo = (id)=>{
        store.update({
            todoLoading: id
        });
        api.todo(id).then((todo)=>store.replaceTodo(todo)).catch((error)=>{
            // A row from "All TODOs" has nothing to show without this read, so it goes back to the
            // list. One of this person's own is whole enough to read and comment on; only its edit
            // controls stay inert, and saying nothing beats a toast on every open while offline.
            if (!store.selected()?.partial) return;
            if (store.getState().selectedId === id) store.update({
                todoView: "list"
            });
            toast(errorText(error, (0, $498477530f916680$export$625550452a3fa3ec)("mobileShellTodoReadFailed", "This TODO could not be read.")));
        }).finally(()=>{
            if (store.getState().todoLoading === id) store.update({
                todoLoading: null
            });
        });
    };
    /**
     * The composer's options — and with them the tenant's names for "assignee" and "owner"
     * (relationNames.ts), which are remembered per tenant and language so the next start draws
     * them from its first frame instead of flipping from the app's words once this answers.
     */ const loadCreateOptions = ()=>{
        api.createOptions().then((createOptions)=>{
            (0, $0a4952cb2d3e704b$export$f9c9510d76d586c3)(createOptions.relationNames, createOptions.relationListNames);
            $9d251d88620741d4$var$writeRelationNames((0, $ef9823cca3288d87$export$3c0dd642b9a3c1cb)(store.getState().profile), (0, $498477530f916680$export$324a75438e87a537)());
            store.update({
                createOptions: createOptions
            });
        }).catch(()=>{
        /* the sheets degrade to what they have */ });
    };
    const bootstrap = async ()=>{
        // ⚠ THE KNOWN INSTALLATIONS ARE LOADED FIRST, BEFORE ANY SESSION CALL.
        // They are device state and have nothing to do with whether a session
        // exists — and `onSessionLost` reads them to decide whether this is an
        // EXPIRED session or a first run. Loading them afterwards meant that
        // handler always saw an empty list, so a returning user whose token had
        // died was shown the first-run address field instead of their own
        // installation and the method they use.
        const knownTenants = $9d251d88620741d4$var$readStoredTenants();
        const current = (0, $9a394e9e7ce3770e$export$3cd89ddcdf90b64f)(knownTenants);
        store.update({
            knownTenants: knownTenants,
            tenantInput: current?.url ?? "",
            collapsedGroups: $9d251d88620741d4$var$readCollapsedGroups(),
            // Already APPLIED by the bundle's first statement; this is so the account sheet can
            // show which option is chosen.
            appearance: (0, $237371521f1a88b0$export$dde01dc24facb6b0)(),
            languageChoice: (0, $d88fccb0df7b88b9$export$e9a443f3e1c479ea)()
        });
        const refreshed = await client.refreshSession();
        if (refreshed) try {
            const profile = await api.me();
            await loadApp(profile, (0, $ef9823cca3288d87$export$9268357d5d664eb6)($9d251d88620741d4$var$readSavedView(), (0, $ef9823cca3288d87$export$3c0dd642b9a3c1cb)(profile)));
            store.update({
                ready: true
            });
            return;
        } catch  {
            // A session that refreshes but cannot answer `me` is not a
            // session — but it IS one that existed, so a returning user gets
            // the recovery card rather than a blank start.
            store.update({
                sessionExpired: knownTenants.length > 0
            });
        }
        store.update({
            ready: true,
            signedIn: false
        });
    };
    // ── onboarding actions ───────────────────────────────────────────────────
    const onboardingActions = {
        submitTenant (tenantUrl) {
            store.update({
                tenantInput: tenantUrl,
                tenantError: "",
                onboarding: "discovering"
            });
            api.discover(tenantUrl).then((discovery)=>{
                const next = (0, $ea7cc6cc52c8cad1$export$b88b3f903c831442)(discovery.methods);
                store.update({
                    discovery: discovery,
                    onboarding: next
                });
                // One method and no choice to make: start it straight away.
                if (next !== "options" && discovery.methods.length === 1) onboardingActions.pickMethod(discovery.methods[0]);
            }).catch((error)=>{
                store.update({
                    onboarding: "failed",
                    onboardingError: errorText(error, (0, $498477530f916680$export$625550452a3fa3ec)("mobileShellNoLoginOptions", "We reached {host} but it did not answer with login options.", {
                        host: (0, $ea7cc6cc52c8cad1$export$da45a345dadb2e43)(tenantUrl)
                    })),
                    onboardingErrorStatus: errorStatus(error)
                });
            });
        },
        pickMethod (method) {
            const tenantUrl = store.getState().tenantInput;
            const step = (0, $ea7cc6cc52c8cad1$export$61ff9a91c9b962ca)(method);
            // Both kinds normally land on the tenant's own approval page; only a
            // method that says it accepts credentials goes to the form instead.
            store.update({
                onboarding: step,
                onboardingError: "",
                onboardingErrorStatus: null,
                approvalOpened: null,
                lastMethodId: method.id
            });
            // ⚠ A CREDENTIAL METHOD STARTS NO PAIRING. `auth/start` runs `ApiToken/Create` on the
            // tenant, and an unapproved token can never be deleted — so creating one before anybody
            // has typed a password leaves one behind for every abandoned form. It also opened the
            // approval page in a new tab over the top of the form. With a password, the token is
            // created only after the login has succeeded (docs/mobile-login-plan.md §4.5).
            if (step === "login") return;
            api.startPairing(tenantUrl, method.id).then((pairing)=>{
                store.update({
                    enrollmentId: pairing.enrollmentId,
                    authorizationUrl: pairing.authorizationUrl,
                    authorizationDeadline: pairing.authorizationDeadline
                });
                // ⚠ A NEW TAB, never an iframe: the tenant's first-factor
                // authority may bounce to the customer's own SAML IdP, and an
                // IdP sets X-Frame-Options. It would simply render nothing.
                // ⚠ A BLOCKED POPUP RETURNS null AND DOES NOT THROW. This call sits inside a
                // promise callback, i.e. outside the user gesture that started it, which Safari
                // on iOS refuses — while desktop browsers allow it. That difference is the whole
                // of "it works on my machine and hangs on the phone": no tab opened, so there
                // was nothing to approve, so the poll waited for an approval that could never
                // come, forever, with no error. The screen has to say so and offer the link.
                const opened = window.open(pairing.authorizationUrl, "_blank", "noopener,noreferrer");
                store.update({
                    approvalOpened: opened !== null && opened !== undefined
                });
                const parsed = Date.parse(pairing.authorizationDeadline);
                pollDeadline = Number.isNaN(parsed) ? Date.now() + (0, $34dfe091e21ab98b$export$bdbb6556783384c0) : parsed;
                pollForApproval();
            }).catch((error)=>{
                store.update({
                    onboarding: "failed",
                    onboardingError: errorText(error, (0, $498477530f916680$export$625550452a3fa3ec)("mobileShellSignInFailed", "Could not start sign-in with that tenant.")),
                    onboardingErrorStatus: errorStatus(error)
                });
            });
        },
        // Reachable only when a method says it accepts credentials — see
        // `LoginMethod.acceptsCredentials` — which no server sends until the sign-in route
        // exists (docs/mobile-login-plan.md §6 step 3). Until then it contacts nothing and says so,
        // rather than finishing a pairing that was never started: it used to call
        // completePairing() on the enrollment `pickMethod()` had created, which is exactly the
        // token-before-password order that step removes.
        signIn (email, password, trustDevice) {
            store.update({
                onboardingError: (0, $498477530f916680$export$625550452a3fa3ec)("mobileShellSignInFailed", "Could not start sign-in with that tenant."),
                onboardingErrorStatus: null
            });
        },
        back () {
            stopPolling();
            store.update({
                onboarding: (0, $ea7cc6cc52c8cad1$export$3da35ff9cbe22ecd)(store.getState().onboarding),
                onboardingError: "",
                onboardingErrorStatus: null
            });
        },
        retry () {
            stopPolling();
            const { tenantInput: tenantInput } = store.getState();
            store.update({
                tokenFailedAt: null,
                tokenStep: 0,
                onboardingError: "",
                onboardingErrorStatus: null
            });
            onboardingActions.submitTenant(tenantInput);
        },
        cancelSso () {
            stopPolling();
            store.update({
                onboarding: "options",
                onboardingError: "",
                onboardingErrorStatus: null,
                enrollmentId: null
            });
        },
        checkNow () {
            // Deliberately not gated here: pollForApproval() already returns when no pairing is in
            // flight, and duplicating that test is how the two come to disagree.
            pollNow();
        },
        openApp () {
            store.update({
                onboarding: "url",
                tab: "home",
                sessionExpired: false
            });
        },
        useTenant (tenant) {
            // Straight back into discovery for that installation — the method it
            // offers is authoritative, and may have changed since last time.
            store.update({
                tenantInput: tenant.url,
                sessionExpired: false,
                addingTenant: false,
                onboardingError: "",
                onboardingErrorStatus: null
            });
            onboardingActions.submitTenant(tenant.url);
        },
        /**
         * Back from "add another tenant" to the app. Nothing was paired and nothing was left — the
         * session, the profile and the TODOs are still the current tenant's — so this only puts the
         * app back on screen, on Home.
         */ backToApp () {
            if (!client.hasSession() || !store.getState().profile) {
                store.update({
                    pairingFromApp: false
                });
                return;
            }
            store.update({
                signedIn: true,
                pairingFromApp: false,
                addingTenant: false,
                onboarding: "url",
                tenantInput: "",
                tenantError: "",
                tab: "home",
                todoView: "list"
            });
        },
        addAnother () {
            store.update({
                addingTenant: true,
                tenantInput: "",
                sessionExpired: false,
                tenantError: ""
            });
        },
        forgetTenant (tenant) {
            const knownTenants = (0, $9a394e9e7ce3770e$export$a2ecad0d59d96472)(store.getState().knownTenants, tenant.host);
            $9d251d88620741d4$var$writeStoredTenants(knownTenants);
            store.update({
                knownTenants: knownTenants,
                addingTenant: knownTenants.length === 0
            });
        }
    };
    /**
     * Make `tenant` the active installation, using the pairing this device already holds.
     *
     * Shared by the account sheet's switch and by the landing after a sign-out, because those are
     * the same operation: both have a live pairing and neither may go back through the tenant's
     * approval page. A 404 means there is no pairing here after all, and the honest answer to that
     * is to start one — the user asked to go there.
     */ function switchToTenant(tenant, announce) {
        // Up for the WHOLE operation — the switch call and the load behind it — because the screen
        // still holds the other installation's TODOs until the new ones land.
        store.update({
            busyLabel: (0, $498477530f916680$export$625550452a3fa3ec)("mobileShellSwitching", "Switching to {name}\u2026", {
                name: tenant.displayName || tenant.host
            })
        });
        api.switchTenant(tenant.url).then((result)=>{
            client.setAccessToken(result.accessToken);
            // Touching `lastUsed` is what makes it active in the list — which is what the
            // account sheet reads to decide which installation to name.
            const knownTenants = (0, $9a394e9e7ce3770e$export$144a76485d039242)(store.getState().knownTenants, {
                ...tenant,
                displayName: result.profile.tenantName ?? tenant.displayName,
                lastUsed: Date.now()
            });
            $9d251d88620741d4$var$writeStoredTenants(knownTenants);
            // Everything on screen belongs to the installation being left — including a search
            // typed against its rows, which would silently filter the next one's.
            (0, $817c29ac222633b5$export$4dbd032533367935)();
            store.update({
                searchOpen: false,
                pairingFromApp: false,
                knownTenants: knownTenants,
                tenantInput: tenant.url,
                todos: [],
                selectedId: null,
                todoView: "list",
                tab: "home",
                createOptions: null,
                allTodos: null,
                sessionExpired: false,
                onboarding: "url"
            });
            // ⚠ `finally`, and it must cover the LOAD as well as the switch: clearing it when
            // the switch resolved would drop the overlay onto an empty list for as long as the
            // TODOs take, which is the part that actually looks broken. loadApp() is written
            // not to throw, but an overlay that outlived a rejection would lock the app with
            // no way out but a reload.
            loadApp(result.profile).finally(()=>store.update({
                    busyLabel: null
                }));
            if (announce) toast((0, $498477530f916680$export$625550452a3fa3ec)("mobileShellSwitched", "Switched to {name}.", {
                name: result.profile.tenantName || tenant.host
            }));
        }).catch((error)=>{
            store.update({
                busyLabel: null
            });
            if (error instanceof (0, $373193550cbe2f46$export$f717aa8b913cec23) && error.statusCode === 404) {
                startPairingWith(tenant.url);
                return;
            }
            toast(errorText(error, (0, $498477530f916680$export$625550452a3fa3ec)("mobileShellSwitchFailed", "Could not switch tenant.")));
        });
    }
    /**
     * Into the app, after a pairing has just been approved.
     *
     * ⚠ THE OVERLAY IS FOR THE GAP AFTER THE TOKEN IS VALID. `loadApp()` sets `signedIn` before it
     * fetches anything, so the moment approval lands the render switches to the signed-in screens
     * and paints an EMPTY TODO list — which, at the end of a sign-in somebody has just walked
     * through a browser tab for, reads as "it worked and there is nothing here" rather than as
     * "still loading". Same reason the switch has one, arrived at from the other direction: there
     * the screen holds the previous installation's rows, here it holds none.
     *
     * Both completion paths go through this: the approval POLL and the manual re-check. They had
     * the same three lines twice already, and the overlay would have been the fourth thing to
     * remember to add in both.
     */ function enterAppAfterPairing(result) {
        client.setAccessToken(result.accessToken);
        store.update({
            tokenStep: 3,
            tokenFailedAt: null,
            busyLabel: (0, $498477530f916680$export$625550452a3fa3ec)("mobileShellOpening", "Opening {name}\u2026", {
                name: result.profile.tenantName || result.profile.tenantHost
            })
        });
        loadApp(result.profile)// `finally`, so a load that fails still hands the app back rather than leaving the
        // overlay up with no way out but a reload.
        .finally(()=>store.update({
                onboarding: "done",
                busyLabel: null
            }));
    }
    /** Leave the app and pair with an installation. An empty url means an empty address field. */ function startPairingWith(tenantUrl) {
        store.update({
            signedIn: false,
            sessionExpired: false,
            onboarding: "url",
            tenantInput: tenantUrl,
            // With no url this is the "add another" flow, which must show the field rather than
            // the picker the device would otherwise open on.
            addingTenant: tenantUrl === "",
            tenantError: "",
            onboardingError: "",
            onboardingErrorStatus: null
        });
        if (tenantUrl) onboardingActions.submitTenant(tenantUrl);
    }
    /** Record the installation that just paired, with whatever we learned about it. */ function rememberCurrentTenant(displayName) {
        const state = store.getState();
        const discovery = state.discovery;
        const host = discovery?.host ?? (0, $ea7cc6cc52c8cad1$export$da45a345dadb2e43)(state.tenantInput);
        const method = discovery?.methods.find((m)=>m.id === state.lastMethodId);
        const knownTenants = (0, $9a394e9e7ce3770e$export$144a76485d039242)(state.knownTenants, {
            url: state.tenantInput,
            host: host,
            displayName: displayName ?? discovery?.displayName,
            methodId: method?.id,
            methodTitle: method?.title,
            lastUsed: Date.now()
        });
        $9d251d88620741d4$var$writeStoredTenants(knownTenants);
        store.update({
            knownTenants: knownTenants,
            sessionExpired: false
        });
    }
    /**
     * Ask the portal whether the user has approved the token yet.
     *
     * A poll rather than a redirect back, because approval happens on the
     * TENANT's own page — it has no way to call this app back. Bounded by the
     * tenant's own `authorizationDeadline`, so it cannot run forever.
     */ /** Check immediately rather than after the interval — used on return and by "Check now". */ function pollNow() {
        pollForApproval(0);
    }
    function pollForApproval(delay = (0, $34dfe091e21ab98b$export$2cf9854ae498407c)) {
        stopPolling();
        const { enrollmentId: enrollmentId } = store.getState();
        if (!enrollmentId) return;
        if (Date.now() > pollDeadline) {
            store.update({
                onboarding: "failed",
                onboardingError: (0, $498477530f916680$export$625550452a3fa3ec)("mobileShellApprovalExpired", "The approval request expired before it was confirmed. Start again to get a new one."),
                onboardingErrorStatus: null
            });
            return;
        }
        pollTimer = window.setTimeout(()=>{
            api.completePairing(enrollmentId).then((result)=>{
                stopPolling();
                rememberCurrentTenant(result.profile.tenantName);
                store.update({
                    onboarding: "validating"
                });
                enterAppAfterPairing(result);
            }).catch((error)=>{
                // ⚠ 409 and ONLY 409 means "not approved yet". 404 used to count too, and
                // auth/complete returns 404 for "we do not know that address" as well — so an
                // unresolvable tenant polled silently to its deadline instead of saying so.
                // Any code that can mean two things cannot be a poll condition.
                if (error instanceof (0, $373193550cbe2f46$export$f717aa8b913cec23) && error.statusCode === 409) {
                    pollForApproval();
                    return;
                }
                stopPolling();
                store.update({
                    onboarding: "validating",
                    tokenFailedAt: 1,
                    onboardingError: errorText(error, (0, $498477530f916680$export$625550452a3fa3ec)("mobileShellTokenInvalid", "The token could not be validated against that tenant.")),
                    onboardingErrorStatus: errorStatus(error)
                });
            });
        }, delay);
    }
    // ── todo actions ─────────────────────────────────────────────────────────
    const todoActions = {
        openAccount () {
            store.update({
                sheet: "account"
            });
        },
        setAppearance (appearance) {
            (0, $237371521f1a88b0$export$48c323151b232e54)(appearance);
            (0, $237371521f1a88b0$export$26ebbdba2049c0c4)(appearance);
            store.update({
                appearance: appearance
            });
        },
        /**
         * The person's own language. The app's words follow at once (t() is read at paint time); the
         * TENANT's words — state names, transitions, the server's sentences — came in the old
         * language, so the TODOs are read again, quietly, in the new one.
         */ setLanguageChoice (choice) {
            const changed = (0, $d88fccb0df7b88b9$export$6401e5c1b69c061b)(choice, (0, $498477530f916680$export$324a75438e87a537)());
            store.update({
                languageChoice: choice
            });
            if (!changed || !store.getState().signedIn) return;
            // The tenant's names for the two roles come in a language too.
            (0, $0a4952cb2d3e704b$export$f9c9510d76d586c3)($9d251d88620741d4$var$readRelationNames((0, $ef9823cca3288d87$export$3c0dd642b9a3c1cb)(store.getState().profile), (0, $498477530f916680$export$324a75438e87a537)()));
            loadCreateOptions();
            if (store.getState().allTodos) loadAllTodos();
            api.todos().then((todos)=>store.update({
                    todos: todos
                })).catch(()=>{
            /* the list keeps the old words until the next read; nothing is wrong with it */ });
        },
        /**
         * Switch to an installation this device is already paired with.
         *
         * ⚠ NO RE-APPROVAL on the happy path — the pairing lives in the device cookie and the
         * server only re-orders it. A 404 means that pairing is gone (or was never made on THIS
         * device, since the remembered list is per browser and the credential is per device), and
         * the answer to that is to pair, not to show an error: the user asked to go there.
         */ switchTenant (tenant) {
            store.update({
                sheet: null
            });
            switchToTenant(tenant, true);
        },
        addInstallation () {
            store.update({
                sheet: null
            });
            startPairingWith("");
            // After startPairingWith(), which is shared with the roads in from outside the app.
            store.update({
                pairingFromApp: true
            });
        },
        /**
         * Sign out of the ACTIVE installation.
         *
         * ⚠ The other pairings survive — the server removes one entry from the device cookie
         * rather than clearing it — so `remaining` decides where the person lands: back in the app
         * on another installation, or on the signed-out picker.
         */ signOut () {
            store.update({
                sheet: null,
                busyLabel: (0, $498477530f916680$export$625550452a3fa3ec)("mobileShellSigningOut", "Deleting the access token\u2026")
            });
            const state = store.getState();
            const leaving = (0, $9a394e9e7ce3770e$export$3cd89ddcdf90b64f)(state.knownTenants);
            api.signOut().then((result)=>{
                client.setAccessToken(null);
                // Forget the installation that was just signed out of; the others stay in the
                // list, and the next one becomes active by `lastUsed`.
                const knownTenants = leaving ? state.knownTenants.filter((t)=>t.host !== leaving.host) : [];
                $9d251d88620741d4$var$writeStoredTenants(knownTenants);
                const next = (0, $9a394e9e7ce3770e$export$3cd89ddcdf90b64f)(knownTenants);
                (0, $817c29ac222633b5$export$4dbd032533367935)();
                store.update({
                    searchOpen: false,
                    pairingFromApp: false,
                    knownTenants: knownTenants,
                    signedIn: false,
                    profile: null,
                    todos: [],
                    selectedId: null,
                    todoView: "list",
                    tab: "home",
                    createOptions: null,
                    allTodos: null,
                    sessionExpired: false,
                    addingTenant: knownTenants.length === 0,
                    tenantInput: next?.url ?? "",
                    onboarding: "url"
                });
                // ⚠ `remaining` is the SERVER's count of live pairings, not the length of the
                // remembered list — the two can disagree (a device paired in another browser
                // profile, a list cleared by site-data cleanup), and only the server knows
                // whether there is a credential to come back on.
                // Cleared here rather than in a `finally`: when there is somewhere to land,
                // switchToTenant() puts its own label straight back up, and clearing after it
                // would flash the app between the two.
                store.update({
                    busyLabel: null
                });
                if (result.remaining > 0 && next) // ⚠ SWITCH, never useTenant(). The device is still paired there, and
                // useTenant() goes back through discovery and the tenant's approval page —
                // which would make signing out of one installation cost a re-approval on
                // another one the person never touched.
                switchToTenant(next, false);
            }).catch((error)=>{
                store.update({
                    busyLabel: null
                });
                toast(errorText(error, (0, $498477530f916680$export$625550452a3fa3ec)("mobileShellSignOutFailed", "The access token could not be deleted.")));
            });
        },
        openRelation (relation) {
            store.update({
                tab: "todos",
                todoView: "list",
                relation: relation
            });
        },
        openTodos () {
            store.update({
                tab: "todos",
                todoView: "list"
            });
        },
        openTodo (todo) {
            const state = store.getState();
            // Back must lead to exactly the screen the TODO was opened from — Home or the list, its
            // segment, and where it was scrolled. Remembered on the FIRST step into a detail; a TODO
            // opened from another one's search card goes back to where the first came from.
            if (!(state.tab === "todos" && state.todoView === "detail")) detailReturn = {
                tab: state.tab,
                relation: state.relation,
                scrollY: window.scrollY
            };
            store.update({
                searchOpen: false,
                tab: "todos",
                todoView: "detail",
                selectedId: todo.id
            });
            // ⚠ READ WHOLE ON EVERY OPEN that does not know yet what this person may do: the lists
            // do not carry `permissions` (one request per row), and the edit controls wait for them.
            // It also completes a row from "All TODOs", and replaces a list row minutes old.
            const opened = store.selected();
            if (opened && (!opened.permissions || opened.partial)) completeTodo(todo.id);
        },
        back () {
            const to = detailReturn;
            detailReturn = null;
            if (!to) {
                store.update({
                    todoView: "list"
                });
                return;
            }
            pendingScroll = to.scrollY;
            store.update({
                todoView: "list",
                tab: to.tab,
                relation: to.relation
            });
            pendingScroll = null;
        },
        openSearch () {
            store.update({
                searchOpen: true
            });
        },
        closeSearch () {
            store.update({
                searchOpen: false
            });
        },
        setRelation (relation) {
            store.update({
                relation: relation
            });
            if (relation === "all") loadAllTodos();
        },
        toggleGrouping () {
            store.update({
                grouping: (0, $0a55aae61f0d5dc0$export$e7b38ea9409824d2)(store.getState().grouping)
            });
        },
        toggleGroup (key) {
            const collapsedGroups = (0, $0a55aae61f0d5dc0$export$8f30fba2bc1b196d)(store.getState().collapsedGroups, key);
            store.update({
                collapsedGroups: collapsedGroups
            });
            $9d251d88620741d4$var$writeCollapsedGroups(collapsedGroups);
        },
        setGroupsCollapsed (keys, collapse) {
            const collapsedGroups = (0, $0a55aae61f0d5dc0$export$7b98c8e0261a518e)(store.getState().collapsedGroups, keys, collapse);
            store.update({
                collapsedGroups: collapsedGroups
            });
            $9d251d88620741d4$var$writeCollapsedGroups(collapsedGroups);
        },
        openNewSheet (projectId) {
            store.update({
                sheet: "new",
                newTodoProjectId: projectId ?? null
            });
        },
        openAssigneesSheet () {
            store.update({
                sheet: "assignees"
            });
        },
        openOwnerSheet () {
            store.update({
                sheet: "owner",
                ownerPick: null
            });
        },
        pickOwner (personId) {
            store.update({
                ownerPick: personId
            });
        },
        /**
         * Hand the TODO over to the picked person, who REPLACES the current owner(s) — `PUT
         * todos/{id}/links/owner` with one target. The answer is the TODO as it is now, relations and
         * permissions included, so the screen follows: one handed over from "I own" leaves it.
         */ handOver () {
            const todo = store.selected();
            const state = store.getState();
            const personId = state.ownerPick;
            if (!todo || !personId || state.assigneeBusy) return;
            const name = state.createOptions?.ownerPeople?.find((p)=>p.id === personId)?.name ?? "";
            store.update({
                assigneeBusy: personId
            });
            api.setLinks(todo.id, "owner", [
                {
                    id: personId
                }
            ]).then((updated)=>{
                store.replaceTodo(updated);
                store.update({
                    sheet: null,
                    ownerPick: null
                });
                toast(name ? (0, $498477530f916680$export$625550452a3fa3ec)("mobileShellHandedOverTo", "Handed over to {name}", {
                    name: name
                }) : (0, $498477530f916680$export$625550452a3fa3ec)("mobileShellHandedOver", "Handed over"));
            }).catch((error)=>{
                toast(errorText(error, (0, $498477530f916680$export$625550452a3fa3ec)("mobileShellHandOverFailed", "The TODO was not handed over.")));
                // What was written so far stays written (the new owner is added before the old
                // one is removed), so the screen re-reads rather than trusting what it showed.
                completeTodo(todo.id);
            }).finally(()=>store.update({
                    assigneeBusy: null
                }));
        },
        openDetailsSheet () {
            store.update({
                sheet: "details"
            });
        },
        openDeleteSheet () {
            store.update({
                sheet: "delete"
            });
        },
        deleteTodo () {
            const todo = store.selected();
            if (!todo) return;
            store.update({
                sheet: null,
                busyLabel: (0, $498477530f916680$export$625550452a3fa3ec)("mobileShellDeleting", "Deleting\u2026")
            });
            api.deleteTodo(todo.id).then(()=>{
                // Back first — to where the TODO was opened — then out of the lists.
                todoActions.back();
                store.removeTodo(todo.id);
                toast((0, $498477530f916680$export$625550452a3fa3ec)("mobileShellDeleted", "TODO deleted"));
            }).catch((error)=>toast(errorText(error, (0, $498477530f916680$export$625550452a3fa3ec)("mobileShellDeleteFailed", "The TODO was not deleted.")))).finally(()=>store.update({
                    busyLabel: null
                }));
        },
        /**
         * Rename, and rewrite the description when the sheet was allowed to edit it.
         *
         * ⚠ `undefined` is not `""`. An omitted description leaves the stored one alone; an empty
         * string clears it. The sheet sends the first for a formatted description it showed
         * read-only, and the route treats the key's absence the same way.
         */ saveDetails (title, description) {
            const todo = store.selected();
            if (!todo) return;
            api.setDetails(todo.id, title, description).then((updated)=>{
                store.replaceTodo(updated);
                store.update({
                    sheet: null
                });
                toast((0, $498477530f916680$export$625550452a3fa3ec)("mobileShellTodoSaved", "Changes saved."));
            })// ⚠ The sheet STAYS OPEN on a failure, with what was typed still in its fields —
            // closing it would throw away the only copy of the text. The toast repaints the
            // sheet, which re-enables its Save button and restores the draft (detailsSheet()).
            .catch((error)=>toast(errorText(error, (0, $498477530f916680$export$625550452a3fa3ec)("mobileShellTodoSaveFailed", "The TODO was not changed."))));
        },
        closeSheet () {
            store.update({
                sheet: null,
                transitionDraft: null
            });
        },
        /** Open the status sheet — nothing picked: the sheet is where the choice is made. */ openStatusSheet () {
            store.update({
                sheet: "transition",
                transitionDraft: {
                    transition: null,
                    label: "",
                    note: "",
                    busy: false,
                    error: ""
                }
            });
        },
        /** Open the priority sheet. Owner only; the server refuses anyone else. */ openPrioritySheet () {
            store.update({
                sheet: "priority",
                priorityPick: null
            });
        },
        /** Pick a priority in the sheet. Sends nothing — Confirm does. */ pickPriority (priority) {
            store.update({
                priorityPick: priority
            });
        },
        /**
         * ⚠ Confirmed against the server BEFORE the screen changes.
         *
         * A lifecycle move is not an optimistic update: it may be refused by the
         * tenant's own rules, and the transitions available afterwards are only
         * knowable server-side. Showing it as done and rolling back would mean
         * telling someone their item moved when it did not.
         */ confirmTransition (transition, label, note) {
            const state = store.getState();
            const draft = state.transitionDraft;
            const todo = store.selected();
            if (!draft || !todo || draft.busy) return;
            store.update({
                transitionDraft: {
                    transition: transition,
                    label: label,
                    note: note,
                    busy: true,
                    error: ""
                }
            });
            api.fireTransition(todo.id, transition.id, note).then((updated)=>{
                store.update({
                    sheet: null,
                    transitionDraft: null
                });
                store.replaceTodo(updated);
                toast((0, $498477530f916680$export$625550452a3fa3ec)("mobileShellMoved", "Moved to {state}", {
                    state: updated.status
                }));
            }).catch((error)=>{
                const current = store.getState().transitionDraft;
                if (!current) return;
                store.update({
                    transitionDraft: {
                        ...current,
                        busy: false,
                        error: errorText(error, (0, $498477530f916680$export$625550452a3fa3ec)("mobileShellMoveRefused", "That move was refused. Reload and try again."))
                    }
                });
            });
        },
        sendComment (text) {
            const todo = store.selected();
            if (!todo) return;
            store.update({
                commentBusy: true
            });
            api.comment(todo.id, text).then((updated)=>{
                store.update({
                    commentBusy: false
                });
                store.replaceTodo(updated);
                // Every other write on this screen confirms itself. A comment did not, so the
                // only feedback was the entry appearing in the log — which is exactly what was
                // NOT happening while the re-read dropped the history.
                toast((0, $498477530f916680$export$625550452a3fa3ec)("mobileShellCommentAdded", "Comment added."));
            }).catch((error)=>{
                store.update({
                    commentBusy: false
                });
                toast(errorText(error, (0, $498477530f916680$export$625550452a3fa3ec)("mobileShellCommentFailed", "Your comment was not saved.")));
            });
        },
        setDueDate (iso) {
            const todo = store.selected();
            if (!todo) return;
            api.setDue(todo.id, iso).then((updated)=>{
                store.replaceTodo(updated);
                // The LABEL, not the raw date: it is the word the screen now shows, and
                // "Due date set to 2026-09-14" would be the only ISO string in the whole app.
                toast(iso ? (0, $498477530f916680$export$625550452a3fa3ec)("mobileShellDueSet", "Due date set to {date}", {
                    date: (0, $439e2ca410399204$export$566d600030c4efae)(updated.dueISO, Date.now()).label
                }) : (0, $498477530f916680$export$625550452a3fa3ec)("mobileShellDueCleared", "Due date cleared"));
            }).catch((error)=>toast(errorText(error, (0, $498477530f916680$export$625550452a3fa3ec)("mobileShellDueFailed", "The due date was not changed."))));
        },
        setPriority (priority) {
            const todo = store.selected();
            if (!todo || store.getState().priorityBusy) return;
            store.update({
                priorityBusy: true
            });
            api.setPriority(todo.id, priority).then((updated)=>{
                // The sheet closes on SUCCESS only: a refused change keeps it open, pick intact.
                store.update({
                    priorityBusy: false,
                    sheet: store.getState().sheet === "priority" ? null : store.getState().sheet
                });
                store.replaceTodo(updated);
                // Named from the ANSWER, not the request — the server re-reads the TODO and
                // refuses a change that did not take, so this is what the tenant now holds.
                toast(updated.priority ? (0, $498477530f916680$export$625550452a3fa3ec)("mobileShellPrioritySet", "Priority set to {priority}", {
                    priority: (0, $2336e268632bd98b$export$bf7f685c4fbf1078)(updated.priority)
                }) : (0, $498477530f916680$export$625550452a3fa3ec)("mobileShellPriorityCleared", "Priority cleared"));
            }).catch((error)=>{
                store.update({
                    priorityBusy: false
                });
                toast(errorText(error, (0, $498477530f916680$export$625550452a3fa3ec)("mobileShellPriorityFailed", "The priority was not changed.")));
            });
        },
        nudge () {
            const todo = store.selected();
            if (!todo) return;
            api.nudge(todo.id)// Named from the server's answer — who was emailed, and who was not and why.
            .then((result)=>toast((0, $0a55aae61f0d5dc0$export$31255910f3c96146)(todo, result))).catch((error)=>toast(errorText(error, (0, $498477530f916680$export$625550452a3fa3ec)("mobileShellNudgeFailed", "The reminder was not sent."))));
        },
        assign (personId) {
            const todo = store.selected();
            if (!todo || store.getState().assigneeBusy) return;
            const name = store.getState().createOptions?.people.find((p)=>p.id === personId)?.name ?? "";
            store.update({
                assigneeBusy: personId
            });
            api.assign(todo.id, personId).then((updated)=>{
                store.replaceTodo(updated);
                toast(name ? (0, $498477530f916680$export$625550452a3fa3ec)("mobileShellPersonAdded", "{name} added", {
                    name: name
                }) : (0, $498477530f916680$export$625550452a3fa3ec)("mobileShellAssigneeAdded", "Assignee added"));
            }).catch((error)=>toast(errorText(error, (0, $498477530f916680$export$625550452a3fa3ec)("mobileShellAssignFailed", "The assignee was not added.")))).finally(()=>store.update({
                    assigneeBusy: null
                }));
        },
        unassign (personId) {
            const todo = store.selected();
            if (!todo || store.getState().assigneeBusy) return;
            const name = todo.assignees.find((a)=>a.id === personId)?.name ?? "";
            store.update({
                assigneeBusy: personId
            });
            api.unassign(todo.id, personId).then((updated)=>{
                store.replaceTodo(updated);
                toast(name ? (0, $498477530f916680$export$625550452a3fa3ec)("mobileShellPersonRemoved", "{name} removed", {
                    name: name
                }) : (0, $498477530f916680$export$625550452a3fa3ec)("mobileShellAssigneeRemoved", "Assignee removed"));
            }).catch((error)=>toast(errorText(error, (0, $498477530f916680$export$625550452a3fa3ec)("mobileShellUnassignFailed", "The assignee was not removed.")))).finally(()=>store.update({
                    assigneeBusy: null
                }));
        },
        createTodo (draft) {
            if (!draft.title) {
                toast((0, $498477530f916680$export$625550452a3fa3ec)("mobileShellTitleMissing", "Give the TODO a title first."));
                return;
            }
            // The busy chip from the tap until the new TODO's screen is ready: "Creating…", then
            // "Loading…" while it is read whole, if it has to be.
            store.update({
                sheet: null,
                busyLabel: (0, $498477530f916680$export$625550452a3fa3ec)("mobileShellCreating", "Creating\u2026")
            });
            api.create(draft).then((created)=>{
                const state = store.getState();
                store.update({
                    todos: [
                        ...state.todos,
                        created
                    ],
                    ...state.allTodos ? {
                        allTodos: [
                            ...state.allTodos,
                            created
                        ]
                    } : {}
                });
                // Straight into the new TODO; back leads to where the composer was opened.
                todoActions.openTodo(created);
                const names = (0, $0a55aae61f0d5dc0$export$5b9e1d8c1d3d59ac)(created);
                toast(names ? (0, $498477530f916680$export$625550452a3fa3ec)("mobileShellCreatedFor", "TODO created for {names}", {
                    names: names
                }) : (0, $498477530f916680$export$625550452a3fa3ec)("mobileShellCreated", "TODO created"));
            }).catch((error)=>toast(errorText(error, (0, $498477530f916680$export$625550452a3fa3ec)("mobileShellCreateFailed", "The TODO was not created.")))).finally(()=>store.update({
                    busyLabel: null
                }));
        }
    };
    // ── rendering ────────────────────────────────────────────────────────────
    const tabButton = (label, iconName, active, onClick)=>{
        const button = (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("button", {
            class: "pqm-tab",
            type: "button",
            role: "tab",
            "aria-selected": active ? "true" : "false"
        }, [
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
                class: "pqm-tab-icon"
            }, [
                (0, $62a79e86ee9eca2d$export$1ca1ec8b29a4ce27)(iconName, 20)
            ]),
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("span", {
                class: "pqm-tab-label",
                text: label
            })
        ]);
        (0, $afadd878e68b56c8$export$2385a24977818dd0)(button, "click", onClick);
        return button;
    };
    /** The overlays on screen after the last paint, by `data-overlay` key. See steadyOverlays(). */ let shownOverlays = new Set();
    /** Which screen the last paint drew — see screenKeyOf() and keepScreenScroll(). */ let shownScreen = null;
    /** Whether the last paint had a sheet or the busy veil up — a new one brings the tab bar back. */ let shownOverlay = false;
    const scrollChrome = (0, $5c4add5e763a6b0c$export$d5681e343986e7ff)(root);
    (0, $8ec6ba565d4684f6$export$dd52d3a8aed5a5e8)(root);
    const render = (state)=>{
        if (!state.ready) {
            // The splash while the app finds out who is signed in: the logo, so a slow start reads as
            // the app starting rather than an empty page with a word on it.
            (0, $afadd878e68b56c8$export$9563e054e6f787fb)(root, [
                (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                    class: "pqm-splash",
                    role: "status"
                }, [
                    (0, $62a79e86ee9eca2d$export$db3c28d99448dcff)(40),
                    (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                        class: "pqm-splash-text",
                        text: (0, $498477530f916680$export$625550452a3fa3ec)("mobileShellLoading", "Mobile client loading\u2026")
                    })
                ])
            ]);
            return;
        }
        if (!state.signedIn) {
            (0, $2d8359f39da97b6c$export$3e6cf6adc32ea205)(root, state, onboardingActions);
            return;
        }
        let screen;
        if (state.tab === "home") screen = (0, $817c29ac222633b5$export$5e5c8210e5686582)(state, todoActions);
        else if (state.todoView === "detail") {
            const todo = store.selected();
            // Not in the lists yet: still loading them (a reload into a detail) — or it is gone.
            screen = todo ? (0, $817c29ac222633b5$export$83faea0403bc9d12)(todo, state, todoActions) : state.loading || state.relation === "all" && state.allLoading ? (0, $817c29ac222633b5$export$465592710b708c6f)(state, todoActions) : (0, $817c29ac222633b5$export$4bf387f59bfc6d67)(state, todoActions);
        } else screen = (0, $817c29ac222633b5$export$4bf387f59bfc6d67)(state, todoActions);
        const children = [
            ...screen
        ];
        children.push((0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-tabbar",
            role: "tablist"
        }, [
            tabButton((0, $498477530f916680$export$625550452a3fa3ec)("mobileShellTabHome", "Home"), "house", state.tab === "home", ()=>store.update({
                    tab: "home"
                })),
            tabButton((0, $498477530f916680$export$625550452a3fa3ec)("mobileShellTabTodos", "TODOs"), "checkSquare", state.tab === "todos", todoActions.openTodos)
        ]));
        if (state.searchOpen) children.push((0, $817c29ac222633b5$export$6893370b63db6be)(state, todoActions));
        const sheet = (0, $4293432b28b7e6e8$export$85dfa746d856176d)(state, todoActions);
        if (sheet) children.push(sheet);
        // ⚠ ABOVE THE SHEETS, and it takes the pointer: the screen underneath belongs to the
        // installation being left, and a tap on one of its rows during the switch would act on a
        // system the person has already moved away from. `aria-busy` + `role=status`, so a screen
        // reader is told what is happening rather than just losing its place.
        // The busy chip — for a write like deleting, and while the open TODO is being read whole:
        // the same chip, so loading a TODO looks like what it is, the app waiting on the server.
        const busyText = state.busyLabel ?? (state.tab === "todos" && state.todoView === "detail" && (state.todoLoading !== null || !store.selected() && (state.loading || state.allLoading)) ? (0, $498477530f916680$export$625550452a3fa3ec)("mobileViewLoading", "Loading\u2026") : null);
        if (busyText) children.push((0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-busy",
            "data-overlay": "busy",
            role: "status",
            "aria-live": "polite",
            "aria-busy": "true"
        }, [
            (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                class: "pqm-busy-card"
            }, [
                (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                    class: "pqm-pulse"
                }),
                (0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
                    class: "pqm-busy-text",
                    text: busyText
                })
            ])
        ]));
        if (state.toast) children.push((0, $afadd878e68b56c8$export$c3da0dad1b44eea9)("div", {
            class: "pqm-toast",
            role: "status",
            "aria-live": "polite",
            "data-overlay": `toast:${state.toast}`,
            text: state.toast
        }));
        const scroll = $9d251d88620741d4$var$sheetScrollOf(root);
        const screenTop = window.scrollY;
        (0, $afadd878e68b56c8$export$9563e054e6f787fb)(root, children);
        shownOverlays = $9d251d88620741d4$export$431b268fff62c239(children, shownOverlays, scroll);
        const screenKey = $9d251d88620741d4$export$bf4611f3c1b221c1(state, store.selected()?.id ?? null);
        $9d251d88620741d4$export$2c25052efdc41639(shownScreen, screenKey, screenTop);
        // Back from a TODO: the screen it was opened from, scrolled where it was.
        if (pendingScroll !== null && screenKey !== shownScreen && window.scrollY !== pendingScroll) window.scrollTo(0, pendingScroll);
        // A new screen, or a sheet arriving, always shows the module menu — and the scroll the
        // restore above just made is the baseline, not a scroll the person made.
        const overlay = Boolean(sheet || state.searchOpen || busyText);
        if (screenKey !== shownScreen || overlay && !shownOverlay) scrollChrome.reset();
        shownScreen = screenKey;
        shownOverlay = overlay;
        // A repaint can change how long the page is; the indicator follows.
        scrollChrome.refresh();
    };
    store.subscribe(render);
    $9d251d88620741d4$var$initViewMemory(store);
    (0, $e8b26be480e701db$export$9c0b714c3560f3aa)(store, {
        closeSheet: todoActions.closeSheet,
        closeSearch: todoActions.closeSearch,
        back: todoActions.back
    });
    render(store.getState());
    bootstrap();
}
function $9d251d88620741d4$export$431b268fff62c239(children, previous, sheetScroll) {
    const shown = new Set();
    for (const child of children){
        const key = child.dataset.overlay;
        if (!key) continue;
        shown.add(key);
        if (previous.has(key)) {
            child.classList.add("pqm-steady");
            if (sheetScroll && sheetScroll.key === key) {
                const panel = child.querySelector(".pqm-sheet");
                if (panel) panel.scrollTop = sheetScroll.top;
            }
        } else if (key.startsWith("sheet:")) // Opening: focusable so Escape reaches it before anything inside has focus.
        window.setTimeout(()=>child.focus(), 0);
    }
    return shown;
}
function $9d251d88620741d4$export$bf4611f3c1b221c1(state, selectedId) {
    if (!state.ready || !state.signedIn) return null;
    if (state.tab === "home") return "home";
    if (state.todoView === "detail" && selectedId) return `detail:${selectedId}`;
    return `list:${state.relation}:${state.grouping}`;
}
function $9d251d88620741d4$export$2c25052efdc41639(previousKey, nextKey, top) {
    if (nextKey === null) return;
    if (previousKey === nextKey) {
        if (window.scrollY !== top) window.scrollTo(0, top);
    } else if (window.scrollY !== 0) window.scrollTo(0, 0);
}
/** The open sheet's key and scroll offset, read before a repaint throws the panel away. */ function $9d251d88620741d4$var$sheetScrollOf(root) {
    const scrim = root.querySelector('[data-overlay^="sheet:"]');
    const panel = scrim?.querySelector(".pqm-sheet");
    return scrim && panel ? {
        key: scrim.dataset.overlay,
        top: panel.scrollTop
    } : null;
}



// ⚠ FIRST, before waiting for the DOM: <html> already exists, and every frame drawn before this
// runs is drawn light. See the header of ui_mobile_appearance.ts.
(0, $237371521f1a88b0$export$8ebb91242307036f)();
function $26e65997225f48fa$var$start() {
    const root = document.getElementById("pqm-root");
    if (!root) return;
    (0, $9d251d88620741d4$export$e97df488f1a5cdc)(root);
}
if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", $26e65997225f48fa$var$start, {
    once: true
});
else $26e65997225f48fa$var$start();
// The service worker is registered from the app, not the shell, so a page that
// failed to boot does not install one. Registered from the ROOT scope — see
// `pqforce-mobile-sw.php` for why it cannot live under site/assets/.
if ("serviceWorker" in navigator) window.addEventListener("load", ()=>{
    const root = document.getElementById("pqm-root");
    const url = root?.dataset.swUrl;
    if (!url) return;
    // `scope: "/"` pairs with the `Service-Worker-Allowed: /` header the
    // worker is served with — see `pqforce-mobile-sw.php`. Without both, the
    // worker's scope is its own path and it controls nothing.
    navigator.serviceWorker.register(url, {
        scope: "/"
    }).catch(()=>{
    // An offline shell is a bonus, not the product. A failure here must
    // never stop the app.
    });
});

})();
//# sourceMappingURL=MobilePage.js.map
